import type { AuthError, Session } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { Report, Contact, InspectionItem, ServiceItem, Note, ChecklistEntry, ProjectPlan, AppendixImage, RabEntry, Authority, EmailLog, BugReport, ProjectRepresentative, ReportMetadata, ServiceTransaction, ServiceTransferOrigin } from '../types';
import { applyRepresentatives, getRepresentatives, promoteRepresentative } from './projectRepresentatives';
import { buildActorName, realActorName } from './actorName';
import { einstellungsAenderung, istFehlendeEinstellungsFunktion, mergeProfileSettings, type ProfileSettings } from './profileSettings';
import { bestandteilFelderAusZeile, definitionsJson, planFlatScopeFreeze,planLinkReturnLog, planServiceLogTransfer, stundenKontingentZeile, type LinkReturnPlan, type LinkTransferPlan } from './projectLv';
// Paketposition (Nr. 165/166): Definition nur mitsenden, wenn gelesen.
import { hatBestandteile } from './bestandteile/bestandteilLogic';
import {
    ProjectSaveConflictError,
    rememberProjectVersion,
    resolveSaveAnchor,
    type SaveAnchorOptions,
} from './projectVersionAnchor';
import { salvageForeignLogEntries } from './reportMerge';
import {
  istBuildVeraltet,
  markiereBuildVeraltet,
  isBuildGuardWriteError,
  buildVeraltetFehler,
} from './versionGate';
import { spiegleProjektwechsel } from './linkTransferJournal';
// Die Spaltenlisten der beiden Projekt-Abfragen stehen seit dem Ausfall um
// `transferred_from` (Nr. 68) nur noch an EINER Stelle — und sind dort zugleich
// die Quelle der Wache, die den Migrationsstand gegen sie hält.
import { projektListeSelect, projektUebersichtSelect } from './queryColumns';
// Vorfall 21.10.2026: ein Listeneintrag ist kein Projekt — kennzeichnen, nie speichern.
import { markiereListenAuszug, ohneListenAuszugKennung, verweigereListenAuszug } from './listenAuszug';
import type { ListProject } from './listProject';
import { applyLinkColumns, setLinkedParentName } from './projectLink';
import { fetchLinkedChildren, fetchParentNames } from './projectLinkService';
// Die dritte Schicht gegen den Ausfall vom 02.09.2026: fehlt eine additiv
// nachgeruestete Spalte, wird sie EINMAL aus der Liste genommen statt die
// Anwendung zu toeten. Der Riegel `ohneFehlendeSpalten` sorgt dafuer, dass das
// rein LESEND bleibt — die Begruendung Spalte fuer Spalte steht dort.
import {
    alsAbfrageFehler,
    fehlendeSpalten,
    istUnbekannteSpalte,
    mitSpaltenRueckfall,
    ohneFehlendeSpalten,
} from './spaltenRueckfall';
// Maengelkatalog (Nr. 131): nur definierte Felder schreiben, Laengen wie der CHECK.
import {
    KATALOG_SPALTEN,
    hatKatalogSpalten,
    istUnbekannteSpalteFehler,
    katalogFelderLesen,
    katalogSpaltenSchreiben,
    ohneKatalogSpalten,
} from './mangelKatalog/spalten';
// Planverortung (Nr. 130): Nadeln IMMER mitschreiben (null = keine Nadel), beim Laden lesen.
import { hatPinSpalten, ohnePinSpalten, pinFelderLesen, pinSpaltenSchreiben } from './verortung/pinSpalten';
// Ohne Netz liefert getUser() keinen Nutzer — das ist keine Abmeldung.
import { fehlerOhneNutzer, geraetOnline, istEigenerNetzFehler } from './netzFehler';
import { ladeAlleSeiten } from './seitenweise';
// Sparsames Speichern: unveraenderte Kindtabellen weglassen, Antwort ohne Blob.
import {
    darfKindTabellenWeglassen,
    naechsterKindStand,
    neuerKindLauf,
    oeffneKindLauf,
    schliesseKindLauf,
    waehleKindTabellen,
    type KindLauf,
} from './childSaveDelta';
import {
    schmaleAntwortMoeglich,
    schmaleProjektAuswahl,
    sperreSchmaleAntwort,
    zeileAusSchmalerAntwort,
} from './schmaleSpeicherAntwort';

const BUCKET = 'project-assets';

// --- EMAIL LOGGING ---
export const logEmailAttempt = async (log: Omit<EmailLog, 'id' | 'sent_at' | 'sender_id'>) => {
    const { data: { user }, error: anmeldeFehler } = await supabase.auth.getUser();
    if (!user) {
        // Kein Netz (oder Auth-Server gestört) ist keine Abmeldung: werfen, damit
        // der Pending-Send-Marker den Sendebericht behält und später nachholt.
        // Ein stilles `return` räumte ihn — der Bericht ginge lautlos verloren.
        const fehler = fehlerOhneNutzer(anmeldeFehler, geraetOnline());
        if (istEigenerNetzFehler(fehler)) throw fehler;
        return;
    }

    // Die Unterscheidung "Idempotenz-Treffer oder echter Konflikt" trifft die
    // DATENBANK, nicht dieser Code: `ignoreDuplicates` erzeugt ein
    // `ON CONFLICT (send_key) DO NOTHING`. Trifft der Schreibversuch auf eine
    // Zeile mit demselben send_key — zweiter Tab, Retry, Marker-Replay —, endet
    // er ohne neue Zeile UND ohne Fehler; genau der gewünschte Endzustand.
    // Jeder ANDERE Konflikt (ein künftiger zweiter Unique-Index, ein Constraint,
    // den es heute noch nicht gibt) bleibt dagegen ein Fehler und fliegt unten
    // heraus. Die frühere Prüfung auf den Fehlercode 23505 konnte das nicht
    // leisten: sie hätte jede beliebige Unique-Verletzung als Erfolg verbucht,
    // der Pending-Marker wäre geräumt worden und der Sendebericht wäre lautlos
    // verloren gegangen.
    const { error } = await supabase.from('email_logs').upsert([{
        project_id: log.project_id,
        project_name: log.project_name,
        recipients: log.recipients,
        subject: log.subject,
        status: log.status,
        error_message: log.error_message,
        body_html: log.body_html,
        pdf_url: log.pdf_url,
        // Der Schlüssel kommt IMMER von außen (newSendKey beim Auslösen des
        // Versands). Hier einen zu erzeugen wäre wirkungslos: er wäre bei jedem
        // Aufruf ein anderer, und der Unique-Index hätte nichts zu vergleichen.
        // Ohne Schlüssel (Alt-Aufrufer) bleibt die Spalte null — der Index ist
        // nicht partiell, lässt aber (NULLS DISTINCT) beliebig viele NULL-Zeilen
        // zu, sodass solche Aufrufe wie bisher einfach durchlaufen.
        send_key: log.send_key ?? null,
        sender_id: user.id
    }], { onConflict: 'send_key', ignoreDuplicates: true });
    // Jeder Fehler wirft: der Pending-Send-Marker wertet "kein Fehler" als
    // "Sendebericht ist gespeichert" und legt sonst nie einen zweiten Versuch
    // nach. Solange geworfen wird, bleibt der Marker stehen und der Bericht wird
    // nachgeholt.
    if (error) throw error;
};

// Listenspalten ohne body_html (kompletter Mail-HTML-Inhalt, potenziell MB
// pro Zeile) — wortgleich mit fetchMyEmailLogs.
const EMAIL_LOG_LISTE = 'id, project_id, project_name, recipients, subject, status, error_message, sent_at, sender_id, pdf_url';

/** Zeitraum ueber `sent_at`: `ab` einschliesslich, `bis` ausschliesslich (ISO). */
export interface EmailLogZeitraum {
    ab?: string;
    bis?: string;
}

// Seitenweise (seitenweise.ts): bis Oktober 2026 holte die Projektansicht die
// 1000 neuesten Sendeberichte ALLER Projekte und filterte erst im Browser —
// 6254 behielt 2 von 24 Eintraegen. Werfen statt leerer Liste: ein leerer
// Sendeverlauf behauptete sonst, es sei nie etwas versendet worden.
const fetchEmailLogsSeitenweise = (spalten: string, filter: EmailLogZeitraum & { projectId?: string }): Promise<EmailLog[]> =>
    ladeAlleSeiten<EmailLog>((von, bis) => {
        let query = supabase.from('email_logs').select(spalten);
        if (filter.projectId) query = query.eq('project_id', filter.projectId);
        if (filter.ab) query = query.gte('sent_at', filter.ab);
        if (filter.bis) query = query.lt('sent_at', filter.bis);
        return query
            .order('sent_at', { ascending: false })
            .order('id', { ascending: false })
            .range(von, bis);
    });

/**
 * Admin-Sendeverlauf: alle Sendeberichte im Zeitraum (ohne Zeitraum: alle),
 * ohne body_html — die Ansicht zeigt ihn nicht.
 */
export const fetchEmailLogs = (zeitraum: EmailLogZeitraum = {}): Promise<EmailLog[]> =>
    fetchEmailLogsSeitenweise(EMAIL_LOG_LISTE, zeitraum);

/**
 * Sendeverlauf EINES Projekts, serverseitig gefiltert. Mit body_html: die
 * Projektansicht zeigt den Inhalt und versendet ihn 1:1 erneut.
 */
export const fetchProjectEmailLogs = (projectId: string): Promise<EmailLog[]> =>
    fetchEmailLogsSeitenweise('*', { projectId });

export const fetchMyEmailLogs = async (userId: string): Promise<EmailLog[]> => {
    // Liste ohne body_html: der komplette Mail-HTML-Inhalt (potenziell MB pro
    // Zeile) wird erst beim Öffnen eines Eintrags über fetchEmailLogBody geholt.
    const { data } = await supabase
        .from('email_logs')
        .select(EMAIL_LOG_LISTE)
        .eq('sender_id', userId)
        .order('sent_at', { ascending: false })
        .limit(50);
    return data || [];
};

export const fetchEmailLogBody = async (logId: string): Promise<string | null> => {
    const { data } = await supabase
        .from('email_logs')
        .select('body_html')
        .eq('id', logId)
        .maybeSingle();
    return data?.body_html ?? null;
};

// --- HILFSFUNKTIONEN ---

const base64ToBlob = async (base64: string): Promise<Blob> => {
  const res = await fetch(base64);
  return res.blob();
};

const generateHash = async (base64: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(base64);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
};

// Nur rasterbasierte Bildtypen: SVG & Co. können Script enthalten und wären im
// public Bucket Stored XSS auf der Storage-Domain. Nicht Erlaubtes bleibt inline.
const ALLOWED_IMAGE_EXTS = new Set(['png', 'jpeg', 'jpg', 'webp', 'gif', 'heic', 'heif']);

export const uploadAsset = async (base64Data: string, folder: string, customFilename?: string): Promise<string> => {
  if (!base64Data || !base64Data.startsWith('data:image')) return base64Data;
  const declaredExt = base64Data.substring(base64Data.indexOf('/') + 1, base64Data.indexOf(';')).toLowerCase();
  if (!ALLOWED_IMAGE_EXTS.has(declaredExt)) return base64Data;

  const performUpload = async () => {
      const blob = await base64ToBlob(base64Data);
      const ext = declaredExt;
      const hash = await generateHash(base64Data);
      const filename = customFilename 
        ? `${folder}/${customFilename}.${ext}`
        : `${folder}/${hash}.${ext}`;
      
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(filename, blob, { 
          contentType: `image/${ext}`, 
          upsert: true 
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(filename);

      return `${publicUrlData.publicUrl}`;
  };

  // Retry with exponential backoff so transient network/storage errors recover
  // instead of immediately falling back to inlining the (large) base64 string.
  const MAX_ATTEMPTS = 3;
  let lastErr: any = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
        return await performUpload();
    } catch (err: any) {
        lastErr = err;
        if (attempt < MAX_ATTEMPTS) {
            const delay = 400 * Math.pow(2, attempt - 1); // 400ms, 800ms
            console.warn(`Upload attempt ${attempt}/${MAX_ATTEMPTS} failed, retrying in ${delay}ms...`, err);
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
  }

  // Last resort: keep the image inline (base64) so the user does not lose data.
  // This bloats the row; it is a degraded fallback, hence the explicit error log.
  console.error("Upload failed after all retries — storing image inline as fallback (DB bloat).", lastErr);
  return base64Data;
};

async function processInChunks<T, R>(items: T[], chunk_size: number, fn: (item: T) => Promise<R>): Promise<R[]> {
    const results: R[] = [];
    for (let i = 0; i < items.length; i += chunk_size) {
        const chunk = items.slice(i, i + chunk_size);
        const chunkResults = await Promise.all(chunk.map(fn));
        results.push(...chunkResults);
    }
    return results;
}

const processReportAssets = async (obj: any, projectId: string, skipRelational: boolean = true): Promise<any> => {
    if (Array.isArray(obj)) {
        return Promise.all(obj.map(item => processReportAssets(item, projectId, skipRelational)));
    } else if (typeof obj === 'object' && obj !== null) {
        const newObj: any = {};
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (skipRelational && ['items', 'checklists', 'notes', 'plans', 'services'].includes(key)) {
                    newObj[key] = obj[key]; 
                    continue; 
                }
                
                if (key === 'unterlage') {
                    newObj[key] = { ...obj[key] };
                    if (newObj[key].projectData) {
                         const { appendixImages, ...restProjectData } = newObj[key].projectData;
                         newObj[key].projectData = await processReportAssets(restProjectData, projectId, skipRelational);
                         newObj[key].projectData.appendixImages = appendixImages; 
                    }
                    continue;
                }

                const value = obj[key];
                newObj[key] = await processReportAssets(value, projectId, skipRelational);
            }
        }
        return newObj;
    }
    return obj;
};

/**
 * Ein Datumsfeld der Kindzeilen (deadline, date_found, closed_at,
 * last_checked): leer ist NULL, nie ''. Die Spalten sind `date` bzw.
 * `timestamptz`, und ein leerer String scheitert dort mit 22007 „invalid
 * input syntax for type date" — am 07.09.2026 von Markus Meyers Android-
 * Handy gemeldet (Maengel ohne Frist). Der Fehler traf save_project_
 * children_tx NACH dem Schreiben von projects.data: Buchungszeile drin,
 * Zaehler nicht. Siehe den Vorfall an verarbeiteMaengel.
 */
const dateOrNull = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value : null;

const ensureIsoDate = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d.toISOString().split('T')[0];
  } catch {
    return null;
  }
};

/**
 * Minimum id length a row must have to survive an orphan prune.
 *
 * Rows still carrying a short client-side temp id count as "not yet persisted"
 * and are excluded from the keep-list. The contacts table has always used a
 * stricter threshold than the rest; the two values stay separate on purpose,
 * because aligning them silently deletes live rows.
 */
const PRUNE_MIN_ID_LENGTH = 5;
const PRUNE_MIN_ID_LENGTH_CONTACTS = 10;

/**
 * Deletes every row of `table` belonging to `projectId` that is not in `ids` —
 * the prune half of the upsert/prune pair each child table performs on save.
 *
 * With no qualifying id NOTHING is deleted. An empty keep list also arises when
 * a child query silently failed on load (mobile, brief network loss) or when a
 * blob-shaped snapshot is saved (services/notes/plans are always empty there);
 * wholesale deletion has wiped the services and email distribution list of
 * whole projects. Trade-off: removing the LAST entry of a list is not
 * persisted — the row reappears on reload instead of data being lost.
 */
const pruneOrphans = async (
  table: string,
  projectId: string,
  ids: (string | undefined | null)[],
  minIdLength: number = PRUNE_MIN_ID_LENGTH,
) => {
  const keepIds = ids.filter((id): id is string => !!id && id.length > minIdLength);
  if (keepIds.length === 0) return;

  const { error } = await supabase
    .from(table)
    .delete()
    .eq('project_id', projectId)
    .not('id', 'in', `(${keepIds.map(id => `"${id}"`).join(',')})`);
  if (error) throw error;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER RIEGEL: SOEBEN UMGEZOGENE LEISTUNGEN UEBERLEBEN DEN NAECHSTEN PRUNE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ─── Der Datenverlust, gegen den das gebaut ist ────────────────────────────
 * `applyLinkReturn` haengt beim Trennen die Positionen an die Tochter zurueck.
 * Danach speichert der Aufrufer den Bericht der Tochter — und `saveProjectToCloud`
 * gleicht `project_services` ueber eine Behalte-Liste ab: die RPC
 * `save_project_children_tx` loescht jede Zeile des Projekts, deren id in der
 * gelieferten Liste FEHLT. Enthaelt der uebergebene Bericht die soeben
 * zurueckgehaengten Zeilen nicht, loescht derselbe Speichervorgang sie wieder.
 * Die Positionen sind dann weg — mit ihren Buchungen, ihren Rechnungsbezuegen
 * und der Menge, die auf ihnen stand.
 *
 * Der Aufrufer (`useProjectLinking`) mischt sie inzwischen ein. Das genuegt
 * nicht: der SCHREIBWEG darf sich nicht auf die Sorgfalt seiner Aufrufer
 * verlassen, sonst verliert der naechste, der davon nichts weiss, die
 * Positionen — und das ist genau die Klasse Fehler, aus der `pruneOrphans`
 * seine Vorgeschichte hat.
 *
 * ─── Wie der Riegel arbeitet ───────────────────────────────────────────────
 * Ein Umzug vermerkt hier die ids, die er einem Projekt angehaengt hat.
 * `saveProjectToCloud` sieht vor dem Bauen der Behalte-Liste nach, ob es
 * vermerkte ids gibt, die die eigene Liste des Aufrufers nicht kennt, und
 * ergaenzt sie — aber NICHT mit erfundenen oder gemerkten Werten, sondern mit
 * der Zeile, wie sie in genau diesem Augenblick in der Datenbank steht. Ein
 * gemerkter Stand koennte veraltet sein, und ein veralteter Wert
 * zurueckgeschrieben ist die teuerste Fehlerklasse dieses Projekts. Die aus
 * der Datenbank gelesene Zeile unveraendert zurueckzuschreiben kann dagegen
 * nichts verstellen.
 *
 * ─── Die zwei Grenzen ──────────────────────────────────────────────────────
 *  1. NUR wenn die Behalte-Liste des Aufrufers ohnehin schon nicht leer ist.
 *     Bei LEERER Behalte-Liste loescht der Prune ausdruecklich nichts
 *     (Datenverlust-Guard vom 21.07.2026, gleichlautend in `pruneOrphans` und
 *     in der RPC). Wuerde der Riegel dort ids ergaenzen, machte er aus einem
 *     Speichern, das nichts loescht, eines, das ALLES UEBRIGE loescht — er
 *     kehrte den Guard in sein Gegenteil. Ist die Liste leer, sind die
 *     zurueckgeholten Zeilen ohnehin sicher.
 *  2. Der Vermerk faellt weg, sobald der Aufrufer die id selbst fuehrt: dann
 *     hat er sie uebernommen, und der Riegel hat nichts mehr zu tun.
 */
//
// ─── Seit dem 21.10.2026 auch fuer die Bergung (6185) ──────────────────────
// Derselbe Vermerk schuetzt jetzt auch Zeilen, die ein UEBERHOLTER Stand nie
// gesehen hat (siehe `vermerkeUnbekannteServerZeilen`), und zwar in
// `project_services` UND `project_notes`. Deshalb ist der Schluessel
// Tabelle + Projekt (`vermerkSchluessel`), nicht mehr das Projekt allein.
const umgezogeneLeistungen = new Map<string, Set<string>>();

/** Die beiden Tabellen, deren Zeilen ein Vermerk schuetzen kann. */
type GeschuetzteTabelle = 'project_services' | 'project_notes';

const vermerkSchluessel = (table: GeschuetzteTabelle, projectId: string): string => `${table}|${projectId}`;

/** Vermerkt ids einer Tabelle als geschuetzt — Begruendung oben. */
const merkeGeschuetzteZeilen = (table: GeschuetzteTabelle, projectId: string, ids: string[]) => {
  const id = (projectId || '').trim();
  if (!id) return;
  const schluessel = vermerkSchluessel(table, id);
  const menge = umgezogeneLeistungen.get(schluessel) || new Set<string>();
  for (const s of ids) if (s) menge.add(s);
  if (menge.size > 0) umgezogeneLeistungen.set(schluessel, menge);
};

/**
 * Vermerkt Leistungs-Ids, die soeben an `projectId` gehaengt wurden.
 * Aufgerufen von `applyLinkTransfer` (an der Mutter) und `applyLinkReturn`
 * (an der Tochter) — beide erst NACH dem bestaetigten Umhaengen.
 */
const merkeUmgezogeneLeistungen = (projectId: string, ids: string[]) =>
  merkeGeschuetzteZeilen('project_services', projectId, ids);

/**
 * Liefert die DB-Zeilen der vermerkten Leistungen, die in der Behalte-Liste
 * des Aufrufers fehlen — leer, wenn nichts zu schuetzen ist. Die Begruendung
 * steht vollstaendig oben.
 *
 * Wirft, wenn die Zeilen nicht gelesen werden koennen. Ein fehlgeschlagenes
 * Speichern ist zu wiederholen; eine weggeprunte Leistungsposition nicht.
 */
const fehlendeUmgezogeneRows = async (
  projectId: string,
  eigeneIds: string[],
  table: GeschuetzteTabelle = 'project_services',
): Promise<any[]> => {
  const schluessel = vermerkSchluessel(table, projectId);
  const vermerkt = umgezogeneLeistungen.get(schluessel);
  if (!vermerkt || vermerkt.size === 0) return [];

  for (const id of eigeneIds) vermerkt.delete(id);
  if (vermerkt.size === 0) {
    umgezogeneLeistungen.delete(schluessel);
    return [];
  }

  // Grenze 1: ohne qualifizierende eigene id prunt weder `pruneOrphans` noch
  // die RPC — dann darf hier auch nichts ergaenzt werden.
  const behalteIds = eigeneIds.filter(id => !!id && id.length > PRUNE_MIN_ID_LENGTH);
  if (behalteIds.length === 0) return [];

  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('project_id', projectId)
    .in('id', [...vermerkt]);
  if (error) {
    throw new Error(
      (table === 'project_services'
        ? 'Soeben umgezogene Leistungen liessen sich vor dem Speichern nicht pruefen: '
        : 'Geschuetzte Notizen liessen sich vor dem Speichern nicht pruefen: ')
      + `${error.message || error}. Es wurde NICHT gespeichert — sonst haette der Abgleich sie geloescht.`,
    );
  }
  // Zeilen, die es nicht mehr gibt, werden NICHT erfunden: eine inzwischen
  // geloeschte Position als Behalte-Zeile zurueckzuschreiben legte sie neu an.
  // Genau deshalb wird hier gelesen statt gemerkt — `ProjectSupplementModal`
  // loescht eine Position ausdruecklich (`.delete().eq('id',…)`), und ein
  // gemerktes Objekt haette sie beim naechsten Speichern wieder angelegt.
  const gefunden = data || [];
  // Und was die Datenbank nicht mehr fuehrt, ist auch nicht mehr zu schuetzen:
  // der Vermerk faellt weg, statt bei jedem Speichern erneut nachzusehen.
  const vorhanden = new Set(gefunden.map((r: any) => String(r.id)));
  for (const id of [...vermerkt]) if (!vorhanden.has(id)) vermerkt.delete(id);
  if (vermerkt.size === 0) umgezogeneLeistungen.delete(schluessel);
  return gefunden;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE BERGUNG LOESCHT NICHTS, WAS DER EIGENE STAND NIE GESEHEN HAT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ─── Der Datenverlust (6185, 16.09.2026; vorher 5882, 07.09.2026) ──────────
 * Ein Stand von VOR dem Einlesen einer Alt-Rechnung speicherte; der Anker
 * traf nicht, die Bergung rettete das Protokoll (die ALT- und BU-Zeilen
 * blieben), und die Zaehler blieben beim Server. Danach glich der Save die
 * Kind-Tabellen gegen die ALTE eigene Liste ab — und loeschte die zwei
 * Positionen, die das Einlesen angelegt hatte (abgerechnet auf
 * RE-26081119, 630 € netto), dazu drei Notizen. Die naechste kumulative
 * Rechnung haette den vollen Abzug gegen einen zu kleinen Gesamtstand
 * gerechnet.
 *
 * ─── Die Regel ─────────────────────────────────────────────────────────────
 * Dieselbe wie fuer das Protokoll in `salvageForeignLogEntries`: ohne
 * Ausgangsstand kann keine fehlende Zeile als „von mir entfernt" gelten. Also
 * wird jede Zeile, die der Server fuehrt und der eigene Stand nicht, als
 * geschuetzt vermerkt — fuer DIESES Speichern und fuer jedes weitere dieser
 * Laufzeit, bis der Aufrufer sie selbst fuehrt (nach dem Neuladen) oder die
 * Datenbank sie nicht mehr kennt. Der zweite Teil ist der wichtige: nach der
 * Bergung traegt der Anker den frischen Stand, und schon das naechste
 * Autosave ginge sonst ohne Bergung mit derselben alten Liste raus.
 *
 * Bewusst nur `project_services` und `project_notes`: dort sitzen Geld und
 * Nachweis. Eine absichtliche Loeschung in einem ueberholten Stand wird
 * dadurch nicht vollzogen — die Zeile erscheint nach dem Neuladen wieder.
 * Das ist sichtbar und korrigierbar; eine still geloeschte, abgerechnete
 * Position ist es nicht.
 *
 * ─── Wann ──────────────────────────────────────────────────────────────────
 * VOR dem Schreiben von `projects.data` — bei der Bergung zwischen dem
 * verfehlten und dem zweiten Update, mit `kindZeilen: 'nur-ergaenzen'` vor dem
 * ersten. Scheitert das Lesen, hat die Datenbank nichts gesehen. Stuende der
 * Vermerk erst danach an, liesse ein Lesefehler den frischen Anker ohne
 * Vermerk zurueck, und das naechste Speichern raeumte ab (Codex P1,
 * 21.10.2026).
 *
 * ─── Auch bei LEERER eigener Liste ─────────────────────────────────────────
 * Vermerkt wird immer, auch wenn die eigene Liste leer ist: dann prunt die
 * RPC in DIESEM Speichern zwar nicht, aber sobald der Anwender seine erste
 * Notiz anlegt, traefe das naechste Speichern den frischen Anker und loeschte
 * alles Unbekannte (Codex P1, 21.10.2026). Nachgelesen werden die Zeilen erst,
 * wenn es etwas zu schuetzen gibt (`fehlendeUmgezogeneRows`, Grenze 1).
 *
 * Wirft, wenn die Datenbank nicht lesbar ist — ein gescheitertes Speichern
 * ist zu wiederholen, eine weggeprunte Position nicht.
 */
const vermerkeUnbekannteServerZeilen = async (
  table: GeschuetzteTabelle,
  projectId: string,
  eigeneIds: (string | undefined | null)[],
): Promise<void> => {
  const { data, error } = await supabase.from(table).select('id').eq('project_id', projectId);
  if (error) {
    throw new Error(
      `Der Serverstand (${table}) liess sich vor dem Speichern nicht lesen: ${error.message || error}. `
      + 'Es wurde NICHT gespeichert — sonst haette der Abgleich Zeilen geloescht, die dieser Stand nie gesehen hat.',
    );
  }
  const eigene = new Set(eigeneIds.filter(Boolean).map(String));
  const unbekannt = (data || []).map((r: { id: unknown }) => String(r.id)).filter((id: string) => !eigene.has(id));
  if (unbekannt.length > 0) merkeGeschuetzteZeilen(table, projectId, unbekannt);
};

/** Beide geschuetzten Tabellen fuer einen ueberholten Stand vermerken. */
const vermerkeFuerUeberholtenStand = async (projectId: string, report: Report): Promise<void> => {
  await vermerkeUnbekannteServerZeilen(
    'project_services', projectId, (report.metadata?.globalData?.services || []).map((s: ServiceItem) => s.id),
  );
  await vermerkeUnbekannteServerZeilen(
    'project_notes', projectId, (report.metadata?.globalData?.notes || []).map((n: Note) => n.id),
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PAKETPOSITION: DIE DEFINITION GEHT NUR MIT, WENN SIE SICH GEAENDERT HAT
// ═══════════════════════════════════════════════════════════════════════════
//
// ─── Der Befund (F1, 19.10.2026) ───────────────────────────────────────────
// Tab B laedt eine Position mit `bestandteile = null`. Ein Admin legt in Tab A
// die Bestandteile fest (die Position ist schon gebucht). B speichert spaeter
// irgendetwas — oder die Bergung nach dem Ankerfehler, oder ein alter
// Warteschlangeneintrag — und schickt sein altes `null` mit. Der Trigger aus
// Nr. 166 haelt nur Nicht-Admins auf; die Definition ist weg, das Gate „kein",
// das Paket abrechenbar. Das Muster von `flat_rate` („mitsenden, sobald
// gelesen") reicht hier nicht.
//
// ─── Die Regel ─────────────────────────────────────────────────────────────
// Gesendet wird `bestandteile` nur, wenn
//   (a) die Position NEU ist — sie traegt keine Basis
//       (`ServiceItem.bestandteileBasis`) oder eine Basis mit fremder `id`
//       (Kopie einer Vorlage) und wurde in dieser Laufzeit noch nicht
//       geschrieben; oder
//   (b) die Definition sich gegenueber IHRER Basis tatsaechlich geaendert hat
//       (Vergleich der normalisierten Liste, `definitionsJson`).
// Die Basis reist MIT dem Stand (Feld an der Position, per Spread
// weitergereicht, auch durch die Warteschlange) — deshalb sieht ein alter
// Stand immer seinen eigenen Ladestand, nie einen frischeren, der zufaellig in
// derselben Laufzeit geladen wurde. Kein Rateversuch gegen den Server: ein
// unveraenderter Wert hat nichts zu sagen, also wird nichts gesagt.
//
// ─── Nach dem eigenen Speichern ─────────────────────────────────────────────
// Was diese Laufzeit bestaetigt geschrieben hat, wird zur neuen Basis — aber
// nur fuer Staende DERSELBEN Herkunft (gleicher Ladevorgang). Sonst ginge
// „festlegen, speichern, wieder entfernen" verloren: das Entfernen verglich
// gegen die alte Ladebasis `null` und hielte sich fuer unveraendert. Ein
// anderer Stand derselben Position (anderer Ladevorgang) bleibt davon
// unberuehrt — sonst saehe ein veralteter Stand die fremde Aenderung als
// „eigene Aenderung" und schickte sein altes `null` doch wieder.
//
// Rein im Speicher dieser Laufzeit; nach einem Neuladen tragen die Staende
// frische Basen. Waechst nur um gesendete Definitionen (selten).
const definitionZuletztGesendet = new Map<string, string>();

/** Schluessel der Abstammung: Ladevorgang + Position, ohne Basis: Projekt + Position. */
const definitionSchluessel = (s: ServiceItem, projektId: string): string => {
    const basis = s.bestandteileBasis;
    return basis && String(basis.id) === String(s.id)
        ? `${basis.herkunft}|${s.id}`
        : `neu|${projektId}|${s.id}`;
};

/** Soll `bestandteile` fuer diese Position mitgehen? Siehe die Regel oben. */
const definitionSenden = (s: ServiceItem, projektId: string): boolean => {
    // Die Spalte nie gelesen (Rueckfall, alter Stand): nichts zu sagen.
    if (s.bestandteile === undefined) return false;
    const basis = s.bestandteileBasis && String(s.bestandteileBasis.id) === String(s.id)
        ? s.bestandteileBasis.json
        : null;
    const vergleich = definitionZuletztGesendet.get(definitionSchluessel(s, projektId)) ?? basis;
    // Keine Basis und noch nie geschrieben: eine neue Position — ihre
    // Definition (aus Katalog, Angebot oder Nachtrag kopiert) muss mit.
    if (vergleich === null) return true;
    return definitionsJson(s.bestandteile) !== vergleich;
};

/**
 * Merkt sich, welcher STAND gesendet wurde — wenn die fertige Zeile (nach
 * `ohneFehlendeSpalten`) `bestandteile` wirklich traegt. Gemerkt wird die Form
 * des Stands (`definitionsJson(s.bestandteile)`), nicht die der Zeile: genau
 * gegen sie vergleicht `definitionSenden` beim naechsten Speichern (`[]` geht
 * als `null` raus, bleibt im Stand aber `[]`).
 */
const merkeGesendeteDefinition = <T extends Record<string, unknown>>(
    merker: Array<[string, string]>,
    s: ServiceItem,
    projektId: string,
    zeile: T,
): T => {
    if (Object.prototype.hasOwnProperty.call(zeile, 'bestandteile')) {
        merker.push([definitionSchluessel(s, projektId), definitionsJson(s.bestandteile)]);
    }
    return zeile;
};

/** Nur fuer Tests: das Gedaechtnis der gesendeten Definitionen leeren. */
export const setzeDefinitionsGedaechtnisZurueck = (): void => { definitionZuletztGesendet.clear(); };

/** Eine Kind-Tabelle im Save: fertige DB-Rows + Quell-Ids für den Prune. */
interface ChildSyncEntry {
  table: string;
  rows: any[];
  ids: (string | undefined | null)[];
  minIdLength?: number;
}

const isMissingRpc = (error: any) =>
  error?.code === 'PGRST202' || /could not find the function/i.test(error?.message || '');

/**
 * Schreibt alle Kind-Tabellen eines Projekts atomar über die Postgres-RPC
 * save_project_children_tx (supabase/save_project_children_tx.sql): upsert +
 * prune aller Tabellen in EINER Transaktion — ein Abbruch mittendrin kann
 * keinen halb gespeicherten Zustand mehr hinterlassen.
 *
 * Ist die RPC (noch) nicht deployt (PGRST202), läuft der bisherige
 * nicht-transaktionale Pfad mit identischer Reihenfolge und identischen
 * Prune-Regeln. Jeder andere RPC-Fehler bricht den Save ab.
 */
const syncChildrenTransactional = async (projectId: string, syncs: ChildSyncEntry[]) => {
  const payload: Record<string, any[]> = {};
  for (const s of syncs) payload[s.table] = s.rows;

  const { error } = await supabase.rpc('save_project_children_tx', { p_project_id: projectId, payload });
  if (!error) return;
  if (!isMissingRpc(error)) throw error;

  for (const s of syncs) {
    if (s.rows.length > 0) {
      const { error: upsertError } = await supabase.from(s.table).upsert(s.rows);
      if (upsertError) throw upsertError;
    }
    await pruneOrphans(s.table, projectId, s.ids, s.minIdLength);
  }
};

// --- PROJEKT SPEICHERN ---

/**
 * Speichert den übergebenen Stand — seit 2026-08-31 mit einer BEDINGUNG.
 *
 * Bis dahin stand hier „speichert IMMER": `update ... where id = ?`, ohne
 * jeden Vergleich mit dem Serverstand. Damit durfte jeder Client jeden
 * beliebig alten Blob zurückschreiben, und genau das ist passiert — die
 * mobile Offline-Warteschlange hat stundenalte Stände hochgeschoben und dabei
 * 19 Buchungszeilen aus fünf Projekten gelöscht (MIGRATIONS.md Nr. 64).
 *
 * Die Bedingung ist `updated_at = <zuletzt gesehener Stand>`; woher der kommt,
 * steht in src/services/projectVersionAnchor.ts. Trifft sie nicht, wird NICHT
 * geschrieben, sondern `ProjectSaveConflictError` geworfen — mit beiden
 * Ständen, damit ein Aufrufer zusammenführen kann statt nur zu melden.
 *
 * Das nimmt „last write wins" NICHT zurück: der Konfliktdialog bleibt
 * abgeschafft, gegen echtes Parallelarbeiten steht weiter die Projektsperre.
 * Es unterscheidet nur den frischen Schreibvorgang (dessen lokaler Stand
 * tatsächlich der aktuellste ist) vom veralteten (dessen Schreiber der
 * älteste Stand im Raum ist). Wer keinen Anker hat, schreibt wie bisher
 * bedingungslos; `saveOptions.anchor: 'off'` schaltet ihn bewusst ab.
 */
/**
 * Die Maengel eines Berichts fuer die Datenbank aufbereiten: Bilder hochladen
 * (Ordner = Projekt-id), Platzhalter abweisen, Zeilen bauen. Reine
 * Vorbereitung ohne Datenbankschreiben — deshalb darf sie VOR dem ersten
 * Schreiben laufen (siehe saveProjectToCloud, „Maengelbilder vor dem ersten
 * Schreiben"). `urlItem` ist die Fassung mit URLs statt Base64 fuer das
 * Archiv (assetSink).
 */
const verarbeiteMaengel = async (
    items: any[],
    projectFolder: string,
): Promise<Array<{ dbRow: any; urlItem: any }>> => {
    const ASSET_CONCURRENCY = 8;
    return processInChunks(items, ASSET_CONCURRENCY, async (item) => {
        if (Array.isArray(item.photos) && item.photos.includes('placeholder')) {
            throw new Error("Speichern blockiert: Bilder wurden nicht korrekt geladen. Bitte laden Sie die Seite neu, um Datenverlust zu vermeiden.");
        }
        const processedPhotos = await Promise.all((item.photos || []).map((p: string) => uploadAsset(p, projectFolder)));
        let closedImage = item.closedImage;
        if (closedImage) closedImage = await uploadAsset(closedImage, projectFolder);
        const dbRow = {
            id: item.id,
            project_id: projectFolder,
            ticket_number: item.ticketNumber ?? null,
            title: item.title,
            description: item.observation,
            location: item.location,
            responsible_person: item.responsiblePerson,
            deadline: dateOrNull(item.deadline),
            immediate_action: !!item.immediateAction,
            date_found: dateOrNull(item.dateFound),
            status: item.status,
            photos: processedPhotos,
            regulation: item.regulation,
            section_id: item.sectionId,
            closed_at: dateOrNull(item.closedDate),
            status_comment: item.statusComment,
            closed_image: closedImage,
            // MAENGELKATALOG (Nr. 131) — Muster Verzicht: ein Schluessel nur,
            // wenn das Feld definiert ist (fehlend = RPC laesst den Bestand
            // stehen, null = bewusst geloescht). Fehlt Nr. 131, laesst die RPC
            // die unbekannten Spalten weg (fail-open).
            ...katalogSpaltenSchreiben(item),
            // NADELN (Nr. 130) — Muster closed_at, NICHT Muster Verzicht: die
            // drei Schluessel stehen IMMER drin, null = keine Nadel. Der Client
            // liest sie beim Laden, eine entfernte Nadel muss als NULL ankommen.
            // Alte Clients schicken sie nicht → die RPC laesst den Bestand stehen.
            ...pinSpaltenSchreiben(item),
        };
        const urlItem = { ...item, photos: processedPhotos, closedImage };
        return { dbRow, urlItem };
    });
};

/**
 * Der Speicherweg selbst — die Huelle darunter (`saveProjectToCloud`) faengt
 * den Build-Riegel des Servers ab. Getrennt, damit dafuer nicht 500 Zeilen
 * eine Ebene tiefer ruecken mussten.
 */
const saveProjectToCloudIntern = async (report: Report, projectId?: string, ownerId?: string, forceLastExportedNr?: number, assetSink?: { items?: any[]; plans?: any[] }, saveOptions?: SaveAnchorOptions, kindLauf: KindLauf = neuerKindLauf()): Promise<any | null> => {
  // BLEIBT eine Serverabfrage (nicht getSession): sie trennt VOR dem ersten
  // Schreiben „kein Netz" von „abgemeldet" (netzFehler.ts), und sie ist die
  // Stelle, an der eine beendete Sitzung (Sperre Nr. 138 loescht
  // auth.sessions) das Autospeichern sofort anhaelt — getSession() wuerde den
  // noch gueltigen Zugangs-Token bis zu einer Stunde weiter benutzen.
  const { data: { user }, error: anmeldeFehler } = await supabase.auth.getUser();
  // Kein Netz oder Auth-Server gestört → NetzFehler ('retryable', Anzeige
  // 'offline'); sonst unverändert „Nicht eingeloggt" (→ 'auth-required').
  // Siehe netzFehler.ts.
  if (!user) throw fehlerOhneNutzer(anmeldeFehler, geraetOnline());

  const { items, checklists, ...reportWithoutData } = report;

  // BILD-PRUEFUNG VOR DEM ERSTEN SCHREIBEN (07.09.2026). Dieselbe Pruefung
  // stand bisher nur im Bilder-Lauf der Maengel — und der kommt NACH dem
  // Schreiben von projects.data und VOR den Positionen. Wirft sie dort, ist
  // die Protokollzeile der Buchung schon drin, der Zaehler nicht: acht
  // Berichte an einem Vormittag (26071027, 26071013, 5944, 5876, 6195, 6224,
  // 26081024, 6221) standen mit „Bericht #N abgeschlossen" im Protokoll und
  // quantity_used N-1, und jeder Klick auf „Erneut speichern" buchte aus dem
  // alten Dialogstand neu (Riegel Nr. 74 fing die Zwillinge, der Zaehler
  // blieb). Ein Speichern, das scheitern muss, scheitert deshalb hier —
  // bevor irgendetwas die Datenbank erreicht.
  if (Array.isArray(items) && items.some((item: any) => Array.isArray(item?.photos) && item.photos.includes('placeholder'))) {
      throw new Error("Speichern blockiert: Bilder wurden nicht korrekt geladen. Bitte laden Sie die Seite neu, um Datenverlust zu vermeiden.");
  }

  let reportData = JSON.parse(JSON.stringify(reportWithoutData));
  
  if (reportData.metadata) {
      const cat = reportData.metadata.projectCategory || reportData.metadata.category || null;
      const sub = reportData.metadata.projectSubcategory || reportData.metadata.subcategory || null;
      reportData.metadata.projectCategory = cat;
      reportData.metadata.category = cat;
      reportData.metadata.projectSubcategory = sub;
      reportData.metadata.subcategory = sub;
  }
  
  if (reportData.metadata?.globalData) {
      reportData.metadata.globalData.notes = [];
      reportData.metadata.globalData.services = [];
      // DIE VERKNUEPFUNG GEHT NICHT MIT. Seit Nr. 71 steht sie in den Spalten
      // `parent_project_id` / `linked_billing_mode` / `linked_billing_history`,
      // und dieser Speicherweg fasst sie NIE an — weder die Spalten (sie
      // stehen nicht in commonPayload) noch das Blob-Feld. Genau darin liegt
      // der Schutz: am 02.09.2026 hat ein Speichern mit veraltetem Stand die
      // Verknuepfung von 6290-2 aus dem Blob geloescht, die Bergung unten
      // rettet nur das Protokoll. Geschrieben wird sie ausschliesslich von
      // verknuepfen/trennen/Art umstellen (services/projectLinkService.ts);
      // beim Laden wird das Feld aus den Spalten zusammengesetzt
      // (services/projectLink.ts).
      delete reportData.metadata.globalData.linkedProjectConfig;
  }
  reportData.metadata.distributionList = (report.metadata.distributionList || []).map((c, idx) => ({ ...c, sortOrder: idx })); 
  reportData.metadata.plans = [];

  if (reportData.unterlage?.projectData?.appendixImages) {
      reportData.unterlage.projectData.appendixImages = []; 
  }
  if (reportData.unterlage?.rabEntries) {
      reportData.unterlage.rabEntries = [];
  }

  const internalId = reportData.metadata.globalData?.projectId;
  let targetProjectId = projectId || (internalId && internalId !== 'temp-id' ? internalId : null);
  const projectFolder = targetProjectId || crypto.randomUUID();
  // Ab hier gehoert dieses Speichern zu einem bestehenden Projekt: den
  // gemerkten Stand der Kindtabellen ENTNEHMEN (childSaveDelta.ts). Vor dem
  // ersten Schreiben — scheitert irgendetwas danach, steht keiner mehr da.
  if (targetProjectId) oeffneKindLauf(kindLauf, targetProjectId);
  const sparsam = saveOptions?.uebertragung === 'sparsam';

  reportData = await processReportAssets(reportData, projectFolder);

  // MAENGELBILDER VOR DEM ERSTEN SCHREIBEN (07.09.2026, Teil 2). Bisher lief
  // der Bilder-Lauf der Maengel ZWISCHEN dem Schreiben von projects.data und
  // den Positionen: brach er ab (Platzhalter, gescheiterter Upload am
  // Baustellennetz), stand die Buchungszeile im Protokoll, der Zaehler nicht
  // — acht Berichte an einem Vormittag. Jetzt wird alles, was scheitern
  // kann, VOR dem ersten Datenbankschreiben erledigt: Uploads, Platzhalter-
  // Pruefung, Zeilenbau. Scheitert es, hat die Datenbank nichts gesehen.
  //
  // Nur fuer BESTEHENDE Projekte: dort ist der Ordner die Projekt-id und
  // damit dieselbe wie bisher. Ein neues Projekt bekommt seine id erst vom
  // Insert; dessen Bilder laufen wie gehabt danach (ein neues Projekt hat
  // beim ersten Speichern keine Buchung, die verloren gehen koennte).
  const vorbereiteteMaengel = targetProjectId && items && items.length > 0
      ? await verarbeiteMaengel(items, targetProjectId)
      : null;

  let finalOwnerId = ownerId;
  // Der Besitzer wird hier nur GELESEN, um ihn unveraendert zurueckzuschreiben
  // (`updatePayload.user_id`). Im sparsamen Modus faellt die Abfrage weg: ohne
  // den Schluessel bleibt die Spalte, wie sie ist — dasselbe Ergebnis, ohne
  // Netzrunde und ohne das Fenster, in dem ein inzwischen gewechselter
  // Besitzer mit dem eben gelesenen alten ueberschrieben wuerde. Wer den
  // Besitzer wirklich setzen will, uebergibt `ownerId`.
  if (targetProjectId && !finalOwnerId && !sparsam) {
      const { data: existingProj } = await supabase.from('projects').select('user_id').eq('id', targetProjectId).single();
      if (existingProj && !finalOwnerId) finalOwnerId = existingProj.user_id;
  }
  if (!finalOwnerId && !targetProjectId) finalOwnerId = user.id;

  const currentInspectionNumber = reportData.metadata.inspectionNumber !== undefined ? reportData.metadata.inspectionNumber : 1;

  const commonPayload: any = {
    name: reportData.metadata.projectName || 'Unbenanntes Projekt',
    updated_at: new Date().toISOString(),
    project_number: reportData.metadata.projectNumber,
    current_inspection_number: currentInspectionNumber,
    status: reportData.metadata.globalData?.status || 'active',
    last_inspection_date: ensureIsoDate(reportData.metadata.date),
    next_scheduled_date: ensureIsoDate(reportData.metadata.intervalConfig?.nextScheduledDate),
    coordinator_name: reportData.metadata.coordinator,
    category: reportData.metadata.category || null,
    subcategory: reportData.metadata.subcategory || null,
    data: reportData 
  };

  if (forceLastExportedNr !== undefined) {
      commonPayload.last_exported_inspection_number = forceLastExportedNr;
  }

  let serverResponse: any = null;

  // ZAEHLER VOM SERVER (07.09.2026). Wer ausdruecklich darum bittet ODER
  // unten durch die Bergung geht, schreibt quantity_used / quantity_billed /
  // status der Positionen NICHT — die Schluessel fehlen dann in den Zeilen,
  // und save_project_children_tx laesst einen fehlenden Schluessel stehen.
  // Warum: die Bergung rettet das PROTOKOLL des fremden Stands, die Zaehler
  // kamen aber weiter aus dem eigenen, alten Stand. 24 Projekte standen so
  // mit Protokoll N und quantity_used N-1 da (alte Tabs, alte Warteschlange).
  let zaehlerVomServer = saveOptions?.counters === 'keep-server';
  // Fuer das Gedaechtnis der Kindtabellen (childSaveDelta.ts): gegen welchen
  // Anker geschrieben wurde und ob er im ERSTEN Versuch getroffen hat. Nur
  // dann steht fest, dass seit dem gemerkten Schreiben niemand `projects`
  // gespeichert hat.
  let kindAnker: string | null = null;
  let ankerGetroffen = false;

  if (targetProjectId) {
    // KEINE LOESCHUNG AUS DEM UEBERHOLTEN STAND (21.10.2026, 6185). Wer darum
    // bittet ODER unten durch die Bergung geht, loescht in project_services und
    // project_notes nichts, was der Server fuehrt und dieser Stand nicht.
    // Vermerkt wird VOR dem ersten Schreiben — Begruendung an
    // `vermerkeUnbekannteServerZeilen`.
    if (saveOptions?.kindZeilen === 'nur-ergaenzen') {
        await vermerkeFuerUeberholtenStand(targetProjectId, report);
    }
    const updatePayload: any = { ...commonPayload };
    if (finalOwnerId) updatePayload.user_id = finalOwnerId;
    // DER ANKER. Nur wenn wir einen Serverstand kennen — sonst wie bisher
    // bedingungslos (siehe projectVersionAnchor.ts, Abschnitt "opportunistisch").
    const expectedUpdatedAt = resolveSaveAnchor(targetProjectId, saveOptions);
    kindAnker = expectedUpdatedAt;
    const projektUpdate = () => {
        let q = supabase.from('projects').update(updatePayload).eq('id', targetProjectId);
        if (expectedUpdatedAt) q = q.eq('updated_at', expectedUpdatedAt);
        return q;
    };
    // SCHMALE ANTWORT (schmaleSpeicherAntwort.ts): im sparsamen Modus ohne
    // den Blob, nur Spalten + das Protokoll des Servers. Kennt die Datenbank
    // eine Spalte der Liste nicht (42703), ist die GANZE Anweisung vor dem
    // Schreiben gescheitert — dann genau einmal wie bisher mit `.select()`.
    let schmal = sparsam && schmaleAntwortMoeglich();
    let antwort = await (schmal ? projektUpdate().select(schmaleProjektAuswahl()) : projektUpdate().select()).maybeSingle();
    if (schmal && antwort.error && istUnbekannteSpalte(antwort.error)) {
        console.warn('[cloudService] Schmale Speicherantwort abgelehnt, volle Zeile:', antwort.error.message);
        sperreSchmaleAntwort();
        schmal = false;
        antwort = await projektUpdate().select().maybeSingle();
    }
    const { error } = antwort;
    const data = antwort.data && schmal ? zeileAusSchmalerAntwort(antwort.data, updatePayload.data) : antwort.data;
    if (error) throw error;
    if (!data) {
        // "Keine Zeile getroffen" hat jetzt ZWEI Ursachen, und sie
        // auseinanderzuhalten ist der ganze Zweck: entweder ist der Anker
        // überholt (jemand anders hat gespeichert) oder die Projektzeile gibt
        // es nicht mehr / RLS verweigert sie. Ohne diese zweite Abfrage
        // erschiene ein Konflikt als "Projekt weg" — und ein gelöschtes
        // Projekt als Konflikt, den jemand durch Wiederholen zu lösen
        // versuchte.
        if (expectedUpdatedAt) {
            const { data: current } = await supabase
                .from('projects').select('updated_at, data').eq('id', targetProjectId).maybeSingle();
            if (current) {
                // ─── DIE BERGUNG ─────────────────────────────────────────
                // Der Regelfall im Betrieb ist nicht "zwei Leute streiten um
                // dasselbe Feld", sondern: derselbe Mitarbeiter bereitet den
                // Bericht mobil vor und arbeitet spaeter am PC weiter,
                // waehrend das Telefon im Hintergrund seine Warteschlange
                // leert. Der Anker trifft dann nicht, obwohl niemand etwas
                // Widersprechendes getan hat. Diesen Fall als Fehler zu
                // melden waere eine Sackgasse — es gaebe nichts zu
                // entscheiden, und die Arbeit bliebe liegen.
                //
                // Also: einmal den Serverstand holen, jede Protokollzeile
                // uebernehmen, die dieser Stand nicht kennt, und mit dem
                // FRISCHEN Anker erneut schreiben. Ohne Ausgangsstand kann
                // keine Zeile als "von mir entfernt" gelten, es wird deshalb
                // nichts entfernt.
                //
                // GRENZE, und sie steht hier, damit sie niemand fuer geloest
                // haelt: gerettet wird das PROTOKOLL. Alles andere (Notizen,
                // Metadaten, Berichtsinhalte) schreibt dieser Stand weiterhin
                // ueber den fremden. Wer wirklich zusammenfuehren will,
                // braucht den Ausgangsstand — den hat nur die Warteschlange,
                // und die faehrt deshalb `onConflict: 'throw'`.
                const aktuellerStand = (current as any).updated_at ?? null;
                const bergen = (saveOptions?.onConflict ?? 'salvage') === 'salvage';
                if (bergen && aktuellerStand) {
                    const geborgen = salvageForeignLogEntries(
                        commonPayload.data as Report,
                        ((current as any).data ?? {}) as Report,
                    );
                    if (geborgen.keptForeignLogEntries > 0) {
                        console.warn(
                            `[cloudService] Projekt ${targetProjectId} wurde zwischenzeitlich `
                            + `gespeichert; ${geborgen.keptForeignLogEntries} fremde Protokollzeile(n) `
                            + `uebernommen.`,
                        );
                    }
                    commonPayload.data = geborgen.merged;
                    updatePayload.data = geborgen.merged;
                    // Dieser Stand ist nachweislich aelter als der Server:
                    // er kennt Positionen und Notizen nicht, die nach seinem
                    // Laden entstanden sind, und der Abgleich unten darf sie
                    // nicht loeschen (6185, 16.09.2026). Vermerkt wird HIER,
                    // vor dem zweiten Update — scheitert das Lesen, ist noch
                    // nichts geschrieben.
                    await vermerkeFuerUeberholtenStand(targetProjectId, report);
                    const zweiter = await supabase.from('projects').update(updatePayload)
                        .eq('id', targetProjectId).eq('updated_at', aktuellerStand)
                        .select().maybeSingle();
                    if (zweiter.error) throw zweiter.error;
                    if (zweiter.data) {
                        serverResponse = zweiter.data;
                        rememberProjectVersion(targetProjectId, (zweiter.data as any).updated_at);
                        // Der Anker hat NICHT getroffen: dieser Stand ist
                        // nachweislich aelter als der Server. Was er ueber
                        // die Zaehler weiss, ist damit ueberholt — die
                        // Positionen unten gehen ohne quantity_used /
                        // quantity_billed / status raus (Vorfall 07.09.2026,
                        // 24 Projekte mit Protokoll N und Zaehler N-1).
                        zaehlerVomServer = true;
                    }
                }
                // Ein zweites Mal danebengetroffen heisst: es wird gerade
                // laufend geschrieben. Dann melden statt ein drittes Mal zu
                // raten — sonst bergen wir gegen einen Stand, der schon
                // wieder ueberholt ist.
                if (!serverResponse) {
                    throw new ProjectSaveConflictError(
                        targetProjectId, expectedUpdatedAt, aktuellerStand,
                    );
                }
            }
        }
        if (!serverResponse) throw new Error('Projekt-Update hat keine Zeile getroffen');
    } else {
        serverResponse = data;
        ankerGetroffen = !!expectedUpdatedAt;
    }
  } else {
    const insertPayload: any = { ...commonPayload, user_id: finalOwnerId, is_legacy_data_corrected: true };
    const { data, error } = await supabase.from('projects').insert([insertPayload]).select().single();
    if (error) throw error;
    serverResponse = data;
  }

  const finalId = serverResponse.id;

  // Der neue Anker kommt AUS DER ANTWORT, nie aus `commonPayload.updated_at`:
  // geschrieben hat der Server, und nur was dort steht, trifft die Bedingung
  // des nächsten Speicherns zeichengleich.
  rememberProjectVersion(finalId, serverResponse.updated_at);

  // RELATONALE DATEN SPEICHERN (SQL)
  // Alle Kind-Tabellen werden erst als Rows gesammelt und dann in EINER
  // Transaktion geschrieben (RPC save_project_children_tx). Ein Abbruch
  // mittendrin kann so keinen "halb gespeicherten" Zustand mehr hinterlassen.
  if (finalId) {
      const childSyncs: ChildSyncEntry[] = [];

      const services = report.metadata.globalData?.services || [];
      const notes = report.metadata.globalData?.notes || [];
      // Der Riegel gegen den Prune: Leistungen, die soeben per Umzug an dieses
      // Projekt gehaengt wurden und in `services` fehlen, kommen als ihre
      // EIGENEN DB-Zeilen dazu — sonst loescht dieser Speichervorgang sie
      // wieder. Die vollstaendige Begruendung steht an `umgezogeneLeistungen`.
      const geretteteRows = await fehlendeUmgezogeneRows(finalId, services.map((s: ServiceItem) => s.id));
      // Welche Definitionen dieses Speichern schickt — erst NACH dem
      // bestaetigten Schreiben werden sie zur neuen Basis (siehe
      // `definitionSenden`).
      const definitionenGesendet: Array<[string, string]> = [];
      // FIX 3: Preserve is_optional when upserting services
      childSyncs.push({
          table: 'project_services',
          ids: [...services.map((s: ServiceItem) => s.id), ...geretteteRows.map((r: any) => r.id)],
          // `ohneFehlendeSpalten` ist der Riegel gegen den einen Fehler, der
          // schlimmer waere als der Ausfall selbst: eine beim LESEN
          // weggelassene Spalte darf beim Speichern nicht als „leer"
          // zurueckgeschrieben werden. Im Normalbetrieb (nichts fehlt) gibt er
          // die Zeile unveraendert zurueck; er wirkt ausschliesslich im
          // Notbetrieb. Der Nachweis Spalte fuer Spalte steht in
          // `spaltenRueckfall.ts` — der Fall, an dem alles haengt, ist
          // `min_hours`: der Schluessel steht unten IMMER drin, und ohne
          // diesen Riegel leerte das erste Speichern aus einer Liste die
          // Mindeststunden des ganzen Bestands.
          rows: services.map((s: ServiceItem) => merkeGesendeteDefinition(definitionenGesendet, s, finalId, ohneFehlendeSpalten('public.project_services', {
              id: s.id,
              project_id: finalId,
              lv_position: s.lvPosition,
              title: s.title,
              unit: s.unit,
              unit_price: s.unitPrice,
              quantity_contracted: s.quantityContracted,
              // DIE ZAEHLER — nur, wenn dieser Stand sie kennen darf.
              // Dasselbe Muster wie beim Verzicht weiter unten: ein
              // ausgelassener Schluessel wird von save_project_children_tx
              // nie aktualisiert. Nach einer Bergung (Anker hat nicht
              // getroffen) oder auf Wunsch des Aufrufers (Warteschlange beim
              // Zusammenfuehren) bleiben die drei weg, und der Server behaelt
              // seine Werte. `status` gehoert dazu, weil er nirgends fuer sich
              // gesetzt wird — nur zusammen mit quantity_billed
              // (manualBillingLogic, AdminAccountingView); ein altes 'open'
              // ueber ein 'billed' stellte die Position wieder zur Abrechnung.
              // Eine NEUE Position aus einem alten Stand bekommt so den
              // Tabellen-Default statt eines geratenen Zaehlers.
              ...(zaehlerVomServer
                  ? {}
                  : {
                        quantity_used: s.quantityUsed,
                        quantity_billed: s.quantityBilled,
                        status: s.status,
                    }),
              category: s.category,
              client_lv_position: s.clientLvPosition,
              assignment: s.assignment,
              is_optional: s.is_optional === true,
              // Mindeststunden IMMER mitsenden, leere Angabe als explizites
              // null. save_project_children_tx baut seine Spaltenliste aus
              // jsonb_object_keys: ein ausgelassener Schluessel wird nie
              // aktualisiert — ein geleertes Feld liesse den alten Wert stehen
              // und waere nach dem Neuladen zurueck. Ein JSON-null wird von
              // jsonb_populate_record zu SQL NULL, die Spalte ist NULL-bar.
              // Bedingung dafuer: JEDER Ladepfad muss min_hours mitlesen, sonst
              // leert ein Speichern aus einer unwissenden Ansicht den Bestand.
              min_hours:
                  typeof s.minHours === 'number' && Number.isFinite(s.minHours) && s.minHours > 0
                      ? s.minHours
                      : null,
              // VERZICHT — und hier bewusst GENAU ANDERSHERUM als bei
              // min_hours: die Schluessel stehen nur drin, wenn der Aufrufer
              // etwas dazu zu sagen hat.
              //
              // Grund ist dieselbe Eigenschaft von save_project_children_tx,
              // die eine Zeile hoeher zum "immer mitsenden" zwingt: die
              // Spaltenliste kommt aus jsonb_object_keys, ein fehlender
              // Schluessel wird nie aktualisiert. min_hours darf das nicht,
              // weil es im Dialog leerbar ist. Der Verzicht dagegen wird an
              // GENAU EINER Stelle gesetzt (serviceWaiver.applyWaiver) und
              // sonst von keiner Ansicht verstanden — AdminOpenPositionsView
              // etwa mappt project_services mit eigener Spaltenliste. Wuerde
              // hier immer ein Wert stehen, loeschte das erste Speichern aus
              // einer solchen Ansicht den Verzicht still weg, und die
              // geschlossene Begehung stuende wieder zur Abrechnung.
              //
              // Zuruecknehmen bleibt moeglich: applyWaiver setzt dafuer eine
              // ausdrueckliche 0, und dann steht der Schluessel da.
              ...(s.quantityWaived === undefined
                  ? {}
                  : {
                        quantity_waived: s.quantityWaived,
                        waived_reason: s.waivedReason ?? null,
                        waived_at: s.waivedAt ?? null,
                        waived_by: s.waivedBy ?? null,
                        // WELCHE Leistungen gemeint sind (Nr. 70). Im selben
                        // Spread wie die Menge, weil beides derselbe Vorgang
                        // setzt: eine Zuordnung ohne Verzicht gibt es nicht,
                        // und ein Verzicht ohne Zuordnung ist der Normalfall
                        // bei Stunden — dort steht die leere Liste.
                        waived_entry_ids: s.waivedEntryIds ?? null,
                    }),
              // MONATSPAUSCHALE (Nr. 53) — dasselbe Muster wie beim Verzicht,
              // aus demselben Grund: save_project_children_tx baut seine
              // Spaltenliste aus jsonb_object_keys, ein fehlender Schluessel
              // wird nie aktualisiert. Der Schluessel steht nur drin, wenn die
              // geladene Position eine Deckungsregel TRAEGT — eine Ansicht,
              // die flat_rate nicht mitliest, loescht sie so nicht still weg.
              // Weggeloescht waeren die gedeckten Positionen wieder offenes
              // Geld, und die naechste Rechnung forderte sie doppelt.
              ...(s.flatRate === undefined ? {} : { flat_rate: s.flatRate }),
              // HERKUNFT (Nr. 68) — dasselbe Muster und derselbe Grund: der
              // Schluessel steht nur drin, wenn die geladene Position eine
              // Herkunft TRAEGT. Geschrieben wird sie ohnehin nur einmal, beim
              // Umzug in applyLinkTransfer; hier geht es allein darum, dass ein
              // Speichern aus einer Ansicht ohne dieses Feld sie nicht wieder
              // ausraeumt.
              ...(s.transferredFrom === undefined ? {} : { transferred_from: s.transferredFrom }),
              // PAKETPOSITION (Nr. 165) — strenger als flat_rate: der
              // Schluessel steht nur drin, wenn DIESE Sitzung die Definition
              // geaendert hat oder die Position neu ist
              // (`definitionSenden`). Nicht genug ist „die Spalte ist
              // bekannt": ein alter Tab, ein alter Warteschlangeneintrag oder
              // die Bergung nach einem Ankerfehler traegt den ALTEN Wert, und
              // ein altes `null` setzte die inzwischen festgelegte Definition
              // eines Admins zurueck — die Paketposition waere ohne Gate
              // abrechenbar (Befund F1, 19.10.2026). Der Trigger aus Nr. 166
              // haelt nur Nicht-Admins auf. Eine leere Liste geht als
              // ausdrueckliches `null` raus ([] und null heissen beide „keine
              // Bestandteile", in der Spalte steht nur eine Form).
              //
              // `bestandteile_offen` steht hier mit Absicht NICHT — auch
              // nicht, wenn `s.bestandteileOffen` gesetzt ist. Die Zahl
              // pflegt die Datenbank (Trigger aus Nr. 166) und rechnet sie bei
              // jedem Schreiben ohnehin neu; ein mitgeschickter Client-Wert
              // waere bestenfalls wirkungslos und schlimmstenfalls — ohne den
              // Trigger — ein veraltetes Gate.
              // `[]` (Paket aus lauter unlesbaren Bestandteilen) geht dabei als
              // `null` raus: lesbar ist davon nichts, und eine leere Liste
              // zaehlt die Datenbank ohnehin nicht. Gesendet wird es ohnehin
              // nur, wenn die Sitzung es neu gesetzt hat (z. B. Reparatur).
              ...(definitionSenden(s, finalId)
                  ? { bestandteile: Array.isArray(s.bestandteile) && s.bestandteile.length > 0 ? s.bestandteile : null }
                  : {}),
              // STUNDENKONTINGENT (Nr. 171) — Muster `flat_rate`: der
              // Schluessel steht nur drin, wenn die Position das Feld traegt
              // (`!== undefined`; gelesen oder beim Anlegen kopiert). Anders
              // als `bestandteile` braucht es keine Basis: bei einer
              // BESTEHENDEN Position behaelt die Datenbank den alten Wert
              // (Trigger aus Nr. 172, S10), aendern geht nur ueber die
              // Admin-RPC. Gesendet wird es trotzdem immer, damit die Kopie
              // beim ANLEGEN ankommt (Katalog, Nachtrag, Angebot).
              //
              // `stunden_erfasst` steht hier mit Absicht NIE — die Summe
              // pflegt die Datenbank (Trigger aus Nr. 172), wie
              // `bestandteile_offen`.
              ...stundenKontingentZeile(s),
          }))).concat(
              // Die geretteten Zeilen gehen UNVERAENDERT zurueck, so wie sie
              // eben aus `project_services` gelesen wurden: `ohneFehlendeSpalten`
              // ist hier ueberfluessig (die Spalten kamen aus dieser Tabelle)
              // und der Umweg ueber ein ServiceItem waere gefaehrlich — er
              // koennte einen Wert verlieren, den der Client gar nicht kennt.
              // Der Upsert schreibt damit denselben Stand zurueck, den er
              // vorgefunden hat; gebraucht wird allein, dass die id in der
              // Behalte-Liste steht.
              //
              // EINE Ausnahme: `bestandteile_offen` (Nr. 165) faellt heraus.
              // Die Spalte gehoert der Datenbank, nicht dem Client — sie wird
              // nie gesendet, auch nicht als soeben gelesener Wert: zwischen
              // Lesen und Schreiben kann ein Bestandteil erledigt worden sein,
              // und ohne den Trigger aus Nr. 166 stuende dann wieder das alte
              // Gate in der Zeile. Aus demselben Grund faellt `stunden_erfasst`
              // (Nr. 171) heraus: die Summe pflegt der Trigger aus Nr. 172.
              geretteteRows.map(({ bestandteile_offen: _dbGepflegt, stunden_erfasst: _dbSumme, ...r }: any) => ({ ...r, project_id: finalId })),
          )
      });

      if (items) {
          let itemRows: any[] = [];
          if (items.length > 0) {
            // Bestehendes Projekt: oben schon verarbeitet, bevor irgendetwas
            // geschrieben wurde. Neues Projekt: jetzt, mit der frischen id.
            const itemResults = vorbereiteteMaengel ?? await verarbeiteMaengel(items, finalId);
            if (assetSink?.items) assetSink.items.push(...itemResults.map(r => r.urlItem));
            itemRows = itemResults.map(r => r.dbRow);
          }
          childSyncs.push({ table: 'inspection_items', ids: items.map(i => i.id), rows: itemRows });
      }

      if (checklists) {
          childSyncs.push({
              table: 'project_checklists',
              ids: checklists.map(c => c.id),
              rows: checklists.map(c => ({
                  id: c.id, project_id: finalId, text: c.text, category: c.category, status: c.status, comment: c.comment, last_checked: dateOrNull(c.lastChecked), is_relevant: c.isRelevant, is_custom: c.isCustom, section_id: c.sectionId
              }))
          });
      }

      const contacts = report.metadata.distributionList || [];
      childSyncs.push({
          table: 'project_contacts',
          ids: contacts.map((c: any) => c.id),
          minIdLength: PRUNE_MIN_ID_LENGTH_CONTACTS,
          rows: contacts.map((c: any, idx: number) => ({
              id: c.id,
              project_id: finalId,
              company: c.company,
              name: c.name,
              email: c.email,
              phone: c.phone,
              role: c.role,
              trade_type: c.tradeType,
              is_present: c.present,
              on_distribution_list: c.emailDistribution,
              in_advance_notice: c.inAdvanceNotice ?? true,
              // Nr. 88: Abschnitts-Zuordnung und Adresse standen bisher nur im
              // Blob; beim Laden ersetzten die Tabellenzeilen den Blob und
              // beides war weg.
              section_ids: Array.isArray(c.sectionIds) ? c.sectionIds : [],
              address: c.address ?? null,
              sort_order: idx
          }))
      });

      // Geschuetzte Notizen (nach einer Bergung) kommen erst unmittelbar vor
      // dem Schreiben dazu, siehe unten.
      const notizSync: ChildSyncEntry = {
          table: 'project_notes',
          ids: notes.map((n: Note) => n.id),
          rows: notes.map((n: Note) => ({
              id: n.id, project_id: finalId, text: n.text, author: n.author, created_at: n.date, mentions: n.mentions
          }))
      };
      childSyncs.push(notizSync);

      const plans = report.metadata.plans || [];
      {
          let planRows: any[] = [];
          if (plans.length > 0) {
            // Upload bleibt in 2er-Chunks (Speicher/Netz), geschrieben wird gesammelt.
            const planChunkSize = 2;
            for (let i = 0; i < plans.length; i += planChunkSize) {
                const chunk = plans.slice(i, i + planChunkSize);
                const chunkResults = await Promise.all(chunk.map(async (p: ProjectPlan) => {
                    let imageUrl = p.image;
                    if (imageUrl.startsWith('data:image')) imageUrl = await uploadAsset(imageUrl, finalId!);
                    return {
                        dbRow: { id: p.id, project_id: finalId, name: p.name, image_url: imageUrl, section_id: p.sectionId },
                        urlPlan: { ...p, image: imageUrl }
                    };
                }));
                if (assetSink?.plans) assetSink.plans.push(...chunkResults.map(r => r.urlPlan));
                planRows.push(...chunkResults.map(r => r.dbRow));
            }
          }
          childSyncs.push({ table: 'project_plans', ids: plans.map((p: any) => p.id), rows: planRows });
      }

      // --- SPEICHERN DER UNTERLAGE (ufsa_rab_entries & ufsa_appendix_images) ---
      const rabEntries = report.unterlage?.rabEntries || [];
      childSyncs.push({
          table: 'ufsa_rab_entries',
          ids: rabEntries.map(e => e.id),
          rows: rabEntries.map((e, idx) => ({
              id: e.id,
              project_id: finalId,
              component: e.component || '',
              category: e.category || '',
              activity: e.activity || '',
              hazards: e.hazards || '',
              measures: e.measures || '',
              frequency: e.frequency || '',
              regulations: e.regulations || '',
              // Nr. 89: Die Unterlage hatte keine Reihenfolgespalte. Der Blob
              // traegt die Eintraege nicht mehr (oben auf [] gekuerzt), also
              // war die Sortierung nach dem Neuladen die Laune von Postgres —
              // die Pfeiltasten im Modul wirkten bis zum naechsten Oeffnen.
              sort_order: idx
          }))
      });

      const appendixImages = report.unterlage?.projectData?.appendixImages || [];
      {
          const imgRows = await Promise.all(appendixImages.map(async (img) => {
              let imgUrl = img.imageData;
              if (imgUrl && imgUrl.startsWith('data:image')) {
                  imgUrl = await uploadAsset(imgUrl, finalId!);
              }
              return {
                  id: img.id,
                  project_id: finalId,
                  image_url: imgUrl,
                  findings: img.findings || [],
                  suggestions: img.suggestions || []
              };
          }));
          childSyncs.push({ table: 'ufsa_appendix_images', ids: appendixImages.map(img => img.id), rows: imgRows });
      }

      // Geschuetzte Notizen (nach einer Bergung) gehen als ihre eigenen
      // DB-Zeilen unveraendert mit — dasselbe Muster wie bei den Leistungen.
      // Gelesen erst HIER, nach den Bild-Uploads: je kuerzer der Abstand zum
      // Schreiben, desto kleiner das Fenster, in dem eine fremde Aenderung
      // dieser Notiz mit ihrem eben gelesenen Stand ueberschrieben wird
      // (Codex P2, 21.10.2026). Ganz schliessen laesst es sich nur in der
      // Datenbank — fuer Positionen tut das Nr. 174.
      const geretteteNotizen = await fehlendeUmgezogeneRows(finalId, notes.map((n: Note) => n.id), 'project_notes');
      if (geretteteNotizen.length > 0) {
          notizSync.ids = [...notizSync.ids, ...geretteteNotizen.map((r: { id: string }) => r.id)];
          notizSync.rows = [...notizSync.rows, ...geretteteNotizen.map((r: Record<string, unknown>) => ({ ...r, project_id: finalId }))];
      }

      // UNVERAENDERTE TABELLEN BLEIBEN WEG — nur im sparsamen Modus und nur,
      // wenn dieser Tab nachweisen kann, dass der Server sie schon genau so
      // haelt. Die Regeln und ihre Grenzen stehen in childSaveDelta.ts. Eine
      // weggelassene Tabelle fasst die RPC nicht an (kein Upsert, kein
      // Prune) — dasselbe Ergebnis wie das erneute Senden desselben Arrays.
      const nutzerId = user.id;
      const darfWeglassen = finalId === kindLauf.projektId && darfKindTabellenWeglassen({
          uebertragung: saveOptions?.uebertragung,
          vorher: kindLauf.vorher,
          ueberlappt: kindLauf.ueberlappt,
          anker: kindAnker,
          ankerGetroffen,
          zaehlerVomServer,
          nutzerId,
      });
      // Gerettete Zeilen (Umzug, Bergung) muessen in die Behalte-Liste: deren
      // Tabelle geht dann immer mit und wird nicht gemerkt — Leistungen wie
      // die nach einer Bergung geschuetzten Notizen.
      const sonderfaelle = new Set<string>([
          ...(geretteteRows.length > 0 ? ['project_services'] : []),
          ...(geretteteNotizen.length > 0 ? ['project_notes'] : []),
      ]);
      const auswahl = waehleKindTabellen(childSyncs, kindLauf.vorher, darfWeglassen, sonderfaelle);
      if (auswahl.senden.length > 0) await syncChildrenTransactional(finalId, auswahl.senden);
      // Bestaetigt geschrieben: was gesendet wurde, ist ab jetzt die Basis
      // dieser Abstammung (setzen, dann entfernen in derselben Sitzung). Nur,
      // wenn die Leistungen wirklich gesendet wurden.
      if (auswahl.senden.some(s => s.table === 'project_services')) {
          for (const [schluessel, json] of definitionenGesendet) definitionZuletztGesendet.set(schluessel, json);
      }
      // Den Stand fuer das naechste Speichern vormerken — gesetzt wird er
      // erst, wenn dieses Speichern ganz durch ist (schliesseKindLauf).
      if (finalId === kindLauf.projektId) {
          kindLauf.neu = naechsterKindStand({
              vorher: kindLauf.vorher,
              anker: kindAnker,
              ankerGetroffen,
              zaehlerVomServer,
              nutzerId,
              version: serverResponse?.updated_at,
              serialisiert: auswahl.serialisiert,
              nichtMerken: sonderfaelle,
          });
      }

      if (serverResponse?.data?.metadata?.globalData) {
          serverResponse.data.metadata.globalData.services = report.metadata.globalData?.services || [];
          serverResponse.data.metadata.globalData.notes = report.metadata.globalData?.notes || [];
          // Die Antwort traegt die Spalten aus Nr. 71 mit (`select()` ohne
          // Liste) — daraus wird das Config zusammengesetzt, wie bei jedem
          // Laden. Der Name der Mutter kommt aus dem Speicher des Aufrufers:
          // der hat ihn beim Verknuepfen gesetzt oder beim Laden nachgeschlagen.
          applyLinkColumns(serverResponse, report.metadata.globalData?.linkedProjectConfig?.linkedProjectName);
      }
  }

  return serverResponse;
};

/**
 * Speichern mit dem Build-Riegel davor UND dahinter — beide Wege in EINER
 * Huelle, damit „veraltetes Bundle" an genau einer Stelle steht.
 *
 * DAVOR die Selbstauskunft `istBuildVeraltet()` (07.09.2026): der Anker
 * schuetzt vor einem veralteten STAND, nicht vor einem veralteten PROGRAMM —
 * ein alter Tab liest frisch, trifft den Anker und wendet dann alte Logik an.
 * Hat das Version-Gate ein neueres Deployment gesehen oder der Server den
 * Mindest-Build gemeldet, kommt hier nichts mehr durch; die Eingaben liegen
 * in der Warteschlange und gehen nach dem Reload mit dem neuen Build hoch.
 * Die Pruefung steht jetzt in der Huelle statt im Rumpf: ein Riegel, der
 * schon feststeht, soll nicht erst eine Sitzungsabfrage kosten.
 *
 * DAHINTER der Riegel auf dem Server (Trigger auf `projects` und zehn
 * Kindtabellen, Signalwort `BUILD_VERALTET`). Weist er ab, ist das kein
 * Fehler des Berichts, sondern ein Befund ueber dieses PROGRAMM: er wird
 * vermerkt (`markiereBuildVeraltet`), damit jeder weitere Schreibweg sofort
 * ohne Netzrunde aufgibt, und als derselbe Fehler weitergereicht, den die
 * Selbstauskunft wirft — eine Meldung, ein Verhalten. Der Originalfehler
 * bleibt als `cause` daran haengen, samt `status` und `hint`.
 *
 * Alles andere geht unveraendert durch.
 */
export const saveProjectToCloud = async (
  report: Report,
  projectId?: string,
  ownerId?: string,
  forceLastExportedNr?: number,
  assetSink?: { items?: any[]; plans?: any[] },
  saveOptions?: SaveAnchorOptions,
): Promise<any | null> => {
  // DER LISTENAUSZUG IST KEIN PROJEKT (Vorfall 21.10.2026, listenAuszug.ts):
  // vor allem anderen, auch vor dem Build-Riegel — ein Eintrag der
  // Projektliste traegt nur `metadata`, und `projects.data` wird komplett
  // geschrieben. Gespeichert loeschte er Unterlage, Vorankuendigung,
  // Einstellungen und jeden anderen Schluessel ausserhalb der Metadaten.
  verweigereListenAuszug(report, projectId);
  if (istBuildVeraltet()) throw buildVeraltetFehler();
  // Der Lauf fuer das Gedaechtnis der Kindtabellen (childSaveDelta.ts): der
  // Rumpf oeffnet ihn, sobald die Projekt-id feststeht, und entnimmt dabei
  // den gemerkten Stand. Gesetzt wird ein neuer NUR, wenn alles gelungen ist
  // — jeder Fehler laesst keinen Stand zurueck, das naechste Speichern
  // schickt dann alles.
  const kindLauf = neuerKindLauf();
  let gelungen = false;
  try {
    const ergebnis = await saveProjectToCloudIntern(report, projectId, ownerId, forceLastExportedNr, assetSink, saveOptions, kindLauf);
    gelungen = true;
    return ergebnis;
  } catch (e) {
    if (!isBuildGuardWriteError(e)) throw e;
    markiereBuildVeraltet();
    throw buildVeraltetFehler(e);
  } finally {
    schliesseKindLauf(kindLauf, gelungen);
  }
};

/** Verweigert RLS ein Update, kommt kein Fehler, nur null Zeilen — das ist kein „gespeichert“. */
const pruefeGeschrieben = (zeilen: unknown, was = 'Einstellungen'): void => {
    if (!Array.isArray(zeilen) || zeilen.length === 0) {
        throw new Error(`${was} nicht gespeichert: das eigene Profil ließ sich nicht schreiben.`);
    }
};

/**
 * Schreibt einzelne Schluessel in profiles.settings (des EIGENEN Profils),
 * ohne den Rest zu gefaehrden. Gibt den gespeicherten Stand zurueck.
 *
 * profiles.settings ist EIN JSON-Objekt fuer mehrere unabhaengige
 * Oberflaechen. Wer es aus einem im Speicher gehaltenen Stand zurueck-
 * schreibt, loescht alles, was seit dessen Laden woanders geschrieben wurde
 * — so gingen Briefkopf, Rechnungsstammdaten und die zentrale E-Mail-
 * Signatur verloren, sobald jemand parallel eine Unterschrift hinterlegte.
 * Deshalb fuehrt kein Weg mehr direkt auf update({ settings: … }).
 *
 * Ablauf: frisch lesen, flach mischen (mergeProfileSettings, samt Umzug des
 * Alt-Briefkopfs), daraus die Aenderung bilden (einstellungsAenderung) und
 * NUR sie der Datenbank geben — die setzt und entfernt in einer Anweisung
 * (Nr. 181). Selbst das frische Lesen liess vorher eine Luecke bis zum
 * Zurueckschreiben, in der ein zweiter Schreiber verloren ging. Fehlt die
 * Funktion noch, wird wie bisher das ganze gemischte Objekt geschrieben.
 *
 * `columns` nimmt Spalten AUSSERHALB des JSON mit (company_name,
 * app_mode, …). Mit Nr. 181 ist das ein zweiter Schreibvorgang nach den
 * Einstellungen; ohne sie bleibt es einer.
 */
export const patchProfileSettings = async (
    userId: string,
    patch: ProfileSettings,
    columns?: Record<string, any>,
): Promise<ProfileSettings> => {
    const { data, error: readError } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', userId)
        .single();
    if (readError) throw readError;

    const merged = mergeProfileSettings(data?.settings, patch);
    const { setzen, entfernen } = einstellungsAenderung(data?.settings, merged);
    // Die Funktion schreibt immer das Profil der SITZUNG. Hat inzwischen ein
    // anderes Konto im Tab angemeldet (verzoegertes Speichern laeuft noch),
    // ginge der Schluessel sonst in dessen Profil.
    const { data: sitzung } = await supabase.auth.getSession();
    const angemeldet = sitzung?.session?.user?.id;
    if (angemeldet && angemeldet !== userId) {
        throw new Error('Einstellungen nicht gespeichert: inzwischen ist ein anderes Konto angemeldet.');
    }
    const { data: gespeichert, error: rpcError } = await supabase.rpc('profil_einstellungen_aendern', {
        p_setzen: setzen,
        p_entfernen: entfernen,
    });

    if (rpcError) {
        if (!istFehlendeEinstellungsFunktion(rpcError)) throw rpcError;
        // Rueckfall bis Nr. 181 laeuft: das ganze Objekt, wie bisher.
        const { data: zeilen, error } = await supabase
            .from('profiles')
            .update({ ...(columns || {}), settings: merged })
            .eq('id', userId)
            .select('id');
        if (error) throw error;
        pruefeGeschrieben(zeilen);
        return merged;
    }

    if (columns && Object.keys(columns).length > 0) {
        const { data: zeilen, error } = await supabase
            .from('profiles')
            .update(columns)
            .eq('id', userId)
            .select('id');
        if (error) throw error;
        // Die Einstellungen stehen da schon — gescheitert sind nur die Spalten.
        pruefeGeschrieben(zeilen, 'Firmenangaben');
    }
    return gespeichert !== null && typeof gespeichert === 'object' && !Array.isArray(gespeichert)
        ? (gespeichert as ProfileSettings)
        : merged;
};

export const ensureGlobalRole = async (newRole: string) => {
    if (!newRole || !newRole.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    try {
        const { data: profile } = await supabase.from('profiles').select('settings').eq('id', user.id).single();
        if (profile) {
            const currentSettings = profile.settings || {};
            const currentRoles: string[] = currentSettings.customRoles || [];
            if (!currentRoles.some(r => r.toLowerCase() === newRole.trim().toLowerCase())) {
                // Nur `customRoles` aendern, nicht das ganze Objekt.
                await patchProfileSettings(user.id, { customRoles: [...currentRoles, newRole.trim()].sort() });
            }
        }
    } catch (e) { console.error("Error updating global roles:", e); }
};

export const syncRolesFromAllProjects = async (): Promise<string[]> => {
    // Seitenweise: 3465 Kontakte (PROD, Oktober 2026) — ohne `.range()` kamen
    // nur die Rollen der ersten 1000 an.
    const data = await ladeAlleSeiten<{ role: string | null }>((von, bis) =>
        supabase.from('project_contacts').select('id, role').order('id').range(von, bis));
    const roles = new Set<string>();
    data.forEach(c => { if (c.role) roles.add(c.role.trim()); });
    return Array.from(roles).sort();
};

export const updateProjectExportStatus = async (projectId: string, exportedNumber: number) => {
    const { error } = await supabase.from('projects').update({ last_exported_inspection_number: exportedNumber }).eq('id', projectId);
    if (error) throw error;
};

// Beide werfen bei einem Fehler: der Mitteilungs-Speicher (notificationStore)
// muss „Abruf gescheitert" von „keine Mitteilungen" unterscheiden koennen —
// sonst leerte ein Netzfehler die Glocke und eine gescheiterte Quittung
// bliebe als „gelesen" stehen.
export const fetchMyNotifications = async (userId: string) => {
    const { data, error } = await supabase.from('notifications').select('*').eq('recipient_id', userId).order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
};

export const markNotificationAsRead = async (notificationId: string) => {
    const { error } = await supabase.from('notifications').update({ status: 'read' }).eq('id', notificationId);
    if (error) throw error;
};

export const createNotificationsForMentions = async (recipients: string[], noteId: string, content: string, projectId: string, projectName: string, senderId: string, senderName: string) => {
    const payloads = recipients.map(rId => ({ project_id: projectId, project_name: projectName, sender_id: senderId, sender_name: senderName, recipient_id: rId, content: content, type: 'mention', status: 'unread', original_note_id: noteId }));
    await supabase.from('notifications').insert(payloads);
};

export const uploadPdf = async (blob: Blob, filename: string): Promise<string> => {
  // Zufallstoken im Namen: der Bucket ist public und das alte Namensschema
  // ({Datum}_{Projekt}_Bericht_Nr_{n}.pdf) war anonym erratbar/enumerierbar.
  const token = crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  const path = `reports/pdfs/${filename.replace(/\.pdf$/i, '')}_${token}.pdf`;
  
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { 
      contentType: 'application/pdf', 
      upsert: true 
    });

  if (error) throw error;

  const { data: publicUrlData } = supabase.storage
    .from(BUCKET)
    .getPublicUrl(path);

  return `${publicUrlData.publicUrl}`;
};

export const listProjectPdfs = async (searchTerms?: string[]) => {
    const mainSearch = (searchTerms && searchTerms.length > 0) ? searchTerms[0] : undefined;

    const { data, error } = await supabase.storage
        .from(BUCKET)
        .list('reports/pdfs', {
            limit: 1000,
            offset: 0,
            sortBy: { column: 'name', order: 'desc' },
            search: mainSearch
        });
    
    if (error) throw error;
    
    if (searchTerms && searchTerms.length > 1 && data) {
        const normalizedTerms = searchTerms
            .filter(Boolean)
            .map(t => t!.replace(/[^a-z0-9äöüß.-]/gi, '_').toLowerCase());
            
        return data.filter(file => {
            const fileName = file.name.toLowerCase();
            return normalizedTerms.some(term => fileName.includes(term));
        });
    }
    
    return data || [];
};

export const getReportDateFromHistory = async (projectId: string, inspectionNumber: number) => {
    const { data } = await supabase
        .from('report_history')
        .select('date')
        .eq('project_id', projectId)
        .eq('inspection_number', inspectionNumber)
        .maybeSingle();
    return data?.date;
};

export const getPdfPublicUrl = (filename: string) => {
    const { data } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(`reports/pdfs/${filename}`);
    return data.publicUrl;
};

/** Antwort von `auth.getSession()` — der Client ist als Proxy untypisiert exportiert. */
type SitzungsAntwort = { data: { session: Session | null }; error: AuthError | null };

/**
 * Wartet auf `arbeit` — oder scheitert beim Abbruch des Signals mit dessen
 * Grund. Der Abbruch-Horcher wird danach wieder entfernt.
 */
const mitAbbruch = async <T>(arbeit: Promise<T>, signal?: AbortSignal): Promise<T> => {
    if (!signal) return arbeit;
    const grundVon = () => signal.reason ?? new DOMException('Abgebrochen', 'AbortError');
    if (signal.aborted) throw grundVon();
    let horcher: (() => void) | undefined;
    const abbruch = new Promise<never>((_, reject) => {
        horcher = () => reject(grundVon());
        signal.addEventListener('abort', horcher, { once: true });
    });
    try {
        return await Promise.race([arbeit, abbruch]);
    } finally {
        if (horcher) signal.removeEventListener('abort', horcher);
    }
};

export const sendReportEmail = async (payload: { recipients: string[]; subject: string; message?: string; html?: string; pdfBase64?: string; pdfUrl?: string; filename: string; senderName?: string; replyTo?: string; icsBase64?: string; icsFilename?: string; }, signal?: AbortSignal) => {
    // Die API verifiziert das JWT und leitet senderName/replyTo server-seitig
    // daraus ab; die gleichnamigen Payload-Felder werden dort ignoriert.
    // Auch das Warten auf die Sitzung ist abbrechbar: erneuert getSession() ein
    // abgelaufenes Token ohne Netz, haengt es sonst vor dem fetch.
    const sitzung = await mitAbbruch<SitzungsAntwort>(supabase.auth.getSession(), signal);
    const { data: { session }, error: sitzungsFehler } = sitzung;
    if (!session?.access_token) {
        // Abgelaufenes Token ohne Netz: die Erneuerung scheitert — das ist
        // keine Abmeldung (netzFehler.ts).
        const fehler = fehlerOhneNutzer(sitzungsFehler, geraetOnline());
        throw istEigenerNetzFehler(fehler) ? fehler : new Error('Nicht angemeldet: E-Mail-Versand erfordert einen Login.');
    }

    const response = await fetch('/api/send-email', {
        method: 'POST',
        // „Warten beenden“ im Versanddialog bricht hier wirklich ab.
        signal,
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
         const err = await response.json().catch(() => ({}));
         throw new Error(err.error || 'Fehler beim Senden der E-Mail (SMTP)');
    }
    
    return await response.json();
};

/**
 * Wer hat gehandelt? Entweder ein Profil aus dem Zustand des Aufrufers oder —
 * beim Nachholen eines abgebrochenen Versands — der beim Versand festgehaltene
 * Name als bloßer String. Der Replay läuft u. U. unter einem anderen Login
 * (Baustellen-Tablet mit Kontowechsel); ohne den mitgeführten Namen stünde
 * dann der GERADE eingeloggte Nutzer im Archiv.
 */
export type SnapshotActor =
    | string
    | { id?: string | null; first_name?: string | null; last_name?: string | null }
    | null;

export const saveReportSnapshot = async (
    report: Report,
    explicitProjectId?: string,
    actor?: SnapshotActor,
    sender?: SnapshotActor,
) => {
    const meta = report.metadata;
    const projectId = explicitProjectId || meta.globalData?.projectId;
    if (!projectId || projectId === 'temp-id') return;

    // Invariante (2026-08-13 umgedreht): Im Archiv steht, WER TATSÄCHLICH
    // GEHANDELT hat — der eingeloggte Nutzer, der den Bericht schreibt und
    // speichert. Admin und feste Vertreter dürfen jedes Projekt bearbeiten;
    // bisher gewann pauschal meta.coordinator, also der Hauptverantwortliche
    // des Projekts. Damit stand in Archiv, Auswertung und Kundendokument ein
    // Name, der gar nicht vor Ort war (Auftrag Betreiber: "Da muss überall
    // immer der richtige Name stehen").
    //
    // Kette daher: handelnder Nutzer → conductedBy (falls dort je ein echter
    // Name gepflegt wird) → coordinator als letzter Notnagel, wenn KEIN
    // Sessionprofil ermittelbar ist (z. B. Replay ohne Login-Kontext).
    //
    // 'coordinator' ist dabei KEIN Name, sondern das Rollen-Literal, das
    // Projektanlage und CSV-Import in conductedBy schreiben; es zählt — wie
    // reiner Whitespace — als "nicht gesetzt".
    //
    // Kosten: der Profil-Lookup läuft jetzt im Regelfall, deshalb zwei
    // Maßnahmen. (1) Wer das Profil ohnehin im Zustand hält, reicht es als
    // `actor` durch (useReportActions) — dann kostet der Name gar nichts.
    // (2) Ohne actor bleibt es bei EINEM Roundtrip: getSession() liest die
    // lokale Session ohne Netzaufruf (getUser() würde /auth/v1/user abfragen),
    // die profiles-Abfrage ist die einzige echte Anfrage.
    const fullName = (p?: SnapshotActor | undefined): string => {
        if (!p) return '';
        // Dieselbe Bewertung wie ueberall sonst (services/actorName): 'System'
        // und 'coordinator' sind Rollenwoerter, 'System (Linked)' der Stub der
        // Tochter-Buchung, "Max undefined" ein Truemmerstueck aus einem halb
        // gefuellten Profil. Woertlich archiviert stuenden sie als
        // Durchfuehrende im Kundendokument — frueher fiel hier nur das nackte
        // 'System' durch.
        return typeof p === 'string' ? realActorName(p) : buildActorName(p);
    };

    let conductorName = fullName(actor);

    if (!conductorName) {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const userId = session?.user?.id;
            if (userId) {
                const { data: profile } = await supabase.from('profiles').select('first_name, last_name').eq('id', userId).single();
                conductorName = fullName(profile);
            }
        } catch (e) { console.warn("Conductor name fetch error", e); }
    }

    if (!conductorName) {
        const conductedBy = (meta.conductedBy || '').trim();
        conductorName = (conductedBy.toLowerCase() === 'coordinator' ? '' : conductedBy)
            || (meta.coordinator || '').trim();
    }

    const processedAssets = await processReportAssets(report, projectId, false);
    // Der Durchfuehrende steht auch IM Snapshot, nicht nur in der Spalte
    // conductor_name: das Archiv-PDF wird aus full_report_json erzeugt und
    // muss ihn ohne Blick auf die Zeile ausweisen (Vertretung / Urlaub).
    const processedReport: Report = conductorName
        ? { ...processedAssets, metadata: { ...processedAssets.metadata, conductedByName: conductorName } }
        : processedAssets;

    // report_history.date ist NOT NULL und eine echte date-Spalte. meta.date
    // wurde bisher roh durchgereicht: ein leerer Wert (Projekt vor der ersten
    // Begehung) kam als 22007 "invalid input syntax for type date" zurueck —
    // ein 400 aus Postgres, dem niemand ansieht, was fehlt. Der Aufrufer
    // entscheidet, ob es etwas zu archivieren gibt (isArchivableReport);
    // kommt es trotzdem hier an, soll die Meldung lesbar sein.
    const snapshotDate = ensureIsoDate(meta.date);
    if (!snapshotDate) {
        throw new Error('Der Bericht hat kein gültiges Begehungsdatum und kann nicht archiviert werden.');
    }

    // Der VERSENDER — und nur er — landet in `sent_by`. Das ist der Name, der
    // im Leistungsnachweis neben der Begehung steht. Gesetzt wird er
    // ausschliesslich vom Versandpfad (EmailModal, Pending-Send-Replay); jeder
    // andere Aufruf schickt null und aendert die Spalte damit nicht.
    //
    // Warum nicht `conductor_name` dafuer benutzen: das ist „wer hat
    // archiviert" und wird beim Anlegen eingefroren — archiviert wird ein
    // Bericht aber auch beim Anlegen der Folgebegehung, oft vom Innendienst.
    // Genau daraus entstand der falsche Name im Kundendokument (6172).
    const senderName = typeof sender === 'string' ? realActorName(sender) : buildActorName(sender);
    const senderId = sender && typeof sender === 'object'
        ? ((sender as { id?: string | null }).id || null)
        : null;

    const payload = {
        project_id: projectId,
        inspection_number: meta.inspectionNumber || 0,
        section_id: meta.sectionId || null,
        project_number: meta.projectNumber,
        project_name: meta.projectName,
        date: snapshotDate,
        inspection_time: meta.time || null,
        conductor_name: conductorName,
        sent_by: senderName || null,
        sent_by_id: senderName ? senderId : null,
        full_report_json: processedReport
    };

    // Ein einziger Schreibvorgang statt SELECT → UPDATE/INSERT: die RPC
    // save_report_snapshot (supabase/report_history_unique.sql) macht daraus ein
    // insert ... on conflict do update gegen den Unique-Index
    // (project_id, inspection_number, section_id).
    //
    // Das frühere Read-then-Write war race-anfällig — zwei Tabs, Doppelklick auf
    // "Senden" oder ein Pending-Send-Replay parallel zum Versand lasen beide
    // "kein Eintrag" und legten beide eine Zeile an (Vorfall 2026-08-07,
    // bereinigt mit supabase/fix_duplicate_report_archive.sql).
    //
    // conductor_name wird weiterhin mitgeschickt, aber SERVERSEITIG nur beim
    // Anlegen verwendet: der do-update-Zweig der RPC fasst die Spalte nicht an.
    // Archiviert bleibt damit, WER DEN BERICHT ANGELEGT hat — ein späterer
    // Erneut-Speicherer (Korrektur, Pending-Send-Replay) überschreibt ihn nicht.
    // Dieser Vertrag bleibt unverändert; nur die Ermittlung des Namens oben
    // wurde umgestellt.
    //
    // Kein Rückfallweg auf den alten Pfad (anders als bei
    // save_project_children_tx): der Fallback wäre genau das Read-then-Write,
    // dessen Race hier abgestellt werden soll. Das SQL muss deshalb VOR dem
    // Client-Deploy laufen — sonst PGRST202.
    //
    // Fehler werden nicht verschluckt: Aufrufer (EmailModal-Spinner,
    // Pending-Send-Replay) müssen wissen, ob das Archiv wirklich geschrieben ist.
    const { error } = await supabase.rpc('save_report_snapshot', {
        p_project_id: payload.project_id,
        p_inspection_number: payload.inspection_number,
        p_section_id: payload.section_id,
        p_project_number: payload.project_number,
        p_project_name: payload.project_name,
        p_date: payload.date,
        p_inspection_time: payload.inspection_time,
        p_conductor_name: payload.conductor_name,
        p_full_report_json: payload.full_report_json,
        // Nr. 67. Die Fassung ohne diese beiden Parameter bleibt in der
        // Datenbank bestehen, damit ein alter Bundle-Stand weiter versenden
        // kann; dieser Client ruft immer die neue.
        p_sent_by: payload.sent_by,
        p_sent_by_id: payload.sent_by_id,
    });
    if (error) throw error;
};

/**
 * Steckt irgendwo in `value` ein Inline-Bild, das der Umbau unten hochladen
 * wuerde? Dieselbe Erkennung wie dort — ein String, der mit `data:image`
 * beginnt, gesucht in Arrays und in den eigenen Schluesseln von Objekten —,
 * nur synchron und mit Abbruch beim ersten Treffer. Schluessel selbst zaehlen
 * nicht, genau wie im Umbau.
 *
 * Warum es die Vorpruefung gibt: der Umbau ist eine reine Kette aus `await`
 * ueber JEDEN Wert, auch wenn nichts umzubauen ist. Beim Berichtsarchiv (6051:
 * 110 Schnappschuesse mit je bis zu 640 Maengeln samt Foto-URLs) waren das
 * Millionen Mikrotasks hintereinander — der Hauptthread stand, der Bericht
 * fror ein, obwohl seit der Selbstheilung kein Base64 mehr drinsteht. Die
 * synchrone Suche kostet dagegen einen einzigen Durchlauf ohne Zuteilung.
 */
const enthaeltBase64Bild = (value: unknown): boolean => {
    if (typeof value === 'string') return value.startsWith('data:image');
    if (Array.isArray(value)) {
        for (const el of value) if (enthaeltBase64Bild(el)) return true;
        return false;
    }
    if (value && typeof value === 'object') {
        for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key) && enthaeltBase64Bild((value as Record<string, unknown>)[key])) return true;
        }
    }
    return false;
};

/**
 * Recursively walks any value and replaces inline base64 image strings with
 * uploaded storage URLs. Returns the converted value; sets stats.changed=true
 * if at least one image was uploaded. Non-image strings/values pass through.
 *
 * Ohne Inline-Bild kommt `value` SELBST zurueck (dieselbe Referenz, keine
 * Kopie) und `stats` bleibt unberuehrt — der Umbau haette ohnehin nichts
 * hochgeladen und `stats.changed` nie gesetzt. Beide Aufrufer schreiben nur
 * bei `stats.changed` zurueck. Ist ein Bild drin, laeuft der Umbau genau wie
 * bisher ueber den ganzen Wert.
 */
const convertBase64ImagesToUrls = async (value: unknown, folder: string, stats: { changed: boolean }): Promise<any> => {
    if (!enthaeltBase64Bild(value)) return value;
    return base64BilderHochladen(value, folder, stats);
};

const base64BilderHochladen = async (value: any, folder: string, stats: { changed: boolean }): Promise<any> => {
    if (typeof value === 'string') {
        if (value.startsWith('data:image')) {
            const url = await uploadAsset(value, folder);
            if (url !== value) stats.changed = true;
            return url;
        }
        return value;
    }
    if (Array.isArray(value)) {
        const out: any[] = [];
        for (const el of value) out.push(await base64BilderHochladen(el, folder, stats));
        return out;
    }
    if (value && typeof value === 'object') {
        const out: any = {};
        for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                out[key] = await base64BilderHochladen(value[key], folder, stats);
            }
        }
        return out;
    }
    return value;
};

/**
 * `skipMigration` schaltet die Selbstheilungs-Migration ab.
 *
 * Im Lesemodus (schreibgeschuetzt geoeffnetes Projekt) wird gelesen, wie es
 * dasteht: keine Uploads in den Speicherordner eines fremden Nutzers, kein
 * Nachschreiben am Archiv versendeter Dokumente. Base64-Bilder rendern
 * unveraendert weiter, nur ohne die einmalige Beschleunigung.
 */
export interface ReadMigrationOptions {
    /** true = keine Base64→URL-Migration, es wird ausschliesslich gelesen. */
    skipMigration?: boolean;
    /**
     * Nur `loadProjectById`: true = den Speicher-Anker NICHT setzen.
     *
     * Fuer Ansichten, aus denen nie gespeichert wird (Archiv „aktueller
     * Stand“). Ist dasselbe Projekt im Editor offen, verschoebe ein neuer
     * Anker dessen Ausgangsstand: ein spaeteres Speichern aus dem veralteten
     * Editor-Stand liefe ohne Konfliktpruefung durch (Muster 6185). Wer aus
     * dem geladenen Stand speichert, laesst die Option weg.
     */
    ohneAnker?: boolean;
}

export const fetchReportHistory = async (projectId: string, options?: ReadMigrationOptions): Promise<any[]> => {
    if (!projectId || projectId === 'temp-id') return [];
    const { data } = await supabase.from('report_history').select('*').eq('project_id', projectId).order('inspection_number', { ascending: false });
    const rows = data || [];

    // Self-healing migration: legacy snapshots store images inline as base64,
    // which makes opening the archive very slow. On first open we upload those
    // images to storage and persist the snapshot with URL references, so every
    // later open is fast. Failures are non-fatal — we still return usable data.
    // Snapshots that already use URLs are detected as unchanged and skipped.
    // Im Lesemodus faellt der ganze Block aus: die Rohdaten sind vollstaendig
    // anzeigbar, sie sind nur groesser.
    const rowsToMigrate: any[] = options?.skipMigration ? [] : rows;
    for (const row of rowsToMigrate) {
        if (!row.full_report_json) continue;
        try {
            const stats = { changed: false };
            const converted = await convertBase64ImagesToUrls(row.full_report_json, projectId, stats);
            if (stats.changed) {
                row.full_report_json = converted;
                await supabase.from('report_history').update({ full_report_json: converted }).eq('id', row.id);
            }
        } catch (e) {
            console.warn('Snapshot base64->URL migration failed for', row.id, e);
        }
    }

    return rows.map((row: any) => ({ id: row.id, projectNumber: row.project_number, projectName: row.project_name, inspectionNumber: row.inspection_number, sectionId: row.section_id, date: row.date, time: row.inspection_time ? row.inspection_time.substring(0, 5) : (row.full_report_json?.metadata?.time || '00:00'), conductedByName: row.conductor_name, sentByName: row.sent_by || undefined, reportData: row.full_report_json as Report }));
};

/**
 * Berichtsarchiv des Projekts UND seiner Tochterprojekte.
 *
 * Ein Mutterprojekt fuehrt im serviceLog nur den Buchungs-Stub der Toechter
 * ("System (Linked)"); wer den echten Koordinator will, braucht deren Archiv.
 * Eine Funktion fuer beide Aufrufer (Leistungsnachweis-Editor und
 * Rechnungsanlage), damit beide Pfade dieselben Namen aufloesen.
 *
 * Scheitert die Tochter-Suche, kommt wenigstens das eigene Archiv zurueck —
 * ein unvollstaendiger Nachweis ist besser als gar keiner.
 */
export const fetchReportHistoryWithChildren = async (projectId: string): Promise<any[]> => {
    // Jede Archivzeile traegt ihr PROJEKT mit. Der Leistungsnachweis ordnet
    // Buchungen mit `RPT-CHILD-<Tochter>-<Nr>` ueber genau dieses Paar dem
    // richtigen Archiv zu — der Projektname taugt dafuer nicht (er ist
    // Anzeige und aenderbar), die Berichtsnummer allein erst recht nicht (sie
    // ist nur innerhalb eines Projekts eindeutig).
    const mitProjekt = (rows: any[], id: string) => rows.map(r => ({ ...r, projectId: id }));
    const own = mitProjekt(await fetchReportHistory(projectId), projectId);
    try {
        // Ueber die Spalte aus Nr. 71, nicht mehr ueber `contains(data, …)`.
        const children = await fetchLinkedChildren(projectId);
        if (!children || children.length === 0) return own;
        const childHistories = await Promise.all(
            children.map(async (c: { id: string }) => mitProjekt(await fetchReportHistory(c.id), c.id)),
        );
        return [...own, ...childHistories.flat()];
    } catch (e) {
        console.warn('Tochterprojekt-Archive konnten nicht geladen werden:', e);
        return own;
    }
};

/**
 * Lightweight archive list: selects only the row columns plus FOUR fields of
 * the snapshot's `metadata` (NOT `items`/photos, NOT the rest of `metadata`).
 * This keeps opening the archive fast even for projects with many large reports.
 * The full report (with items) is loaded on demand via loadReportSnapshot().
 *
 * `reportData.metadata` traegt hier NUR:
 *   projectNumber            — HistoryView, Projektfilter (currentProjectNumber)
 *   sectionId                — HistoryView, Abschnittsfilter und Abschnittsname
 *   time                     — Rueckfall fuer `time`, wenn die Spalte leer ist
 *   globalData.sections      — HistoryView, Abschnittsname (id → name)
 * Bis 30.09.2026 kam der ganze `metadata`-Zweig mit — samt Planbildern,
 * Verteiler und `globalData.serviceLog` vom Versandtag, bei 6051 also 110-mal
 * das Buchungsjournal, nur um eine Liste zu zeichnen. Wer ein weiteres Feld
 * aus der Liste braucht, nimmt es HIER in die Auswahl auf; alles andere steht
 * im vollen Schnappschuss (loadReportSnapshot).
 *
 * `->` statt `->>`: der Wert kommt als JSON mit seinem Typ zurueck, eine als
 * Zahl gespeicherte Projektnummer bleibt eine Zahl — wie vorher im ganzen
 * `metadata`-Zweig. Fehlende Felder bleiben weg (undefined), wie vorher.
 *
 * `reportData` ist jetzt IMMER gesetzt. Vorher war es bei einem Schnappschuss
 * ohne `metadata` undefined, und HistoryView (getSectionName) brach beim
 * Zeichnen der ganzen Liste an `entry.reportData.metadata` ab.
 */
/** Eine Zeile der schlanken Archivliste (Spalten plus einzelne Metadaten-Pfade). */
interface ArchivListenZeile {
    id: string;
    project_number: string | null;
    project_name: string | null;
    inspection_number: number;
    section_id: string | null;
    date: string;
    inspection_time: string | null;
    conductor_name: string | null;
    sent_by: string | null;
    meta_project_number?: string | null;
    meta_section_id?: string | null;
    meta_time?: string | null;
    meta_sections?: unknown;
}

export const fetchReportHistoryList = async (projectId: string): Promise<any[]> => {
    if (!projectId || projectId === 'temp-id') return [];
    const { data } = await supabase
        .from('report_history')
        .select('id, project_number, project_name, inspection_number, section_id, date, inspection_time, conductor_name, sent_by, '
            + 'meta_project_number:full_report_json->metadata->projectNumber, '
            + 'meta_section_id:full_report_json->metadata->sectionId, '
            + 'meta_time:full_report_json->metadata->time, '
            + 'meta_sections:full_report_json->metadata->globalData->sections')
        .eq('project_id', projectId)
        .order('inspection_number', { ascending: false });
    return ((data || []) as ArchivListenZeile[]).map((row) => {
        const metadata: Record<string, unknown> = {};
        if (row.meta_project_number != null) metadata.projectNumber = row.meta_project_number;
        if (row.meta_section_id != null) metadata.sectionId = row.meta_section_id;
        if (row.meta_time != null) metadata.time = row.meta_time;
        if (row.meta_sections != null) metadata.globalData = { sections: row.meta_sections };
        return {
            id: row.id,
            projectNumber: row.project_number,
            projectName: row.project_name,
            inspectionNumber: row.inspection_number,
            sectionId: row.section_id,
            date: row.date,
            time: row.inspection_time ? row.inspection_time.substring(0, 5) : (row.meta_time || '00:00'),
            conductedByName: row.conductor_name,
            sentByName: row.sent_by || undefined,
            // Only these metadata fields are present here — enough for list
            // display, filtering and section names. Use loadReportSnapshot()
            // before generating a PDF.
            reportData: { metadata } as unknown as Report
        };
    });
};

/**
 * Nur die Koepfe des Berichtsarchivs: Kennung, Berichtsnummer, Abschnitt,
 * Datum — OHNE `full_report_json`.
 *
 * Fuer den PDF-Generator: er braucht aus dem Archiv einzig Nummer, Abschnitt
 * und Datum des VORBERICHTS (fuer „Erledigt-Fotos nach dem Folgebericht
 * ausblenden"). Dafuer lud er bisher jeden Schnappschuss komplett samt
 * Maengelbestand und schickte ihn durch die Base64-Selbstheilung.
 *
 * Absteigend nach Berichtsnummer, wie fetchReportHistory. Ein Fehler liefert
 * eine leere Liste: fuer die Aufrufer heisst fehlendes Archiv „kein
 * Vorbericht", und das PDF entsteht dann wie ohne die Option.
 */
export const fetchReportHistoryKoepfe = async (projectId: string): Promise<Array<{ id: string; inspectionNumber: number; sectionId: string | null; date: string }>> => {
    if (!projectId || projectId === 'temp-id') return [];
    try {
        const { data, error } = await supabase
            .from('report_history')
            .select('id, inspection_number, section_id, date')
            .eq('project_id', projectId)
            .order('inspection_number', { ascending: false });
        if (error) {
            console.error('Berichtsarchiv (Koepfe) konnte nicht geladen werden:', error);
            return [];
        }
        return ((data || []) as Array<{ id: string; inspection_number: number; section_id: string | null; date: string }>).map((row) => ({
            id: row.id,
            inspectionNumber: row.inspection_number,
            sectionId: row.section_id ?? null,
            date: row.date,
        }));
    } catch (e) {
        console.error('Berichtsarchiv (Koepfe) konnte nicht geladen werden:', e);
        return [];
    }
};

/**
 * Loads the full snapshot for a single archive entry. Legacy snapshots with
 * inline base64 images are converted to storage URLs on first load and persisted
 * back, so subsequent loads are fast. Non-fatal on conversion errors.
 *
 * `skipMigration` (Lesemodus) liefert den Snapshot unveraendert zurueck — das
 * Archiv versendeter Dokumente bleibt dann garantiert unberuehrt.
 */
export const loadReportSnapshot = async (entryId: string, projectId?: string, options?: ReadMigrationOptions): Promise<Report | null> => {
    const { data } = await supabase.from('report_history').select('id, project_id, full_report_json').eq('id', entryId).maybeSingle();
    if (!data || !data.full_report_json) return null;
    if (options?.skipMigration) return data.full_report_json as Report;
    const folder = projectId || data.project_id;
    try {
        const stats = { changed: false };
        const converted = await convertBase64ImagesToUrls(data.full_report_json, folder, stats);
        if (stats.changed) {
            await supabase.from('report_history').update({ full_report_json: converted }).eq('id', entryId);
        }
        return converted as Report;
    } catch (e) {
        console.warn('Snapshot base64->URL migration failed for', entryId, e);
        return data.full_report_json as Report;
    }
};

export const deleteReportHistory = async (entryId: string): Promise<boolean> => {
    if (!entryId || entryId === 'live-current') return false;
    const { error } = await supabase.from('report_history').delete().eq('id', entryId);
    if (error) {
        console.error("Error deleting report history entry:", error);
        return false;
    }
    return true;
};

export const loadSingleProjectOverview = async (projectId: string) => {
    try {
        // Derselbe Rueckfall wie in der Projektliste — diese Abfrage waehlt
        // dieselben Spalten und stirbt an derselben fehlenden Spalte. Ohne ihn
        // waere die Uebersicht nach dem Reparieren der Liste der zweite Ort,
        // an dem der Ausfall weiterlebt.
        const data = await mitSpaltenRueckfall(
            'cloudService.loadSingleProjectOverview (Einzeluebersicht)',
            async () => {
                const { data: zeile, error } = await supabase
                    .from('projects')
                    .select(projektUebersichtSelect(fehlendeSpalten()))
                    .eq('id', projectId)
                    .single();
                if (error) throw alsAbfrageFehler(error);
                return zeile as any;
            },
        );
        if (!data) return null;

        // Ersetzt per Realtime einen Eintrag der Projektliste — und ist wie
        // der ein AUSZUG: `data` kommt zwar ganz, aber ohne Positionen,
        // Notizen, Plaene, Beteiligte, Maengel und Checklisten aus den
        // Kindtabellen. Nie als Projekt speichern (Vorfall 21.10.2026,
        // listenAuszug.ts).
        markiereListenAuszug(data.data);

        // Apply transformations similar to loadProjectsFromCloud
        if (data.data && data.data.metadata) {
            if (data.data.metadata.intervalConfig) {
                 const iType = data.data.metadata.intervalConfig.type;
                 data.data.metadata.intervalConfig.type = iType === 'wöchentlich' ? 'weekly' : iType === 'zweiwöchentlich' ? 'biweekly' : iType === 'monatlich' ? 'monthly' : iType === 'benutzerdefiniert' ? 'custom' : iType;
                 // Der Folgetermin steht doppelt: Spalte und JSON-Zweig. Die
                 // Spalte ist die juengere Quelle — jeder Direkt-Write
                 // (Begehung anlegen, Nachbuchen, Verschieben) setzt sie, ohne
                 // den Blob anzufassen. loadProjectsFromCloud (:1161) und
                 // loadProjectById (:1336) gleichen deshalb ab; hier fehlte es,
                 // und nach einer Realtime-Aktualisierung gewann wieder der
                 // alte JSON-Wert — sichtbar in der Kachel "Nächste Termine",
                 // die den JSON-Zweig liest (ProjectStats.tsx:252).
                 data.data.metadata.intervalConfig.nextScheduledDate =
                     data.next_scheduled_date || data.data.metadata.intervalConfig.nextScheduledDate;
            }
        }

        // Die Verknuepfung aus den Spalten (Nr. 71) — die Spalte gewinnt ueber
        // das Blob-Feld, siehe services/projectLink.ts.
        const mutterId = applyLinkColumns(data);
        if (mutterId) {
            const namen = await fetchParentNames([mutterId]);
            setLinkedParentName(data, namen.get(mutterId));
        }

        return data as any;
    } catch (e) {
        console.error("loadSingleProjectOverview error", e);
        return null;
    }
};

// Beim Login stoßen getSession-Callback, onAuthStateChange und der View-Effect
// denselben Volllast-Load mehrfach gleichzeitig an. Gleichzeitige Aufrufer
// teilen sich einen laufenden Request; nach Abschluss wird wieder frisch geladen.
let projectsLoadInFlight: Promise<ListProject[]> | null = null;
// Wann der laufende (oder zuletzt gestartete) Voll-Load BEGONNEN hat. Der
// Aufrufer braucht diese Zeit zum Zusammenfuehren mit Realtime-Ereignissen:
// eine Anlage oder Loeschung, die NACH dem Beginn eintraf, kann in der
// Antwort fehlen bzw. noch drinstehen. Die Zeit haengt am geteilten Promise,
// nicht am einzelnen Aufrufer — wer sich einen laufenden Load teilt, bekommt
// dessen Beginn, nicht seinen eigenen (spaeteren) Aufrufzeitpunkt.
let projectsLoadStartedAt = 0;

// Bei Logout/Session-Wechsel aufrufen: ein noch laufender Load des vorherigen
// Users darf nicht vom nächsten User wiederverwendet werden.
export const invalidateProjectsLoadCache = () => { projectsLoadInFlight = null; };

export interface ProjectsLoad {
    /** Die Projekte, wie bisher — als Listenauszug (listProject.ts). */
    promise: Promise<ListProject[]>;
    /** Beginn des zugrunde liegenden Requests (Date.now()). */
    startedAt: number;
}

/** Wie loadProjectsFromCloud, liefert zusaetzlich den Beginn des Requests. */
export const loadProjectsFromCloudMitBeginn = (): ProjectsLoad => {
  if (!projectsLoadInFlight) {
      // Nur den eigenen Eintrag räumen: nach invalidateProjectsLoadCache kann
      // hier schon der Load der nächsten Session stehen.
      projectsLoadStartedAt = Date.now();
      const p: Promise<ListProject[]> = doLoadProjectsFromCloud().finally(() => {
          if (projectsLoadInFlight === p) projectsLoadInFlight = null;
      });
      projectsLoadInFlight = p;
  }
  return { promise: projectsLoadInFlight, startedAt: projectsLoadStartedAt };
};

export const loadProjectsFromCloud = (): Promise<ListProject[]> => loadProjectsFromCloudMitBeginn().promise;

const doLoadProjectsFromCloud = async (): Promise<ListProject[]> => {
  // Fetch in pages. Pulling every project with its nested project_services
  // in one response can exceed what an HTTP/3 (QUIC) connection
  // delivers reliably -> the request returns 200 but the body transfer aborts
  // with ERR_QUIC_PROTOCOL_ERROR / "Failed to fetch". Fetching in bundles of
  // PAGE_SIZE ids keeps each response small while the returned/mapped shape
  // stays identical.
  const PAGE_SIZE = 50;
  // Wie viele Buendel gleichzeitig unterwegs sind. Der Bestand liegt bei gut
  // 200 Projekten, also 4–5 Buendel — die Grenze greift heute gar nicht. Sie
  // steht trotzdem da, damit ein Bestand von 1.000 Projekten nicht 20
  // Abfragen mit eingebetteten Leistungspositionen auf einmal auf PostgREST
  // wirft; 6 ist die Zahl, die ein Browser je Host ohnehin nebeneinander
  // haelt. Umgesetzt als Bloecke von je 6 Buendeln, Block fuer Block — ein
  // echter Pool waere hier mehr Code fuer denselben Effekt.
  const MAX_PARALLEL = 6;
  let lastError;
  for (let attempt = 0; attempt < 3; attempt++) {
      try {
          // Zwei Wiederholungen, die nichts miteinander zu tun haben:
          //
          //   AUSSEN (diese Schleife) der QUIC-Abbruch. Die Antwort kommt mit
          //   200 und bricht mitten im Body ab; dieselbe Abfrage noch einmal
          //   zu schicken ist genau die richtige Antwort darauf.
          //
          //   INNEN `mitSpaltenRueckfall` die fehlende Spalte (42703). Dort
          //   hilft Wiederholen NIE — dieselbe Abfrage antwortet beliebig oft
          //   mit demselben Fehler. Wiederholt wird deshalb nicht dieselbe,
          //   sondern eine KLEINERE Abfrage, und nur, wenn die genannte Spalte
          //   entbehrlich ist. Bleibt sie es nicht, faellt der Fehler unten
          //   sofort durch: die aeussere Schleife bricht bei 42703 ab, statt
          //   zwei weitere Sekunden auf einen unveraenderlichen Fehler zu
          //   warten. Genau diese zwei Sekunden waren am 02.09.2026 die ganze
          //   Wirkung des Retrys.
          return await mitSpaltenRueckfall('cloudService.doLoadProjectsFromCloud (Projektliste)', async () => {
          // ─── Erst die Kennungen, dann die Zeilen — und die PARALLEL ──────
          // Bis zum 07.09.2026 lief eine Seite nach der anderen (`.range()`
          // zu je 50, ein `await` je Seite): bei N Seiten N volle Rundlaeufe
          // in Serie. Der erste Umbau zaehlte vorab (`count`) und holte die
          // Fenster `range(0,49)`, `range(50,99)`, … gleichzeitig. Das Review
          // desselben Tages fand die Luecke: Fenster nach OFFSET verschieben
          // sich. Wird zwischen zwei Anfragen eine Zeile geloescht, ruecken
          // alle dahinter um eins auf, das naechste Fenster beginnt bei der
          // frueheren 51., und die fruehere 50. faellt still heraus. Kein
          // Zaehler und keine Dublettenpruefung koennen das bemerken — es
          // fehlt etwas, statt dass etwas doppelt waere.
          //
          // Deshalb: EIN kleiner Rundlauf holt nur die Kennungen (ein paar
          // hundert uuids), die werden in Buendel zu je PAGE_SIZE geschnitten,
          // und jedes Buendel kommt ueber `.in('id', …)` — kein `.order`,
          // kein `.range`. Ein Buendel ist durch seine Kennungen bestimmt,
          // nicht durch eine Position; nichts kann sich verschieben. Eine
          // nach der Kennungsliste geloeschte Zeile kommt schlicht nicht
          // zurueck, alle anderen unveraendert. Eine NACH der Kennungsliste
          // angelegte Zeile ist in diesem Load nicht drin — sie kommt ueber
          // den Realtime-Kanal in App.tsx (INSERT auf `projects`) und
          // spaetestens mit dem naechsten vollen Load; genau wie vorher eine
          // Zeile, die hinter der zuletzt gelesenen Seite angelegt wurde.
          //
          // Die Kennungsliste selbst kommt per KEYSET, nicht per Offset:
          // `order('id').limit(ID_PAGE)`, die naechste Seite ab `.gt('id',
          // letzte)`. Heute ist das eine Seite; sollten es einmal mehr sein,
          // haette ein `.range()` ueber die Kennungen dieselbe Luecke wie
          // oben — eine Loeschung zwischen zwei Seiten liesse eine Kennung
          // ueber die Kante rutschen. Ab einer festen Kennung kann nichts
          // rutschen.
          const ID_PAGE = 1000;
          const ids: string[] = [];
          for (let letzte: string | null = null; ; ) {
              let abfrage = supabase
                .from('projects')
                .select('id')
                .order('id', { ascending: true })
                .limit(ID_PAGE);
              if (letzte !== null) abfrage = abfrage.gt('id', letzte);
              const { data: kennungen, error } = await abfrage;
              // Nur `id` — eine fehlende Spalte kann hier nicht auffallen,
              // aber der Fehlerpfad bleibt derselbe wie bei den Buendeln,
              // damit ein 401 oder ein Abbruch gleich aussieht, egal wo.
              if (error) throw alsAbfrageFehler(error);
              const seite = (kennungen || []).map((z: { id: string }) => z.id).filter(Boolean);
              ids.push(...seite);
              if (seite.length < ID_PAGE) break;
              letzte = seite[seite.length - 1];
          }

          const ladeBuendel = async (kennungen: string[]): Promise<any[]> => {
              const { data: zeilen, error } = await supabase
                .from('projects')
                .select(projektListeSelect(fehlendeSpalten()))
                .in('id', kennungen);

              // `alsAbfrageFehler` statt `new Error(error.message)`: der
              // frueher weggeworfene `code` ist die einzige Angabe, an der
              // 42703 von einem QUIC-Abbruch zu unterscheiden ist. Die
              // `message` bleibt zeichengleich. Wirft EIN Buendel 42703,
              // faellt `Promise.all` sofort mit genau diesem Fehler um, und
              // `mitSpaltenRueckfall` sieht ihn wie bisher.
              if (error) throw alsAbfrageFehler(error);
              return zeilen || [];
          };

          const buendel: string[][] = [];
          for (let i = 0; i < ids.length; i += PAGE_SIZE) buendel.push(ids.slice(i, i + PAGE_SIZE));
          const data: any[] = [];
          for (let i = 0; i < buendel.length; i += MAX_PARALLEL) {
              const antworten = await Promise.all(buendel.slice(i, i + MAX_PARALLEL).map(ladeBuendel));
              for (const zeilen of antworten) data.push(...zeilen);
          }

          // Dubletten nach id — erste Fundstelle gewinnt. Die Buendel sind
          // disjunkt, die Pruefung kostet nichts und bleibt als Riegel gegen
          // einen kuenftigen Umbau, der das wieder aendert.
          const uniqueProjects: any[] = [];
          const seenIds = new Set<string>();
          data.forEach(p => {
              if (p.id && !seenIds.has(p.id)) {
                  seenIds.add(p.id);
                  uniqueProjects.push(p);
              }
          });

          // Reihenfolge wie vor dem Umbau: juengste Aenderung zuerst. Die
          // Aufrufer sortieren zwar selbst (AdminProjectList) oder filtern
          // nur — aber die Liste hier war jahrelang so geordnet, und wer
          // stillschweigend darauf baut, soll es nicht erst im Betrieb
          // merken. Gleicher Zeitstempel: nach id, damit die Reihenfolge
          // zwischen zwei Ladevorgaengen nicht springt.
          const geaendertAm = (p: any): number => {
              const t = Date.parse(typeof p?.updated_at === 'string' ? p.updated_at : '');
              return Number.isNaN(t) ? 0 : t;
          };
          uniqueProjects.sort((a, b) => geaendertAm(b) - geaendertAm(a) || String(a.id).localeCompare(String(b.id)));

          const projekte = uniqueProjects.map(p => {
              // reconstruct p.data from p.metadata
              if (p.metadata) {
                  p.data = { metadata: p.metadata };
                  delete p.metadata;
              } else {
                  p.data = { metadata: { globalData: {} } };
              }
              // Ein AUSZUG, kein Projekt: `saveProjectToCloud` weist ihn ab
              // (Vorfall 21.10.2026, listenAuszug.ts). Wer aus der Liste
              // heraus aendert, laedt vorher mit `loadProjectById`.
              markiereListenAuszug(p.data);

              if (p.data && p.data.metadata) {
                  if (p.data.metadata.intervalConfig) {
                      p.data.metadata.intervalConfig.nextScheduledDate = p.next_scheduled_date || p.data.metadata.intervalConfig.nextScheduledDate;
                  }
                  p.data.metadata.projectCategory = p.category || p.data.metadata.projectCategory || '';
                  p.data.metadata.projectSubcategory = p.subcategory || p.data.metadata.projectSubcategory || '';
                  p.data.metadata.category = p.category || p.data.metadata.category || '';
                  p.data.metadata.subcategory = p.subcategory || p.data.metadata.subcategory || '';
              }

              // FIX 1+2: Map project_services WITH is_optional to globalData.services AND keep raw project_services
              if (p.project_services && p.project_services.length > 0) {
                  const mappedServices = p.project_services.map((dbService: any) => ({
                      id: dbService.id,
                      lvPosition: dbService.lv_position,
                      title: dbService.title,
                      unitPrice: dbService.unit_price,
                      quantityContracted: dbService.quantity_contracted,
                      quantityUsed: dbService.quantity_used,
                      quantityBilled: dbService.quantity_billed,
                      assignment: dbService.assignment || 'none',
                      unit: dbService.unit || 'Stk.',
                      is_optional: dbService.is_optional || false,
                      status: dbService.status,
                      category: dbService.category,
                      clientLvPosition: dbService.client_lv_position,
                      // MUSS mitgelesen werden: saveProjectToCloud schreibt
                      // min_hours immer mit (auch als NULL). Eine Ansicht, die
                      // das Feld nicht kennt, wuerde es beim naechsten Speichern
                      // bestandsweit leeren. (AdminGlobalSettings und
                      // ProjectOverview speicherten bis zum 21.10.2026 direkt
                      // aus dieser Liste; seitdem weist saveProjectToCloud
                      // einen Listeneintrag ab — listenAuszug.ts.)
                      // Fehlt die SPALTE (Rueckfall, siehe spaltenRueckfall.ts),
                      // steht hier ebenfalls undefined — dass daraus kein
                      // Leeren wird, haelt dort der Riegel am Schreibweg auf,
                      // nicht diese Zeile.
                      minHours: dbService.min_hours ?? undefined,
                      // Verzicht mitlesen — sonst schriebe ein Speichern
                      // aus dieser Ansicht keinen Schluessel und der
                      // Verzicht waere zwar nicht geloescht, aber im
                      // Client unsichtbar: die geschlossene Begehung
                      // erschiene bis zum naechsten Neuladen wieder als
                      // offen.
                      quantityWaived: dbService.quantity_waived ?? undefined,
                      waivedEntryIds: dbService.waived_entry_ids ?? undefined,
                      waivedReason: dbService.waived_reason ?? undefined,
                      waivedAt: dbService.waived_at ?? undefined,
                      waivedBy: dbService.waived_by ?? undefined,
                      // MONATSPAUSCHALE (Nr. 53): die Deckungsregel liegt
                      // relational in project_services.flat_rate. JEDER
                      // Ladepfad muss sie mitlesen (dieselbe Bedingung wie
                      // bei min_hours): eine Ansicht, die sie nicht kennt,
                      // saehe gedeckte Positionen als offenes Geld und boete
                      // sie erneut an — doppelt bezahlt.
                      flatRate: dbService.flat_rate ?? undefined,
                      // HERKUNFT (Nr. 68): woher die Position beim Verknuepfen
                      // kam. Reine Anzeige, aber aus demselben Grund wie
                      // min_hours in JEDEM Ladepfad — eine Ansicht, die das
                      // Feld nicht kennt, reicht es nicht weiter, und die
                      // Unterscheidbarkeit gleichnamiger Positionen an einer
                      // Mutter mit mehreren Toechtern waere wieder weg.
                      transferredFrom: dbService.transferred_from ?? undefined,
                      // PAKETPOSITION (Nr. 165/166): Definition und Gate —
                      // in JEDEM Ladepfad, sonst saehe diese Ansicht eine
                      // Paketposition als gewoehnliche, sofort abrechenbare
                      // Position. Fehlt die Spalte (Rueckfall), bleiben die
                      // Felder weg und das Speichern sendet nichts. Die
                      // Stunden (Nr. 171) kommen dort gleich mit.
                      ...bestandteilFelderAusZeile(dbService)
                  }));
                  if (!p.data) p.data = { metadata: { globalData: {} } };
                  if (!p.data.metadata) p.data.metadata = { globalData: {} };
                  if (!p.data.metadata.globalData) p.data.metadata.globalData = {};
                  p.data.metadata.globalData.services = mappedServices;
              }

              // Maengel werden hier NICHT mehr abgebildet — die Liste laedt
              // sie nicht mehr (queryColumns.ts, INSPECTION_ITEMS). Ein
              // Listenobjekt hat damit kein `data.items`; wer es speichert,
              // fasst die Maengel nicht an. Genau das war der Weg, auf dem
              // Bilder verschwanden: `photos: []` aus einer Projektion ohne
              // Bildspalten, und der Platzhalter-Riegel griff nicht.

              return p;
          });

          // Die Verknuepfung aus den Spalten (Nr. 71) — fuer die ganze Liste,
          // und die Namen der Muetter in EINEM Rundlauf statt je Tochter.
          const mutterIds: string[] = [];
          for (const p of projekte) {
              const mutterId = applyLinkColumns(p);
              if (mutterId) mutterIds.push(mutterId);
          }
          if (mutterIds.length > 0) {
              const namen = await fetchParentNames(mutterIds);
              for (const p of projekte) {
                  const mutterId = p.data?.metadata?.globalData?.linkedProjectConfig?.linkedProjectId;
                  if (mutterId) setLinkedParentName(p, namen.get(mutterId));
              }
          }
          return projekte;
          });
      } catch (e) {
          // Eine fehlende Spalte ist KEIN Wiederholungsfall. Kommt sie hier an,
          // hat der Rueckfall sie bereits geprueft und abgelehnt (nicht
          // entbehrlich, Meldung unlesbar, oder das Weglassen half nicht) —
          // dann soll es scheitern wie bisher, aber sofort.
          if (istUnbekannteSpalte(e)) throw e;
          console.warn(`Attempt ${attempt + 1} failed to load projects:`, e);
          lastError = e;
          if (attempt < 2) await new Promise(res => setTimeout(res, 1000));
      }
  }
  throw lastError;
};

/**
 * Die Eintraege der Unterlage (UfsA) in ihre Reihenfolge bringen — Nr. 89.
 *
 * Gelesen wird mit `select('*')`, damit eine Datenbank OHNE die Spalte
 * `sort_order` nicht mit 42703 abbricht (dieselbe Regel wie oben im Kopf von
 * `loadProjectById`). Sortiert wird deshalb HIER, im Client:
 *
 *  • aufsteigend nach `sort_order`;
 *  • Zeilen ohne Zahl (NULL, fehlende Spalte, Altbestand vor Nr. 89) hinten;
 *  • STABIL — bei gleichem Wert und unter den Zeilen ohne Wert bleibt die
 *    Reihenfolge der Datenbank erhalten. Damit ist der Zustand vor Nr. 89
 *    exakt der heutige (alle ohne Wert → nichts wird umgestellt), statt dass
 *    ein Sortierlauf die Liste zusaetzlich durcheinanderwirft.
 *
 * Rein und exportiert, damit die Regel ohne Supabase pruefbar ist.
 */
export const sortRabEntryRows = <T extends { sort_order?: number | null }>(rows: T[]): T[] => {
    const wert = (zeile: T): number | null => {
        const roh = zeile?.sort_order;
        return typeof roh === 'number' && Number.isFinite(roh) ? roh : null;
    };
    return rows
        .map((zeile, idx) => ({ zeile, idx }))
        .sort((a, b) => {
            const av = wert(a.zeile);
            const bv = wert(b.zeile);
            // Ohne Wert immer nach hinten, untereinander in DB-Reihenfolge.
            if (av === null && bv === null) return a.idx - b.idx;
            if (av === null) return 1;
            if (bv === null) return -1;
            if (av !== bv) return av - bv;
            return a.idx - b.idx;
        })
        .map(e => e.zeile);
};

/**
 * `skipMigration` (Lesemodus) laesst die Base64→URL-Selbstheilung aus: ein
 * schreibgeschuetzt geoeffnetes Projekt gehoert einem anderen Nutzer, dort wird
 * weder in dessen Speicherordner hochgeladen noch an dessen Zeilen geschrieben.
 * Die Bilder kommen dann als Base64 zurueck und werden genauso angezeigt.
 */
export const loadProjectById = async (id: string, onMigrationProgress?: (current: number, total: number) => void, options?: ReadMigrationOptions) => {
  // KEIN Spalten-Rueckfall, und das ist keine Luecke: dieser Pfad liest
  // `select('*')` — hier und bei den Kindtabellen unten. Ein `*` kann 42703
  // gar nicht ausloesen, PostgREST liefert schlicht, was da ist. Fehlt die
  // Spalte, kommt der Wert als undefined an; dass daraus beim Speichern kein
  // Leeren wird, haelt der Riegel am Schreibweg auf (spaltenRueckfall.ts).
  const { data: project, error } = await supabase.from('projects').select('*').eq('id', id).single();
  if (error || !project) return null;
  // Der Anker für alles, was aus diesem geladenen Stand heraus gespeichert
  // wird. Hier und nur hier entsteht er beim Öffnen — die Listenladewege
  // liefern bewusst keinen: aus einer Liste wird nicht geschrieben, und ein
  // Anker aus einer Liste wäre schon veraltet, sobald jemand das Projekt
  // öffnet. (Dass `loadProjectById` beim reinen LESEN Selbstheilungen
  // schreiben kann, ist ein bekannter Altbestand; die schreiben über andere
  // Wege und setzen ihren Anker dort selbst.) Reine Leseansichten sagen
  // `ohneAnker` und lassen den Anker eines offenen Editors unberührt.
  if (!options?.ohneAnker) rememberProjectVersion(id, (project as any).updated_at);
  // Kind-Tabellen parallel laden: acht sequenzielle Round-Trips machten das
  // Öffnen eines Projekts unnötig langsam; die Abfragen sind unabhängig.
  const [
      { data: items, error: itemsError },
      { data: checklists, error: checklistsError },
      { data: contacts, error: contactsError },
      { data: notes, error: notesError },
      { data: plans, error: plansError },
      { data: services, error: servicesError },
      { data: appendixImages, error: appendixError },
      { data: rabEntries, error: rabError },
  ] = await Promise.all([
      supabase.from('inspection_items').select('*').eq('project_id', id),
      supabase.from('project_checklists').select('*').eq('project_id', id),
      supabase.from('project_contacts').select('*').eq('project_id', id).order('sort_order', { ascending: true }),
      supabase.from('project_notes').select('*').eq('project_id', id).order('created_at', { ascending: false }),
      supabase.from('project_plans').select('*').eq('project_id', id),
      supabase.from('project_services').select('*').eq('project_id', id).order('lv_position', { ascending: true }),
      supabase.from('ufsa_appendix_images').select('*').eq('project_id', id),
      supabase.from('ufsa_rab_entries').select('*').eq('project_id', id),
  ]);

  // A failed child query must abort the load instead of degrading to an empty
  // list: the report would look loaded, and the next autosave would prune the
  // "missing" rows from the database (this wiped services + distribution list).
  const childError = itemsError || checklistsError || contactsError || notesError
      || plansError || servicesError || appendixError || rabError;
  if (childError) {
      console.error('loadProjectById: Kind-Abfrage fehlgeschlagen, Laden abgebrochen', childError);
      return null;
  }

  const mappedItems: InspectionItem[] = (items || []).map((dbItem: any) => ({ 
      id: dbItem.id,
      // NULL heisst „keine Nummer" (Information), nicht 0 und nicht null.
      ticketNumber: dbItem.ticket_number ?? undefined,
      title: dbItem.title, 
      location: dbItem.location, 
      observation: dbItem.description, 
      regulation: dbItem.regulation, 
      responsiblePerson: dbItem.responsible_person, 
      deadline: dbItem.deadline,
      immediateAction: !!dbItem.immediate_action,
      dateFound: dbItem.date_found, 
      status: dbItem.status as 'open' | 'closed' | 'info' | 'good' | 'general', 
      photos: dbItem.photos || [], 
      sectionId: dbItem.section_id, 
      closedDate: dbItem.closed_at,
      statusComment: dbItem.status_comment,
      closedImage: dbItem.closed_image,
      // Nr. 131: nur gesetzte Werte; NULL/fehlende Spalte → Feld bleibt weg.
      ...katalogFelderLesen(dbItem),
      // Nr. 130: Nadeln + Sensor-Verortung, normalisiert; NULL/fehlend → Feld bleibt weg.
      ...pinFelderLesen(dbItem)
  }));
  const mappedChecklists: ChecklistEntry[] = (checklists || []).map((dbCheck: any) => ({ id: dbCheck.id, text: dbCheck.text, category: dbCheck.category, status: dbCheck.status, comment: dbCheck.comment, lastChecked: dbCheck.last_checked, isRelevant: dbCheck.is_relevant, isCustom: dbCheck.is_custom, sectionId: dbCheck.section_id }));
  const mappedContacts: Contact[] = (contacts || []).map((dbContact: any) => ({ id: dbContact.id, name: dbContact.name, company: dbContact.company, role: dbContact.role, email: dbContact.email, phone: dbContact.phone, tradeType: dbContact.trade_type, present: dbContact.is_present, emailDistribution: dbContact.on_distribution_list, inAdvanceNotice: dbContact.in_advance_notice ?? true, sectionIds: Array.isArray(dbContact.section_ids) ? dbContact.section_ids : [], address: dbContact.address ?? undefined, sortOrder: dbContact.sort_order }));
  const mappedNotes: Note[] = (notes || []).map((dbNote: any) => ({ id: dbNote.id, text: dbNote.text, author: dbNote.author, date: dbNote.created_at, mentions: dbNote.mentions || [], readBy: [] }));
  const mappedPlans: ProjectPlan[] = (plans || []).map((dbPlan: any) => ({ id: dbPlan.id, name: dbPlan.name, image: dbPlan.image_url, sectionId: dbPlan.section_id }));
  // FIX: include is_optional in loadProjectById mapping
  const mappedServices: ServiceItem[] = (services || []).map((dbService: any) => ({ id: dbService.id, lvPosition: dbService.lv_position, title: dbService.title, unit: dbService.unit, unitPrice: dbService.unit_price, quantityContracted: dbService.quantity_contracted, quantityUsed: dbService.quantity_used, quantityBilled: dbService.quantity_billed, status: dbService.status, category: dbService.category, clientLvPosition: dbService.client_lv_position, assignment: dbService.assignment || 'none', is_optional: dbService.is_optional || false, minHours: dbService.min_hours ?? undefined, quantityWaived: dbService.quantity_waived ?? undefined, waivedEntryIds: dbService.waived_entry_ids ?? undefined, waivedReason: dbService.waived_reason ?? undefined, waivedAt: dbService.waived_at ?? undefined, waivedBy: dbService.waived_by ?? undefined, flatRate: dbService.flat_rate ?? undefined, transferredFrom: dbService.transferred_from ?? undefined,
      // Paketposition (Nr. 165/166): Definition und Gate, dazu die Stunden
      // (Nr. 171) — dieselbe Lesart wie in der Projektliste
      // (projectLv.bestandteilFelderAusZeile).
      ...bestandteilFelderAusZeile(dbService) }));
  const mappedAppendixImages: AppendixImage[] = (appendixImages || []).map((dbImg: any) => ({ id: dbImg.id, imageData: dbImg.image_url, findings: dbImg.findings || [], suggestions: dbImg.suggestions || [] }));
  const mappedRabEntries: RabEntry[] = sortRabEntryRows(rabEntries || []).map((e: any) => ({ id: e.id, component: e.component, category: e.category, activity: e.activity, hazards: e.hazards, measures: e.measures, frequency: e.frequency, regulations: e.regulations }));

  // Self-healing migration: legacy inspection items can still hold images inline
  // as base64, which makes opening (and re-sending) old projects slow. On open we
  // upload those to storage and persist URL references, so future loads are fast.
  // Only the two image columns are updated; failures are non-fatal. Progress is
  // reported via onMigrationProgress so the UI can show what is happening.
  try {
      const isB64 = (s: any) => typeof s === 'string' && s.startsWith('data:image');
      // Lesemodus: nichts zu migrieren — die Base64-Bilder bleiben, wie sie sind.
      const itemsToMigrate = options?.skipMigration ? [] : mappedItems.filter(it =>
          (it.photos || []).some(isB64) || isB64(it.closedImage)
      );
      const total = itemsToMigrate.length;
      if (total > 0) {
          let done = 0;
          onMigrationProgress?.(0, total);
          const chunkSize = 5;
          for (let i = 0; i < itemsToMigrate.length; i += chunkSize) {
              const chunk = itemsToMigrate.slice(i, i + chunkSize);
              await Promise.all(chunk.map(async (it) => {
                  const newPhotos = await Promise.all((it.photos || []).map(p => isB64(p) ? uploadAsset(p, id) : Promise.resolve(p)));
                  let newClosed = it.closedImage;
                  if (isB64(newClosed)) newClosed = await uploadAsset(newClosed as string, id);
                  it.photos = newPhotos;
                  it.closedImage = newClosed;
                  await supabase.from('inspection_items').update({ photos: newPhotos, closed_image: newClosed }).eq('id', it.id);
                  done++;
                  onMigrationProgress?.(done, total);
              }));
          }
      }
  } catch (e) {
      console.warn('Live item base64->URL migration failed', e);
  }

  if (project.data) {
      // Voll geladen ist nie ein Listenauszug — eine Kennung, die doch in der
      // Datenbank stuende, machte das Projekt sonst unspeicherbar
      // (Vorfall 21.10.2026, listenAuszug.ts).
      ohneListenAuszugKennung(project.data);
      if (project.data.metadata && project.data.metadata.intervalConfig) {
          project.data.metadata.intervalConfig.nextScheduledDate = project.next_scheduled_date || project.data.metadata.intervalConfig.nextScheduledDate;
      }
      
      project.data.items = mappedItems;
      project.data.checklists = mappedChecklists;
      if (project.data.metadata) {
          project.data.metadata.distributionList = mappedContacts;
          project.data.metadata.plans = mappedPlans;
          project.data.metadata.projectCategory = project.category || project.data.metadata.projectCategory || '';
          project.data.metadata.projectSubcategory = project.subcategory || project.data.metadata.projectSubcategory || '';
          project.data.metadata.category = project.category || project.data.metadata.category || '';
          project.data.metadata.subcategory = project.subcategory || project.data.metadata.subcategory || '';
          if (!project.data.metadata.globalData) project.data.metadata.globalData = {};
          project.data.metadata.globalData.projectId = project.id;
          project.data.metadata.globalData.notes = mappedNotes;
          project.data.metadata.globalData.services = mappedServices;
      }
      // Die Verknuepfung aus den Spalten (Nr. 71). `select('*')` liefert sie
      // mit; fehlen sie in dieser Datenbank, kommt `undefined` und
      // `applyLinkColumns` faellt auf das Blob-Feld zurueck. Steht die Spalte
      // dagegen auf NULL, gewinnt sie: das Blob-Feld wird entfernt.
      const mutterId = applyLinkColumns(project);
      if (mutterId) {
          const namen = await fetchParentNames([mutterId]);
          setLinkedParentName(project, namen.get(mutterId));
      }
      if (!project.data.unterlage) {
          project.data.unterlage = {};
      }
      if (!project.data.unterlage.projectData) {
          project.data.unterlage.projectData = { appendixImages: mappedAppendixImages };
      } else {
          project.data.unterlage.projectData.appendixImages = mappedAppendixImages;
      }
      project.data.unterlage.rabEntries = mappedRabEntries;
  }
  return project;
};

/** Snapshot-Mängel, die im aktuellen Bestand fehlen (Abgleich über die ID). */
export const computeMissingItems = (snapshotItems: InspectionItem[], currentItems: InspectionItem[]): InspectionItem[] => {
    const currentIds = new Set((currentItems || []).map(i => i.id).filter(Boolean));
    return (snapshotItems || []).filter(i => i.id && !currentIds.has(i.id));
};

/**
 * Restores inspection items from an archive snapshot: upsert only, never a
 * prune/delete — recovery must not be able to remove anything. Base64 photos
 * from old snapshots are uploaded to storage first.
 */
export const restoreInspectionItems = async (projectId: string, items: InspectionItem[]) => {
    if (!items.length) return;
    const dbRows = await Promise.all(items.map(async (item) => {
        const processedPhotos = await Promise.all(
            (item.photos || []).filter(p => p !== 'placeholder').map(p => uploadAsset(p, projectId))
        );
        let closedImage = item.closedImage;
        if (closedImage) closedImage = await uploadAsset(closedImage, projectId);
        return {
            id: item.id,
            project_id: projectId,
            // Explizit null statt undefined — sonst bleibt beim Update die alte
            // Nummer stehen (s. Begruendung im anderen Schreibweg oben).
            ticket_number: item.ticketNumber ?? null,
            title: item.title,
            description: item.observation,
            location: item.location,
            responsible_person: item.responsiblePerson,
            deadline: dateOrNull(item.deadline),
            immediate_action: !!item.immediateAction,
            date_found: dateOrNull(item.dateFound),
            status: item.status,
            photos: processedPhotos,
            regulation: item.regulation,
            section_id: item.sectionId,
            // Explizit null statt undefined — sonst bleibt beim Update das alte
            // Erledigungsdatum stehen (s. Begruendung im anderen Schreibweg oben).
            closed_at: dateOrNull(item.closedDate),
            status_comment: item.statusComment,
            closed_image: closedImage,
            ...katalogSpaltenSchreiben(item),
            // Nadeln (Nr. 130): immer alle drei Schluessel, null = keine Nadel.
            ...pinSpaltenSchreiben(item),
        };
    }));
    // Ein Bulk-Upsert schreibt die VEREINIGUNG aller Spalten: traegt nur eine
    // Zeile katalog_*, bekaemen die uebrigen dort NULL (Codex-Review 27.09.).
    // Deshalb je Satz vorhandener Katalogspalten ein eigener Aufruf. Die
    // Nadel-Schluessel stehen in JEDER Zeile — sie aendern die Gruppen nicht.
    const gruppen = new Map<string, typeof dbRows>();
    for (const zeile of dbRows) {
        const satz = KATALOG_SPALTEN.filter((k) => k in zeile).join(',');
        const gruppe = gruppen.get(satz);
        if (gruppe) gruppe.push(zeile); else gruppen.set(satz, [zeile]);
    }
    for (const gruppe of gruppen.values()) {
        const { error } = await supabase.from('inspection_items').upsert(gruppe);
        if (!error) continue;
        // Der Direkt-Upsert filtert Spalten NICHT wie save_project_children_tx:
        // fehlt Nr. 131 oder Nr. 130, scheitert er an katalog_* bzw. plan_id/
        // plan_coordinates/verortung (PGRST204/42703). Der Mangel ist wichtiger
        // als Katalog-Zuordnung und Nadeln — EINMAL ohne beide wiederholen.
        if (!istUnbekannteSpalteFehler(error) || !gruppe.some((z) => hatKatalogSpalten(z) || hatPinSpalten(z))) throw error;
        const { error: ohneKatalogFehler } = await supabase.from('inspection_items')
            .upsert(gruppe.map((z) => ohnePinSpalten(ohneKatalogSpalten(z))));
        if (ohneKatalogFehler) throw ohneKatalogFehler;
    }
};

/**
 * Es gibt hier bewusst KEIN `deleteProjectFromCloud` mehr.
 *
 * Bis 2026-08-26 stand an dieser Stelle ein `supabase.from('projects')
 * .delete()`, das ein Projekt nach einer einzigen Rueckfrage entfernte. Seit
 * supabase/project_deletion_approvals.sql (MIGRATIONS.md Nr. 49) liegt auf
 * `public.projects` ein RESTRICTIVE-Deckel plus BEFORE-DELETE-Trigger: der
 * Aufruf wuerde nichts loeschen, sondern scheitern.
 *
 * Der Weg fuehrt ueber src/services/projectDeletionService.ts —
 * beantragen (mit Begruendung), ein ZWEITER Administrator gibt frei,
 * ausfuehren. Wer hier eine Abkuerzung wieder einbaut, baut sie gegen die
 * Datenbank und bekommt sie nicht.
 */

export interface LinkTransferContext {
    /** Die Tochter, deren Positionen wandern. */
    childProjectId: string;
    /** Projektnummer der Tochter — wird als Herkunft auf die Position gestempelt. */
    childProjectNumber?: string;
    /** Projektname der Tochter — dito. */
    childProjectName?: string;
    /**
     * Das Leistungsprotokoll der Tochter, so wie der Aufrufer es im Speicher
     * hat. Bewusst uebergeben statt hier nachgeladen: der Aufrufer schreibt
     * gleich darauf denselben Stand ohne die gewanderten Zeilen zurueck, und
     * zwei verschiedene Staende waeren genau die Sorte Rennen, an der schon
     * einmal Buchungen verloren gegangen sind.
     */
    childServiceLog?: ServiceTransaction[] | null;
}

export interface LinkTransferResult {
    /** Was an der Tochter im Protokoll STEHEN BLEIBT — vom Aufrufer zu speichern. */
    remainingChildLog: ServiceTransaction[];
    /** Wie viele Protokollzeilen an die Mutter gegangen sind. */
    movedLogEntries: number;
}

/**
 * Haengt beim Verknuepfen die Leistungen der Tochter an die Mutter um.
 *
 * Warum ueberhaupt: eine Tochter rechnet ueber das LV der Mutter ab
 * (projectHubLogic.resolveEffectiveServices). Ein eigenes LV der Tochter waere
 * ab dem Verknuepfen unsichtbar, laege aber weiter in der Datenbank — Mengen,
 * die niemand mehr sieht und die trotzdem in Auswertungen zaehlen.
 *
 * Warum `update` und nicht neu anlegen: die `id` einer Leistung steht in den
 * Buchungen der Begehungsberichte und in Rechnungszeilen (lines[].serviceId).
 * Eine Kopie mit neuer id zerrisse beide Bezuege; der Leistungsnachweis einer
 * ausgestellten Rechnung fande seine Position nicht mehr.
 *
 * Fehlende Schreibrechte auf die Mutter (fremdes Projekt) liefern von
 * PostgREST KEINEN Fehler, sondern null betroffene Zeilen. Deshalb wird die
 * Anzahl geprueft statt nur `error`: sonst meldete der Dialog Erfolg, waehrend
 * die Leistungen unveraendert an der Tochter haengen blieben.
 *
 * ─── DAS PROTOKOLL WANDERT MIT ─────────────────────────────────────────────
 * Die Positionen liegen relational, ihre Protokolleintraege dagegen im
 * JSON-Blob des Projekts (`data.metadata.globalData.serviceLog`). Wurde nur
 * die Position umgehaengt, stand an der Mutter ein Leistungsstand ohne einen
 * einzigen belegenden Eintrag: der Projektcheck meldet das als
 * `unlogged_quantity` ueber die volle Menge, und der Leistungsnachweis einer
 * Rechnung findet fuer die Position keinen Anker. Welche Zeilen wandern und
 * mit welcher `serviceId` sie ankommen, rechnet `planServiceLogTransfer`
 * (projectLv.ts) — hier steht nur, was ohne Datenbank nicht zu haben ist.
 *
 * ─── DIE REIHENFOLGE IST DIE SICHERHEIT ────────────────────────────────────
 *   1. Positionen umhaengen (Geld zuerst — daran haengen die Belege).
 *   2. Protokollzeilen an der Mutter ANHAENGEN.
 *   3. Erst danach entfernt der AUFRUFER sie an der Tochter (er speichert das
 *      JSON der Tochter ohnehin, siehe `remainingChildLog`).
 * Bricht es zwischen 2 und 3 ab, steht eine Zeile DOPPELT — sichtbar und von
 * Hand zu bereinigen. Umgekehrt waere sie weg, und das faellt niemandem auf.
 *
 * ─── DAS BUCHUNGSJOURNAL ───────────────────────────────────────────────────
 * `public.service_journal` (SQL Nr. 59) fuehrt dieselben Zeilen relational und
 * traegt eine eigene `project_id`. Es kennt weder update noch delete — ein
 * Nachweis, aus dem sich Zeilen entfernen lassen, waere keiner. Der Umzug ist
 * dort deshalb ANHANG an der Mutter plus RUECKNAHME an der Tochter
 * (`service_journal_void`, Grund `correction`), nicht ein Umschreiben der
 * Spalte. Der Ablauf steht in `spiegleProjektwechsel` (linkTransferJournal.ts)
 * und wirft nie: das JSON ist zu diesem Zeitpunkt geschrieben, und ein Wurf
 * zeigte einen Fehler zu einem Vorgang, der gelungen ist. Fehlt die Migration,
 * passiert schlicht nichts.
 *
 * Ein Fallstrick dabei: `service_journal.id` ist projektuebergreifend eindeutig
 * und uebernimmt die `id` des JSON-Eintrags. Die gewanderte Zeile traegt
 * dieselbe id wie die Zeile der Tochter — ohne Vorkehrung verschluckte
 * `on conflict do nothing` sie still. Deshalb bekommt der Anhang die
 * gewanderten ids als BEREITS VERGEBEN mit (`zielPreviousLog`): dann vergibt
 * `journalIdDecision` einen inhaltsabgeleiteten Ersatzschluessel und legt die
 * urspruengliche id nach `legacy_entry_id`.
 *
 * Und ein zweiter: dieser Ersatzschluessel haengt am Inhalt samt Projekt. Nach
 * Verknuepfen → Trennen → Verknuepfen ist er derselbe wie beim ersten Mal und
 * laengst stillgelegt — der Anhang schreibt nichts. Stillgelegt wird an der
 * Tochter deshalb NUR, was an der Mutter danach nachweislich lebt, und zwar
 * ueber die JOURNAL-ids der Tochterzeilen, nicht ueber die JSON-id (die bei
 * zwei lebenden Zeilen nur eine traefe). Alles andere bleibt an der Tochter
 * lebend: falscher Ort, keine verlorene Menge.
 */
export const applyLinkTransfer = async (
    parentProjectId: string,
    plan: LinkTransferPlan,
    context?: LinkTransferContext,
): Promise<LinkTransferResult> => {
    const childLog = context?.childServiceLog ?? [];
    if (plan.total === 0) return { remainingChildLog: [...childLog], movedLogEntries: 0 };

    const moveIds = plan.moves.map(m => m.service.id);

    // ─── DIE PAUSCHALE WIRD FESTGESCHRIEBEN, BEVOR IRGENDETWAS GESCHRIEBEN WIRD
    // Die Rechnung dazu steht vollstaendig in `planFlatScopeFreeze`; hier steht
    // nur, was ohne Datenbank nicht zu haben ist. Aufgeloest wird gegen die
    // volle Liste der TOCHTER — die ist aus dem Plan vollstaendig zu haben:
    // `planLinkTransfer` steckt JEDE Position der Tochter entweder in `moves`
    // oder in `merges`. Die Reihenfolge moves-dann-merges aendert die Deckung
    // nicht: die Gewinner-Regel in `resolveFlatCoverage` haengt allein an der
    // Reihenfolge der PAUSCHALEN untereinander, und Pauschalen sind immer
    // Moves ('flat_rate') und behalten dort ihre relative Reihenfolge.
    const childServices = [...plan.moves.map(m => m.service), ...plan.merges.map(m => m.service)];
    const flatFreezes = planFlatScopeFreeze(childServices, moveIds);
    const verwaisteDeckung = flatFreezes.filter(f => f.leftBehind.length > 0);
    if (verwaisteDeckung.length > 0) {
        // Unerreichbar, solange `planLinkTransfer` gedeckte Positionen ueber
        // 'flat_covered' vom Merge fernhaelt (der Nachweis steht dort). Der
        // Riegel steht trotzdem: eine gedeckte Position, die nicht mitwandert,
        // waere an der Mutter wieder offenes Geld — und das faellt erst bei
        // einer doppelt gestellten Rechnung auf. Geprueft wird VOR dem ersten
        // Schreiben, damit "es wurde nichts uebertragen" auch stimmt.
        const namen = verwaisteDeckung
            .map(f => f.service.lvPosition || f.service.title)
            .join(', ');
        throw new Error(
            `Die Monatspauschale ${namen} deckt Positionen, die nicht mitwandern. Der Umzug wuerde ihre `
            + 'Deckung verlieren und die Mengen ein zweites Mal zur Abrechnung stellen. Es wurde nichts '
            + 'uebertragen — bitte die Pauschale vor dem Verknuepfen klaeren.',
        );
    }
    const flatFreezeById = new Map(flatFreezes.map(f => [f.service.id, f.flatRate]));

    // ─── PAKETPOSITIONEN WERDEN NIE ZUSAMMENGEFUEHRT (Nr. 165/166) ──────────
    // Ein MOVE ist unkritisch: dieselbe Zeile bekommt nur ein anderes
    // `project_id`, die Definition (`bestandteile`) steht an ihr und wandert
    // mit, und die Eintraege in `position_bestandteil_eintraege` ziehen ueber
    // den zusammengesetzten Fremdschluessel (`on update cascade`) selbst nach.
    // Ein MERGE dagegen LOESCHT die Zeile der Tochter — die Kaskade nimmt alle
    // Eintraege mit (Datum, Uhrzeit, Person: der ganze Nachweis), und die Menge
    // landet in einer Zeile der Mutter, deren Definition nichts davon weiss.
    // Umgekehrt haette eine Paketposition der Mutter nach dem Aufaddieren
    // Menge 2 — Paketpositionen gibt es nur mit Menge 1 (Entscheid E5), das
    // Gate deckelt auf `Vertragsmenge − abgerechnet` und forderte die zweite
    // Menge mit dem ersten Paket frei.
    //
    // Dieselbe Entscheidung wie bei der verwaisten Deckung oben: geprueft wird
    // VOR dem ersten Schreiben, und es wird abgebrochen statt umgeplant — der
    // Plan ist das, was der Anwender im Dialog bestaetigt hat. Eigentlich
    // gehoert die Regel in `planLinkTransfer` (als Grund wie 'flat_rate',
    // dann wandert die Position als eigene Zeile); bis dahin ist das hier
    // der Riegel.
    const paketMerges = plan.merges.filter(m => hatBestandteile(m.service) || hatBestandteile(m.into));
    if (paketMerges.length > 0) {
        const namen = paketMerges
            .map(m => m.service.lvPosition || m.service.title)
            .join(', ');
        throw new Error(
            `Die Position ${namen} ist eine Paketposition mit Bestandteilen (an der Tochter oder an der `
            + 'Mutter) und darf nicht mit einer gleichnamigen Position zusammengefuehrt werden — die '
            + 'erledigten Bestandteile gingen verloren. Es wurde nichts uebertragen. Bitte die LV-Nummer '
            + 'der Position vor dem Verknuepfen eindeutig machen.',
        );
    }

    // Zusammenfuehrungen zuerst, und darin die Mutter VOR der Tochter: bricht
    // das Loeschen ab, steht die Menge doppelt, aber die Zeile der Tochter ist
    // noch da und die Meldung nennt sie. Umgekehrt waere die Menge verloren
    // und die Zeile verschwunden.
    //
    // ─── WARUM DIE MENGE MIT VORBEDINGUNG GESCHRIEBEN WIRD ──────────────────
    // Geschrieben wird ein ABSOLUTER Wert (`merge.quantityContracted`), der zum
    // Planungszeitpunkt errechnet wurde. Verknuepfen ZWEI SCHWESTERN
    // gleichzeitig in dieselbe Zeile der Mutter, rechnen beide Plaene mit
    // demselben Ausgangswert, und der zweite Schreiber ueberschreibt den
    // Zuwachs des ersten — die Menge der einen ist lautlos weg, und auffallen
    // wuerde es erst an einer zu kleinen Rechnung.
    //
    // Ein `update ... set x = x + n` gibt es ueber PostgREST nicht. Von den
    // Mitteln, die dieses Projekt sonst nutzt, faellt die RPC aus: sie
    // brauchte eine Migration, und bis die gefahren ist, faellt der Client
    // (PGRST202) auf genau das unsichere absolute Schreiben zurueck — der
    // Fehler waere dann noch da, aber niemand rechnete mehr damit. Es bleibt
    // das OPTIMISTISCHE SCHREIBEN MIT VORBEDINGUNG, dasselbe Muster, mit dem
    // `saveProjectToCloud` seinen Versionsanker prueft (`.eq('updated_at',…)`).
    //
    // Und es wird ABGEBROCHEN statt neu addiert: der Plan ist zugleich das,
    // was der Anwender im Dialog bestaetigt hat. Auf einen inzwischen
    // veraenderten Ausgangswert stillschweigend aufzuaddieren hiesse, eine
    // andere Zahl zu schreiben als die bestaetigte. Der Dialog rechnet den
    // Plan unmittelbar vor dem Bestaetigen neu — ein zweiter Anlauf kostet
    // also einen Klick.
    //
    // `erwarteteMenge` fuehrt den Stand je Zielzeile mit: fuehrt die TOCHTER
    // zwei Zeilen derselben LV-Nummer, addiert `planLinkTransfer` beide auf
    // dieselbe Mutterzeile, und fuer die zweite ist die Vorbedingung das
    // Ergebnis der ersten, nicht mehr der geladene Ausgangswert.
    const erwarteteMenge = new Map<string, number>();
    for (const merge of plan.merges) {
        const erwartet = erwarteteMenge.get(merge.into.id) ?? (merge.into.quantityContracted || 0);
        const bezeichnung = merge.into.lvPosition || merge.into.title;
        const { data, error } = await supabase
            .from('project_services')
            .update({ quantity_contracted: merge.quantityContracted })
            .eq('id', merge.into.id)
            .eq('quantity_contracted', erwartet)
            .select('id');
        if (error) throw error;
        if (!data || data.length === 0) {
            // Null Zeilen heisst hier ZWEIERLEI: fehlende Schreibrechte (dann
            // steht die Menge unveraendert da) oder eine inzwischen veraenderte
            // Menge (dann hat eine Schwester gerade dieselbe Zeile gefuellt).
            // Die beiden Faelle brauchen verschiedene Antworten des Anwenders,
            // also wird nachgesehen statt geraten.
            const { data: stand } = await supabase
                .from('project_services')
                .select('quantity_contracted')
                .eq('id', merge.into.id)
                .maybeSingle();
            const jetzt = stand ? Number((stand as any).quantity_contracted) || 0 : null;
            if (jetzt !== null && jetzt !== erwartet) {
                throw new Error(
                    `Die Menge der Position ${bezeichnung} des Mutterprojekts hat sich waehrend des Vorgangs `
                    + `geaendert (erwartet ${erwartet}, jetzt ${jetzt}) — vermutlich verknuepft gerade ein `
                    + 'zweites Tochterprojekt dieselbe Position. Es wurde nichts uebertragen. Bitte den '
                    + 'Dialog schliessen und erneut verknuepfen, damit der Plan neu gerechnet wird.',
                );
            }
            throw new Error(
                `Die Position ${bezeichnung} des Mutterprojekts konnte nicht `
                + 'geaendert werden — fehlende Berechtigung auf das Mutterprojekt. Es wurde nichts uebertragen.',
            );
        }
        erwarteteMenge.set(merge.into.id, merge.quantityContracted);
    }

    const mergedChildIds = plan.merges.map(m => m.service.id);
    if (mergedChildIds.length > 0) {
        // Die Meldung nennt die Position beim Namen — der Kopfkommentar dieser
        // Funktion verspricht es, und ein roher PostgREST-Fehler haelt es
        // nicht. Und geprueft wird die ZEILENZAHL: fehlt das Loeschrecht auf
        // die Tochter, meldet PostgREST keinen Fehler, sondern null Zeilen.
        // Die Menge steht dann DOPPELT (an der Mutter aufaddiert, an der
        // Tochter unveraendert stehengeblieben) — genau der Zustand, den die
        // Reihenfolge oben bewusst in Kauf nimmt, weil er sichtbar ist. Er darf
        // aber nicht unbemerkt bleiben.
        const namen = plan.merges
            .map(m => m.service.lvPosition || m.service.title)
            .join(', ');
        const { data, error } = await supabase
            .from('project_services')
            .delete()
            .in('id', mergedChildIds)
            .select('id');
        if (error) {
            throw new Error(
                `Die Menge der Position(en) ${namen} ist an der Mutter bereits aufgenommen, die Zeile(n) der `
                + `Tochter liessen sich aber nicht entfernen: ${error.message || error}. Die Menge steht jetzt `
                + 'DOPPELT — sichtbar und von Hand zu bereinigen. Bitte den Stand pruefen, bevor erneut '
                + 'verknuepft wird.',
            );
        }
        if (!data || data.length !== mergedChildIds.length) {
            throw new Error(
                `Es konnten nur ${data?.length ?? 0} von ${mergedChildIds.length} zusammengefuehrten Zeilen `
                + `der Tochter entfernt werden (${namen}) — fehlende Berechtigung. Die Menge der uebrigen `
                + 'steht jetzt DOPPELT: an der Mutter aufgenommen und an der Tochter stehengeblieben. Bitte '
                + 'den Stand pruefen, bevor erneut verknuepft wird.',
            );
        }
    }

    if (moveIds.length > 0) {
        // Die HERKUNFT wird im selben `update` gesetzt, das `project_id`
        // umhaengt: zwei Anweisungen koennten auseinanderfallen, und eine
        // Position ohne Herkunft ist an einer Mutter mit mehreren Toechtern
        // von ihren gleichnamigen Schwestern nicht mehr zu unterscheiden.
        // Ohne Kontext (Altaufrufer) wird nichts gestempelt — ein erfundener
        // Ursprung waere schlechter als keiner.
        const origin: ServiceTransferOrigin | null = context?.childProjectId
            ? {
                  projectId: context.childProjectId,
                  projectNumber: context.childProjectNumber,
                  projectName: context.childProjectName,
                  at: new Date().toISOString(),
              }
            : null;
        const rumpf = {
            project_id: parentProjectId,
            ...(origin ? { transferred_from: origin } : {}),
        };
        let umgehaengt = 0;

        // Die Positionen OHNE Pauschale in einem Zug. `flat_rate` steht hier
        // ausdruecklich NICHT drin: der Schluessel wuerde die Spalte auf allen
        // Zeilen des Zugs setzen und damit jede fremde Pauschale leeren —
        // dieselbe Fehlerklasse wie beim Verzicht in `saveProjectToCloud`.
        const ohnePauschale = moveIds.filter(id => !flatFreezeById.has(id));
        if (ohnePauschale.length > 0) {
            const { data, error } = await supabase
                .from('project_services')
                .update(rumpf)
                .in('id', ohnePauschale)
                .select('id');
            if (error) throw error;
            umgehaengt += data?.length ?? 0;
        }

        // Und je Pauschale ein eigenes `update` — die festgeschriebene Deckung
        // geht im SELBEN Schreibvorgang mit, der die Position umhaengt. Zwei
        // Anweisungen koennten auseinanderfallen, und eine Pauschale, die an
        // der Mutter angekommen ist und ihren scope noch nicht eingefroren
        // hat, deckt vom ersten Laden an die Positionen der Mutter mit.
        //
        // Fehlt die Spalte `flat_rate` (Nr. 53 nicht gefahren), scheitert das
        // hier laut — richtig so, und dieselbe Entscheidung wie bei
        // `transferred_from`: ohne die Spalte laesst sich die Deckung nicht
        // festschreiben, und ein Umzug mit streuender Pauschale ist genau der
        // Zustand, gegen den das hier gebaut ist. Der Spalten-Rueckfall
        // (spaltenRueckfall.ts) greift bewusst nicht — er ist rein lesend.
        for (const freeze of flatFreezes) {
            const { data, error } = await supabase
                .from('project_services')
                .update({ ...rumpf, flat_rate: freeze.flatRate })
                .eq('id', freeze.service.id)
                .select('id');
            if (error) throw error;
            umgehaengt += data?.length ?? 0;
        }

        if (umgehaengt !== moveIds.length) {
            throw new Error(
                `Es konnten nur ${umgehaengt} von ${moveIds.length} Leistungen uebertragen werden — `
                + 'fehlende Berechtigung. Bitte den Stand pruefen, bevor erneut verknuepft wird.',
            );
        }

        // Der Aufrufer speichert gleich die TOCHTER, `saveProjectToCloud`
        // gleicht `project_services` dabei ueber eine Behalte-Liste ab. Die
        // umgezogenen Zeilen gehoeren jetzt der MUTTER; taeuchten sie in einer
        // Behalte-Liste der Tochter wieder auf, holte der Prune sie nicht
        // zurueck (er loescht nur), aber die Mutter bekommt hier ihren
        // Vermerk — ihr naechstes Speichern darf sie nicht wegprunen, falls es
        // aus einem Stand kommt, der sie noch nicht kennt.
        merkeUmgezogeneLeistungen(parentProjectId, moveIds);
    }

    // Die Zeilen wandern in der KIND-FORM an die Mutter (projectLv.inKindForm):
    // dort sind es Tochter-Begehungen und muessen als solche lesbar sein. Ohne
    // Tochter-Kennung gibt es keine Herkunft zu vermerken — dann wandern sie
    // wie bisher unveraendert.
    const logTransfer = planServiceLogTransfer(
        plan,
        childLog,
        context?.childProjectId
            ? {
                  projectId: context.childProjectId,
                  projectName: context.childProjectName,
                  projectNumber: context.childProjectNumber,
                  at: new Date().toISOString(),
              }
            : null,
    );
    if (logTransfer.moving.length === 0) {
        return { remainingChildLog: logTransfer.staying, movedLogEntries: 0 };
    }

    // Frisch laden statt aus einem Listenabzug schreiben: das Protokoll der
    // Mutter waechst durch jede Buchung, und ein veralteter Stand schriebe
    // fremde Zeilen zurueck oder liesse inzwischen entstandene fallen.
    // Geladen wird NACH dem Umhaengen — dann tragen die Positionen der Mutter
    // die gewanderten Zeilen bereits, und das folgende Speichern schreibt sie
    // unveraendert zurueck statt sie als verwaist zu behandeln.
    const freshParent = await loadProjectById(parentProjectId);
    if (!freshParent || !freshParent.data) {
        throw new Error(
            'Die Leistungen sind uebertragen, aber das Mutterprojekt ließ sich nicht laden — das '
            + 'Leistungsprotokoll wurde NICHT mitgenommen. Bitte den Stand pruefen, bevor erneut verknuepft wird.',
        );
    }
    const parentMeta = freshParent.data.metadata as ReportMetadata;
    const parentLogBefore: ServiceTransaction[] = parentMeta?.globalData?.serviceLog || [];
    try {
        await saveProjectToCloud(
            {
                ...freshParent.data,
                metadata: {
                    ...parentMeta,
                    globalData: {
                        ...parentMeta.globalData,
                        serviceLog: [...logTransfer.moving, ...parentLogBefore],
                    },
                },
            },
            parentProjectId,
        );
    } catch (e: any) {
        throw new Error(
            'Die Leistungen sind uebertragen, das Leistungsprotokoll dazu aber NICHT: '
            + `${e?.message || e}. Bitte den Stand pruefen, bevor erneut verknuepft wird.`,
        );
    }

    // Zweitschrift und Ruecknahme im Journal — nach dem BESTAETIGTEN Speichern
    // und ohne Wurf (siehe Kopfkommentar): anhaengen an der Mutter, die Mutter
    // frisch lesen, und an der Tochter nur stilllegen, was an der Mutter lebt.
    // `zielPreviousLog` enthaelt ausdruecklich AUCH die gewanderten Zeilen:
    // ihre ids sind als Journalschluessel bereits an der Tochter vergeben.
    // Ohne Tochter-Kennung (Altaufrufer) wird wie bisher nur angehaengt.
    await spiegleProjektwechsel({
        quelleProjectId: context?.childProjectId ?? null,
        zielProjectId: parentProjectId,
        entries: logTransfer.moving,
        zielPreviousLog: [...parentLogBefore, ...logTransfer.moving],
        quelleVerbleibend: logTransfer.staying,
        linkedProjectConfig: parentMeta?.globalData?.linkedProjectConfig ?? null,
        source: 'link_transfer',
    });

    return { remainingChildLog: logTransfer.staying, movedLogEntries: logTransfer.moving.length };
};

export interface LinkReturnContext {
    /** Die Tochter, an die zurueckgeht — Ziel des Umzugs. */
    childProjectId: string;
    /** Die Mutter, bei der die Positionen bis jetzt liegen. */
    parentProjectId: string;
    /**
     * Der Bericht der Tochter, so wie der Aufrufer ihn im Speicher hat.
     *
     * Bewusst uebergeben und nicht hier nachgeladen: die Tochter ist das
     * GEOEFFNETE Projekt. Ein frisch geladener Serverstand waere aelter als
     * das, was der Anwender vor sich hat, und ihn zurueckzuschreiben hiesse,
     * seine ungespeicherte Arbeit fuer einen Augenblick zu verwerfen — die
     * Klasse Fehler, aus der die verlorenen Buchungszeilen stammen.
     */
    childReport: Report;
}

export interface LinkReturnResult {
    /** Die Protokollzeilen, die jetzt an der Tochter stehen. */
    returnedLog: ServiceTransaction[];
    /**
     * Die zurueckgeholten Positionen — mit GELEERTEM Herkunftsvermerk, so wie
     * sie jetzt in der Datenbank stehen. Der Aufrufer uebernimmt genau diese
     * Objekte in seinen Speicher; nur so kann sein naechstes Speichern den
     * Stempel nicht versehentlich wieder setzen (saveProjectToCloud schickt
     * `transferred_from` mit, sobald das Feld gesetzt ist).
     */
    returnedServices: ServiceItem[];
}

/**
 * Holt beim TRENNEN die Positionen der Tochter von der Mutter zurueck.
 *
 * Die Umkehrung von `applyLinkTransfer` und mit denselben harten Regeln —
 * welche Positionen ueberhaupt zurueckgehen, entscheidet allein
 * `projectLv.planLinkReturn` (Herkunftsvermerk, Belege, Pauschalen). Hier
 * steht nur, was ohne Datenbank nicht zu haben ist.
 *
 * Warum `update` und nicht neu anlegen: dieselbe Begruendung wie in der
 * Hinrichtung — die `id` einer Leistung steht in den Buchungen der
 * Begehungsberichte und in Rechnungszeilen (lines[].serviceId). Die Position
 * wandert deshalb als DIESELBE Zeile, und beide Bezuege bleiben heil.
 *
 * Fehlende Schreibrechte liefern von PostgREST KEINEN Fehler, sondern null
 * betroffene Zeilen. Geprueft wird deshalb die ANZAHL, nicht `error`.
 *
 * ─── DER TEILERFOLG UND SEINE ERHOLUNG ─────────────────────────────────────
 * Vor dem ersten Schreiben wird geprueft, ob alle betroffenen Zeilen
 * erreichbar sind und beim Mutterprojekt liegen; fehlt eine, wird gar nicht
 * erst geschrieben (die Rechnung dazu steht vollstaendig an der Pruefung
 * selbst — samt dem, was sie NICHT zusichern kann). Bleibt trotzdem ein
 * Teilerfolg, ist die Erholung ein ERNEUTES TRENNEN: die bereits
 * zurueckgeholten Positionen tragen keinen Herkunftsvermerk mehr und liegen
 * bei der Tochter, `planLinkReturn` plant also genau den verbliebenen Rest.
 * Ihre am Mutterprojekt zurueckgebliebenen Protokollzeilen holt derselbe Lauf
 * mit (siehe `beiDerTochter` weiter unten).
 *
 * ─── Der Stempel wird GELEERT ──────────────────────────────────────────────
 * `transferred_from` sagt "kam von woanders". Die Position ist wieder zu
 * Hause; bliebe der Stempel stehen, wiese sie beim naechsten Verknuepfen auf
 * ein Projekt, aus dem sie nicht mehr kommt — und ein spaeteres Trennen wollte
 * sie ein zweites Mal dorthin zurueckschicken. Geleert wird im SELBEN
 * `update`, das `project_id` umhaengt: zwei Anweisungen koennten
 * auseinanderfallen.
 *
 * ─── DIE REIHENFOLGE IST DIE SICHERHEIT ────────────────────────────────────
 *   1. Positionen zurueckhaengen (das Geld zuerst, daran haengen die Belege).
 *   2. Protokollzeilen an der Tochter ANHAENGEN — bestaetigt gespeichert.
 *   3. Erst danach an der Mutter entfernen.
 * Bricht es zwischen 2 und 3 ab, steht eine Zeile DOPPELT — sichtbar, und von
 * Hand zu bereinigen. Umgekehrt waere sie weg, und das faellt niemandem auf.
 *
 * Anders als beim Verknuepfen speichert der Aufrufer NICHT beide Seiten: die
 * Mutter ist hier das fremde Projekt und wird von dieser Funktion geschrieben,
 * die Tochter ist das geoeffnete. Deshalb wird Schritt 2 hier bestaetigt
 * geschrieben (mit dem Bericht, den der Aufrufer mitgibt) und der Aufrufer
 * uebernimmt anschliessend denselben Stand in seinen Speicher.
 *
 * ─── DAS BUCHUNGSJOURNAL ───────────────────────────────────────────────────
 * Wie in der Hinrichtung: ANHANG an der Tochter plus RUECKNAHME an der Mutter
 * (`service_journal_void`, Grund `correction`), ueber denselben Ablauf
 * (`spiegleProjektwechsel`): an der Mutter wird nur stillgelegt, was an der
 * Tochter danach nachweislich lebt. Das Journal kennt weder Update noch
 * Delete — ein Nachweis, aus dem sich Zeilen entfernen lassen, waere keiner.
 * Die Quelle bleibt `link_transfer`: es ist derselbe Vorgang, nur in die
 * andere Richtung, und eine zweite Quelle fuer dieselbe Sache machte die
 * Auswertung nur unschaerfer. Der Ablauf wirft nie.
 *
 * ─── DIE PAUSCHALE IN DIESER RICHTUNG ──────────────────────────────────────
 * Beim Verknuepfen muss der scope einer Pauschale festgeschrieben werden,
 * sonst deckt sie an der Mutter deren eigene Positionen mit
 * (`planFlatScopeFreeze`). Hier stellt sich die Frage nicht: `planLinkReturn`
 * laesst eine Pauschale gar nicht zurueckwandern (`reason: 'flat_rate'`) und
 * eine gedeckte Position ebenso wenig ('flat_covered') — beide bleiben mit
 * ihrer Deckung bei der Mutter, und der Dialog nennt sie. Es gibt also nichts
 * umzuschreiben.
 *
 * Geprueft wird es trotzdem, unmittelbar vor dem ersten Schreiben. Der Plan
 * kommt von aussen; kaeme eine Pauschale doch in `moves` an, wanderte sie mit
 * praedikatbasiertem scope an die Tochter (dort deckte sie ploetzlich deren
 * Positionen) und ihre gedeckten Zeilen blieben bei der Mutter ungedeckt
 * zurueck — offenes Geld, das ein zweites Mal gefordert wuerde. Eine Annahme,
 * die nur im Kommentar steht, ist beim naechsten Umbau still weg.
 */
export const applyLinkReturn = async (
    plan: LinkReturnPlan,
    context: LinkReturnContext,
): Promise<LinkReturnResult> => {
    const childProjectId = (context.childProjectId || '').trim();
    const parentProjectId = (context.parentProjectId || '').trim();
    if (!childProjectId || !parentProjectId) {
        throw new Error('Zum Zurückholen fehlt die Projektzuordnung. Es wurde nichts verändert.');
    }
    // KEIN Kurzschluss bei einem Plan ohne Moves: gerade nach einem frueheren
    // Teilerfolg, der ALLE Positionen umgehaengt hat und erst danach abbrach
    // (das Mutterprojekt liess sich nicht laden, oder Schritt 2 unten schlug
    // fehl), plant `planLinkReturn` genau NULL Moves — die Positionen liegen
    // ja schon bei der Tochter. Ihre Protokollzeilen liegen dann aber noch
    // verwaist bei der Mutter, und der Abgleich weiter unten (`beiDerTochter`)
    // ist die einzige Stelle, die sie noch holt. Ein Rueckflug ohne Passagiere
    // schreibt trotzdem nichts, solange es nichts zu holen gibt: Vorpruefung
    // und Umhaengen sind unten an `ids.length` gebunden, und der Protokollteil
    // endet vor dem ersten Schreiben, wenn keine Zeile wandert.

    // Der Riegel gegen die streuende Pauschale — VOR dem ersten Schreiben,
    // damit "es wurde nichts verändert" auch stimmt. Die Begruendung steht im
    // Kopfkommentar; unerreichbar, solange der Plan von `planLinkReturn` kommt.
    const wandernedePauschalen = plan.moves.filter(m => !!m.service.flatRate);
    if (wandernedePauschalen.length > 0) {
        const namen = wandernedePauschalen
            .map(m => m.service.lvPosition || m.service.title)
            .join(', ');
        throw new Error(
            `Die Monatspauschale ${namen} soll zurückgeholt werden. Ihre Deckung gilt für Positionen, die `
            + 'bei der Mutter bleiben — zurückgeholt stünden die wieder als offenes Geld da und würden ein '
            + 'zweites Mal gefordert. Es wurde nichts verändert; die Pauschale ist von Hand zu klären.',
        );
    }

    const ids = plan.moves.map(m => m.service.id);
    /** "2.7.50" / Titel / notfalls die id — fuer die Meldungen unten. */
    const bezeichnungVon = (id: string): string => {
        const move = plan.moves.find(m => m.service.id === id);
        return move ? (move.service.lvPosition || move.service.title || id) : id;
    };
    const aufzaehlung = (liste: string[]): string => liste.map(bezeichnungVon).join(', ');

    // ─── VORPRUEFEN STATT HINTERHER FESTSTELLEN ────────────────────────────
    // Das `update` unten haengt in EINEM Zug alles um, was RLS freigibt, und
    // erst danach faellt die Zeilenzahl auf. Gibt RLS nur einen Teil frei oder
    // fehlt eine Zeile inzwischen, liegen einige Positionen bei der Tochter
    // und die uebrigen bei der Mutter — das LV ist gespalten, waehrend die
    // Verknuepfung weiterbesteht (der Aufrufer trennt erst nach Erfolg).
    //
    // ─── Warum hier KEINE transaktionale RPC steht ─────────────────────────
    // Sie waere das saubere Mittel und wird trotzdem nicht gebaut:
    //   • Eine RPC braucht eine neue Migration. Bis die ausgerollt ist,
    //     antwortet PostgREST mit PGRST202, und der Client fiele auf genau
    //     diesen unsicheren Weg zurueck — der Fehler bliebe, nur wuerde ihn
    //     niemand mehr erwarten. Am 2026-09-02 hat eine nicht gefahrene
    //     Migration bereits einen kompletten Produktionsausfall verursacht
    //     (derselbe Grund steht in `applyLinkTransfer` an der Mengenaenderung).
    //   • `applyLinkTransfer` hat dieselbe Struktur. Nur den Rueckweg atomar
    //     zu machen, waere inkonsistent und verschoebe das Problem.
    //
    // Stattdessen wird VOR dem ersten Schreiben festgestellt, ob ueberhaupt
    // alle betroffenen Zeilen erreichbar sind und beim erwarteten Projekt
    // liegen. Fehlt eine, wird gar nicht erst geschrieben: die Verknuepfung
    // bleibt bestehen und das LV bleibt ganz.
    //
    // ─── WAS DIESE PRUEFUNG ZUSICHERT — UND WAS NICHT ──────────────────────
    // Sie sagt etwas ueber das LESEN, nicht ueber das SCHREIBEN. Dass beides
    // in diesem Projekt auseinanderfaellt, ist keine Vermutung, sondern der
    // Normalfall dieses Dialogs: der Trennen-Dialog LIEST das fremde
    // Mutterprojekt vollstaendig (er zeigt dessen Positionen ja an), und
    // trotzdem ist „fehlende Berechtigung auf das Mutterprojekt" beim
    // SCHREIBEN der erwartete Fehlschlag. Ein `select` kann ein fehlendes
    // Schreibrecht also nicht ausschliessen — nur ein Schreibversuch koennte
    // das, und der waere kein Vorabtest mehr.
    //
    // Was die Vorpruefung damit wirklich leistet:
    //   • Sie faengt die Faelle, die eine Zeile EINZELN betreffen — geloescht,
    //     nicht lesbar, inzwischen bei einem anderen Projekt. Genau daraus
    //     entstuende ein Teilerfolg mitten in einer sonst erlaubten Menge.
    //   • Beim Recht bleibt die Aussage schwaecher: alle Zeilen dieser
    //     Anweisung teilen dasselbe alte `project_id` (genau das stellt diese
    //     Pruefung fest) und bekommen dasselbe neue — eine Policy, die am
    //     Projekt haengt, urteilt damit fuer alle gleich, und ein
    //     Rechtefehlschlag trifft alle oder keine: kein Teilerfolg, sondern
    //     ein sauberes Nichts. DASS die Policy am Projekt haengt, ist
    //     allerdings eine Annahme ueber die Umgebung — fuer
    //     `project_services` liegt im Repo weder ein `create table` noch eine
    //     Policy, nur das Zugriffsmodell (`can_access_project`,
    //     rls_hardening.sql) ist durchgehend projektbezogen. Deshalb bleibt
    //     der Teilerfolgs-Zweig unten das eigentliche Netz und wird nicht
    //     etwa als unerreichbar behandelt.
    //   • DAS FENSTER BLEIBT OFFEN: zwischen Pruefen und Schreiben kann sich
    //     etwas aendern. Das ist der bewusst in Kauf genommene Rest — und
    //     genau deshalb sagt die Meldung unten praezise, WAS dann steht.
    //
    // Gelesen werden nur `id` und `project_id` — beides Urbestandsspalten.
    // Eine additiv nachgeruestete Spalte (`transferred_from`, `flat_rate`)
    // haette diese Sicherung dort, wo ihre Migration fehlt, in einen Ausfall
    // verwandelt; der Spalten-Rueckfall ist rein lesend und ausdruecklich kein
    // Freibrief, hier mehr zu verlangen als noetig (spaltenRueckfall.ts).
    // Ohne Moves gibt es nichts vorzupruefen — die Abfrage entfaellt, statt
    // mit leerer id-Liste eine sinnlose Anweisung abzusetzen.
    const { data: bestand, error: bestandError } = ids.length > 0
        ? await supabase
            .from('project_services')
            .select('id, project_id')
            .in('id', ids)
        : { data: [] as any[], error: null };
    if (bestandError) {
        throw new Error(
            `Der Stand der zurückzuholenden Leistungen ließ sich nicht prüfen: ${bestandError.message || bestandError}. `
            + 'Es wurde nichts verändert; die Verknüpfung bleibt bestehen.',
        );
    }
    const projektJeId = new Map<string, string>(
        (bestand || []).map((row: any) => [String(row.id), String(row.project_id ?? '')]),
    );
    const unerreichbar = ids.filter(id => !projektJeId.has(id));
    const schonZurueck = ids.filter(id => projektJeId.get(id) === childProjectId);
    const anderswo = ids.filter(id => {
        const projekt = projektJeId.get(id);
        return projekt !== undefined && projekt !== parentProjectId && projekt !== childProjectId;
    });
    if (unerreichbar.length > 0 || schonZurueck.length > 0 || anderswo.length > 0) {
        const gruende = [
            unerreichbar.length > 0
                ? `nicht auffindbar (gelöscht oder ohne Leseberechtigung): ${aufzaehlung(unerreichbar)}`
                : '',
            schonZurueck.length > 0
                ? `liegt bereits bei diesem Projekt: ${aufzaehlung(schonZurueck)}`
                : '',
            anderswo.length > 0
                ? `liegt inzwischen bei einem anderen Projekt: ${aufzaehlung(anderswo)}`
                : '',
        ].filter(Boolean).join('; ');
        throw new Error(
            `Der Bestand des Mutterprojekts stimmt nicht mehr mit dem angezeigten Plan überein — ${gruende}. `
            + 'Es wurde NICHTS verändert und nicht getrennt: ein nur teilweise zurückgeholtes '
            + 'Leistungsverzeichnis wäre der schlechtere Zustand. Bitte den Dialog schließen und erneut '
            + 'trennen, damit der Plan neu gerechnet wird.',
        );
    }

    const { data, error } = ids.length > 0
        ? await supabase
            .from('project_services')
            .update({ project_id: childProjectId, transferred_from: null })
            .in('id', ids)
            .select('id')
        : { data: [] as any[], error: null };
    if (error) throw error;
    const umgehaengt = (data || []).map((row: any) => String(row.id));
    // Ab hier gehoeren die umgehaengten Zeilen der TOCHTER. Der Vermerk sorgt
    // dafuer, dass der naechste Speichervorgang der Tochter sie nicht wieder
    // wegprunt — auch dann nicht, wenn sein Bericht sie noch nicht kennt. Die
    // vollstaendige Begruendung steht an `umgezogeneLeistungen`.
    //
    // Vermerkt wird, was TATSAECHLICH umgezogen ist, und zwar VOR dem Wurf
    // unten: gerade im Teilerfolg kennt der Aufrufer die Zeilen nicht (er
    // uebernimmt sie erst nach einem erfolgreichen Lauf), und ohne den Vermerk
    // koennte sein naechstes Speichern sie loeschen.
    if (umgehaengt.length > 0) merkeUmgezogeneLeistungen(childProjectId, umgehaengt);
    if (umgehaengt.length !== ids.length) {
        // Der Teilerfolg, der trotz Vorpruefung im offenen Fenster entstehen
        // kann. Die Meldung muss deshalb sagen, was JETZT steht — „bitte den
        // Stand pruefen" allein laesst den Anwender damit allein.
        const geblieben = ids.filter(id => !umgehaengt.includes(id));
        throw new Error(
            `Es konnten nur ${umgehaengt.length} von ${ids.length} Leistungen zurückgeholt werden. `
            + `Bei diesem Projekt liegen jetzt: ${aufzaehlung(umgehaengt) || '—'}. `
            + `Beim Mutterprojekt liegen weiterhin: ${aufzaehlung(geblieben)}. `
            + 'Die Verknüpfung besteht unverändert weiter, es wurde NICHT getrennt und kein '
            + 'Leistungsprotokoll bewegt. Bitte erneut trennen: der Plan rechnet dann den verbliebenen '
            + 'Rest, und die Protokollzeilen der bereits zurückgeholten Positionen kommen dabei mit.',
        );
    }
    const returnedServices: ServiceItem[] = plan.moves.map(m => ({ ...m.service, transferredFrom: undefined }));

    // Frisch laden statt aus dem Plan schreiben: das Protokoll der Mutter
    // waechst durch jede Buchung, und der Dialog kann Minuten offen gestanden
    // haben. Geladen wird NACH dem Umhaengen — dann traegt die Mutter die
    // zurueckgegangenen Positionen nicht mehr, und das folgende Speichern
    // schreibt ihr LV unveraendert zurueck.
    const freshParent = await loadProjectById(parentProjectId);
    if (!freshParent || !freshParent.data) {
        // Die Meldung muss sagen, was JETZT stimmt: mit Moves ist bereits
        // umgehaengt worden, ohne Moves wurde noch gar nichts angefasst.
        throw new Error(ids.length > 0
            ? 'Die Leistungen sind zurückgeholt, aber das Mutterprojekt ließ sich nicht laden — das '
            + 'Leistungsprotokoll wurde NICHT mitgenommen. Bitte den Stand prüfen, bevor erneut getrennt wird.'
            : 'Das Mutterprojekt ließ sich nicht laden — ob dort noch Protokollzeilen dieses Projekts '
            + 'liegen, ist nicht festzustellen. Es wurde nichts verändert und nicht getrennt.');
    }
    const parentMeta = freshParent.data.metadata as ReportMetadata;
    const parentLogBefore: ServiceTransaction[] = parentMeta?.globalData?.serviceLog || [];

    // ─── DIE VERWAISTEN ZEILEN EINES FRUEHEREN TEILERFOLGS KOMMEN MIT ──────
    // Ist ein frueherer Trennversuch nach dem Umhaengen der Positionen
    // abgebrochen (Teilerfolg oben, oder das Mutterprojekt liess sich danach
    // nicht laden), liegen jene Positionen bei der Tochter, ihre
    // Protokollzeilen aber weiter bei der Mutter. Dort sind sie verwaist: der
    // Leistungsnachweis der Mutter findet keine Position mehr dazu, und der
    // Projektcheck meldet die Menge an der Tochter als `unlogged_quantity`.
    //
    // Beim erneuten Trennen taucht so eine Position in KEINEM Plan mehr auf —
    // `planLinkReturn` sieht nur die Zeilen der Mutter. Ohne diesen Abgleich
    // bliebe ihr Nachweis fuer immer liegen. Gefragt wird deshalb die
    // Datenbank, nicht der Plan: eine Protokollzeile der Mutter, die auf eine
    // Position DIESES Kindprojekts zeigt, gehoert an das Kind — gleichgueltig,
    // wann sie dorthin geraten ist. Zeilen zu Positionen einer SCHWESTER sind
    // damit ausdruecklich nicht gemeint und bleiben unberuehrt.
    //
    // Schlaegt die Abfrage fehl, wird NICHT geworfen: die Positionen sind zu
    // diesem Zeitpunkt bereits umgehaengt, und die Reparatur einer alten
    // Schieflage darf den laufenden Vorgang nicht scheitern lassen. Dann
    // wandert genau das, was der Plan sagt — das bisherige Verhalten.
    const { data: kindZeilen, error: kindZeilenError } = await supabase
        .from('project_services')
        .select('id')
        .eq('project_id', childProjectId);
    if (kindZeilenError) {
        console.warn('applyLinkReturn: Positionen der Tochter nicht lesbar, '
            + 'verwaiste Protokollzeilen bleiben ggf. bei der Mutter', kindZeilenError);
    }
    const beiDerTochter = (kindZeilen || []).map((row: any) => String(row.id));

    const logTransfer = planLinkReturnLog(plan, parentLogBefore, beiDerTochter);
    if (logTransfer.moving.length === 0) return { returnedLog: [], returnedServices };

    // Schritt 2: ANHAENGEN an der Tochter, bestaetigt.
    const childMeta = context.childReport.metadata as ReportMetadata;
    const childGlobal = childMeta?.globalData || ({} as ReportMetadata['globalData']);
    const childLogBefore: ServiceTransaction[] = childGlobal?.serviceLog || [];
    try {
        await saveProjectToCloud(
            {
                ...context.childReport,
                metadata: {
                    ...childMeta,
                    globalData: {
                        ...childGlobal,
                        serviceLog: [...logTransfer.moving, ...childLogBefore],
                    },
                },
            },
            childProjectId,
        );
    } catch (e: any) {
        throw new Error(
            'Die Leistungen sind zurückgeholt, das Leistungsprotokoll dazu aber NICHT: '
            + `${e?.message || e}. Die Verknüpfung bleibt bestehen; bitte den Stand prüfen.`,
        );
    }

    // Schritt 3: erst jetzt an der Mutter ENTFERNEN.
    try {
        await saveProjectToCloud(
            {
                ...freshParent.data,
                metadata: {
                    ...parentMeta,
                    globalData: { ...parentMeta.globalData, serviceLog: logTransfer.staying },
                },
            },
            parentProjectId,
        );
    } catch (e: any) {
        throw new Error(
            'Die Leistungen und ihr Protokoll sind bei der Tochter angekommen, im Mutterprojekt ließen '
            + `sich die Zeilen aber nicht entfernen: ${e?.message || e}. Sie stehen jetzt doppelt — `
            + 'sichtbar und von Hand zu bereinigen. Die Verknüpfung bleibt bestehen.',
        );
    }

    // Zweitschrift und Ruecknahme im Journal — nach dem BESTAETIGTEN Speichern
    // und ohne Wurf. `zielPreviousLog` enthaelt ausdruecklich AUCH die
    // gewanderten Zeilen: ihre ids sind als Journalschluessel an der Mutter
    // bereits vergeben, und ohne diesen Hinweis verschluckte
    // `on conflict do nothing` sie still.
    //
    // Der Schluessel an der Tochter haengt am Inhalt samt Projekt: nach
    // Trennen → Verknuepfen → Trennen ist er derselbe wie beim ersten Trennen
    // und wurde beim Verknuepfen dazwischen stillgelegt. Deshalb liest
    // `spiegleProjektwechsel` die Tochter nach dem Anhaengen frisch und legt
    // an der Mutter nur still, was an der Tochter wirklich lebt.
    //
    // `linkedProjectConfig: null`, weil die Tochter mit diesem Vorgang keine
    // mehr ist — eine Abrechnungsart gegenueber einer Mutter, die es gleich
    // nicht mehr gibt, waere an der Zeile eine falsche Aussage.
    await spiegleProjektwechsel({
        quelleProjectId: parentProjectId,
        zielProjectId: childProjectId,
        entries: logTransfer.moving,
        zielPreviousLog: [...childLogBefore, ...logTransfer.moving],
        quelleVerbleibend: logTransfer.staying,
        linkedProjectConfig: null,
        source: 'link_transfer',
    });

    return { returnedLog: logTransfer.moving, returnedServices };
};

/**
 * Wie viele Projekte fuehren dieses Projekt als MUTTER?
 *
 * Der Riegel dahinter: ein Projekt mit eigenen Toechtern darf nicht selbst
 * Tochter werden. Sein LV wanderte beim Verknuepfen an die neue Mutter, und
 * die Buchungen SEINER Toechter suchen die Position weiter bei ihm —
 * useReportActions bricht dann mit 'Leistung im Mutterprojekt nicht gefunden.'
 * ab, und die Begehung ist gebucht auf nichts.
 *
 * `null` heisst NICHT NULL Toechter, sondern UNBEKANNT (Abfrage fehlgeschlagen,
 * kein Netz, keine Leseberechtigung). Der Aufrufer muss beides unterscheiden
 * und im unbekannten Fall sperren: eine Verknuepfung laesst sich nachholen,
 * eine Kette nicht ohne Weiteres aufloesen.
 */
export const countLinkedChildren = async (projectId: string): Promise<number | null> => {
    const id = (projectId || '').trim();
    if (!id || id === 'temp-id') return 0;
    try {
        // Ueber die Spalte aus Nr. 71 (Index), nicht mehr ueber den Blob.
        // Fehlt die Spalte in dieser Datenbank, ist die Antwort ein Fehler
        // und damit UNBEKANNT — der Aufrufer sperrt dann, richtig so.
        const { count, error } = await supabase
            .from('projects')
            .select('id', { count: 'exact', head: true })
            .eq('parent_project_id', id);
        if (error) return null;
        // `count` kann null sein, obwohl die Abfrage gelungen ist (der Zaehler
        // wurde nicht geliefert). Das ist genauso unbekannt wie ein Fehler und
        // darf nicht als "keine Toechter" durchgehen.
        return typeof count === 'number' ? count : null;
    } catch {
        return null;
    }
};

export const initiateProjectTransfer = async (projectId: string, targetUserId: string) => {
    await supabase.from('projects').update({ transfer_target_id: targetUserId }).eq('id', projectId);
};

/**
 * Zieht NUR die Besitzerspalte nach, wenn der Hauptverantwortliche in der
 * Vertreterliste gewechselt hat.
 *
 * Warum hier nicht auch `data` geschrieben wird: Der Bericht (und damit
 * `metadata.coordinatorId`, die Vertreterliste und die Protokollnotiz) läuft
 * über den normalen Speicherweg des geöffneten Projekts. Ein zweiter
 * Schreibweg auf `data` würde vom nächsten Autosave überschrieben — und die
 * hier eingefügte Notiz vom Prune in `save_project_children_tx` wieder
 * gelöscht, weil sie in der lokalen Notizliste fehlte. Deshalb: Spalten hier,
 * Bericht dort, jeder Wert genau einmal.
 *
 * `transfer_target_id` wird geleert wie bei jedem anderen Besitzerwechsel:
 * eine offene Transferanfrage ist mit der Übernahme gegenstandslos.
 */
export const setProjectOwnerColumn = async (
    projectId: string,
    targetUserId: string,
    coordinatorName: string,
) => {
    const { data, error } = await supabase
        .from('projects')
        .update({ user_id: targetUserId, coordinator_name: coordinatorName, transfer_target_id: null })
        .eq('id', projectId)
        .select('id')
        .maybeSingle();
    if (error) throw error;
    // Ohne Guard hiesse "keine Zeile getroffen" nur noch: die Projektzeile gibt
    // es nicht (mehr) oder die RLS verweigert sie. Still durchlaufen darf das
    // nicht — sonst stuende der Wechsel im Bericht und nicht in der Spalte.
    if (!data) throw new Error('Besitzerwechsel hat keine Zeile getroffen (Projekt gelöscht oder keine Berechtigung).');
};

/**
 * Setzt den neuen Hauptverantwortlichen in den Projekt-Metadaten. Telefon wird
 * bewusst mitgeschrieben (sonst bliebe die Nummer des Erst-Koordinators stehen).
 * Führt das Projekt bereits eine Vertreterliste, rückt der bisherige
 * Hauptverantwortliche auf Position 1 — er wird nie stillschweigend entfernt.
 */
const withNewCoordinator = (meta: any, coordinator: ProjectRepresentative): any => {
    const base = {
        ...meta,
        coordinator: coordinator.name,
        coordinatorId: coordinator.id,
        coordinatorEmail: coordinator.email,
        coordinatorPhone: coordinator.phone
    };
    if (!Array.isArray(meta?.representatives) || meta.representatives.length === 0) return base;
    return applyRepresentatives(base, promoteRepresentative(getRepresentatives(meta), coordinator));
};

export const respondToTransfer = async (projectId: string, action: 'accept' | 'reject') => {
    const { data: { user }, error: anmeldeFehler } = await supabase.auth.getUser();
    if (!user) throw fehlerOhneNutzer(anmeldeFehler, geraetOnline());

    if (action === 'reject') {
        await supabase.from('projects').update({ transfer_target_id: null }).eq('id', projectId);
        return;
    }

    const { data: profile } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    if (!profile) throw new Error("Profil nicht gefunden");
    
    const fullName = `${profile.first_name} ${profile.last_name}`.trim();

    const { data: project } = await supabase.from('projects').select('data').eq('id', projectId).single();
    if (!project) throw new Error("Projekt nicht gefunden");

    const currentData = project.data || { metadata: {} };
    const meta = currentData.metadata || {};
    
    const updatedData = {
        ...currentData,
        metadata: withNewCoordinator(meta, {
            id: user.id,
            name: fullName,
            email: profile.email,
            phone: profile.phone
        })
    };

    await supabase.from('projects').update({ 
        transfer_target_id: null, 
        user_id: user.id,
        coordinator_name: fullName,
        data: updatedData,
        updated_at: new Date().toISOString()
    }).eq('id', projectId);
};

/**
 * Setzt den Projekt-Besitzer administrativ um.
 *
 * `actor` = der Admin, der die Aktion auslöst. Ohne ihn stand in der
 * Projektnotiz bisher pauschal 'System-Admin' — im Protokoll war dann nicht
 * mehr nachvollziehbar, WER umgehängt hat. Der Parameter ist optional, damit
 * Aufrufer ohne Identität im Scope weiterhin funktionieren; dort bleibt es
 * beim alten Text.
 */
export const forceTransferByAdmin = async (
    projectId: string,
    targetUserId: string,
    actor?: { first_name?: string | null; last_name?: string | null } | null,
) => {
    const { data: currentProject } = await supabase.from('projects').select('user_id, data').eq('id', projectId).single();
    if (!currentProject) throw new Error("Projekt nicht gefunden");
    
    const { data: profile } = await supabase.from('profiles').select('*').eq('id', targetUserId).single();
    if (!profile) throw new Error("Zielprofil nicht gefunden");
    
    const newName = `${profile.first_name} ${profile.last_name}`.trim();

    // Realer Name statt 'System-Admin': Note.author wird in NotesModal und
    // ProjectStats wörtlich angezeigt — hier handelt ein Mensch, also gehört
    // sein Name ins Protokoll. Fallback nur, wenn der Aufrufer keine Identität
    // mitgibt.
    const actorName = actor ? `${actor.first_name || ''} ${actor.last_name || ''}`.trim() : '';

    const newNote = {
        id: crypto.randomUUID(),
        text: `SYSTEM: Projekt-Besitzer manuell angepasst durch Admin. Neuer Besitzer: ${newName}`,
        date: new Date().toISOString(),
        author: actorName || 'System-Admin',
        mentions: [targetUserId], 
        readBy: [] 
    };
    
    const currentData = currentProject.data || { metadata: {} };
    const meta = currentData.metadata || {};
    const updatedData = {
        ...currentData,
        metadata: {
            ...withNewCoordinator(meta, {
                id: targetUserId,
                name: newName,
                email: profile.email,
                phone: profile.phone
            }),
            globalData: {
                ...(meta.globalData || {}),
                notes: [newNote, ...(meta.globalData?.notes || [])]
            }
        }
    };

    const { error } = await supabase.from('projects').update({ 
        transfer_target_id: null,
        user_id: targetUserId, 
        coordinator_name: newName,
        data: updatedData,
        updated_at: new Date().toISOString()
    }).eq('id', projectId);

    if (error) throw error;

    await supabase.from('project_notes').insert({ 
        project_id: projectId, 
        text: newNote.text, 
        author: newNote.author, 
        created_at: newNote.date, 
        mentions: newNote.mentions 
    });
};

/** Eine Zeile des API-Protokolls, wie die Admin-Ansicht sie braucht. */
export interface ApiLogZeile {
    id: string;
    created_at: string;
    user_id: string | null;
    action: string | null;
    model: string | null;
    prompt_tokens: number | null;
    candidate_tokens: number | null;
    estimated_cost: number | null;
    profiles: { id: string; first_name: string | null; last_name: string | null; email: string | null } | null;
}

/**
 * API-Protokoll ab `ab` (ISO, einschliesslich; ohne: alles), neueste zuerst.
 *
 * Seitenweise und nur die Spalten der Ansicht: die Admin-Ansicht summiert
 * Kosten und Tokens ueber ALLE geladenen Zeilen. Ohne `.range()` rechnete sie
 * im Oktober 2026 ueber 1000 von 11566 Aufrufen — die Summen waren falsch,
 * ohne dass es auffiel. `model_version` bleibt draussen: die Spalte fehlt in
 * Umgebungen ohne die Migration, und die Ansicht zeigt sie nicht.
 *
 * Wirft bei einem Fehler. Leere Summen hiessen sonst „keine Kosten“.
 */
export const fetchApiLogs = async (opts: { ab?: string } = {}): Promise<ApiLogZeile[]> => {
    type Zeile = Omit<ApiLogZeile, 'profiles'>;
    const data = await ladeAlleSeiten<Zeile>((von, bis) => {
        let query = supabase
            .from('api_logs')
            .select('id, created_at, user_id, action, model, prompt_tokens, candidate_tokens, estimated_cost');
        if (opts.ab) query = query.gte('created_at', opts.ab);
        return query
            .order('created_at', { ascending: false })
            .order('id', { ascending: false })
            .range(von, bis);
    });

    // Fetch profiles separately to avoid relation errors
    const userIds = Array.from(new Set(data.map(d => d.user_id).filter((id): id is string => !!id)));

    const profilesAssoc: Record<string, ApiLogZeile['profiles']> = {};
    if (userIds.length > 0) {
        const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email')
            .in('id', userIds);
        for (const p of profilesData || []) profilesAssoc[p.id] = p;
    }

    return data.map(log => ({
        ...log,
        profiles: log.user_id ? profilesAssoc[log.user_id] ?? null : null
    }));
};

// --- BUG REPORTS ---
// All reporting functions are fire-and-forget safe: they swallow errors so the
// bug reporter can never crash the app (incl. when Supabase is unconfigured).

export const reportBug = async (input: {
    fingerprint: string;
    message: string;
    stack?: string;
    componentStack?: string;
    source: 'boundary' | 'window' | 'rejection';
    appContext?: string;
    userAgent?: string;
}): Promise<{ report_id: string; is_new: boolean; has_solution: boolean } | null> => {
    try {
        const { data, error } = await supabase.rpc('report_bug', {
            p_fingerprint: input.fingerprint,
            p_message: input.message,
            p_stack: input.stack || null,
            p_component_stack: input.componentStack || null,
            p_source: input.source,
            p_app_context: input.appContext || null,
            p_user_agent: input.userAgent || null
        });
        if (error) throw error;
        // RETURNS TABLE -> array with one row
        return Array.isArray(data) ? (data[0] || null) : (data || null);
    } catch (e) {
        console.warn('Bug report failed:', e);
        return null;
    }
};

export const saveBugSolution = async (id: string, solution: string, status: 'done' | 'failed'): Promise<void> => {
    try {
        const { error } = await supabase.rpc('set_bug_solution', {
            p_id: id,
            p_solution: solution,
            p_status: status
        });
        if (error) throw error;
    } catch (e) {
        console.warn('Saving bug solution failed:', e);
    }
};

export const fetchBugReports = async (): Promise<BugReport[]> => {
    try {
        // Seitenweise wie alle wachsenden Listen (seitenweise.ts, 23.10.2026).
        const data = await ladeAlleSeiten<BugReport & { user_id?: string | null }>((von, bis) =>
            supabase
                .from('bug_reports')
                .select('*')
                .order('last_seen_at', { ascending: false })
                .order('id', { ascending: false })
                .range(von, bis));

        // Fetch profiles separately to avoid relation errors
        const userIds = Array.from(new Set((data || []).map(d => d.user_id).filter(Boolean)));

        let profilesAssoc: Record<string, any> = {};
        if (userIds.length > 0) {
            const { data: profilesData } = await supabase
                .from('profiles')
                .select('id, first_name, last_name, email')
                .in('id', userIds);

            if (profilesData) {
                profilesAssoc = profilesData.reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
            }
        }

        return (data || []).map(report => ({
            ...report,
            profiles: report.user_id ? profilesAssoc[report.user_id] : null
        }));
    } catch (e) {
        console.warn('Error fetching bug reports:', e);
        return [];
    }
};

export const fetchOpenBugCount = async (): Promise<number> => {
    try {
        const { count, error } = await supabase
            .from('bug_reports')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'open');
        if (error) throw error;
        return count || 0;
    } catch {
        return 0;
    }
};

export const updateBugReportStatus = async (id: string, status: 'open' | 'resolved'): Promise<void> => {
    const { error } = await supabase.from('bug_reports').update({ status }).eq('id', id);
    if (error) throw error;
};

export const deleteBugReport = async (id: string): Promise<void> => {
    const { error } = await supabase.from('bug_reports').delete().eq('id', id);
    if (error) throw error;
};

export const fetchAuthorities = async (): Promise<Authority[]> => {
    const { data, error } = await supabase.from('authorities').select('*').order('name');
    if (error) throw new Error(error.message);
    return data || [];
};

export const createAuthority = async (auth: Omit<Authority, 'id'>) => {
    const { error } = await supabase.from('authorities').insert([auth]);
    if (error) throw error;
};

export const updateAuthority = async (id: string, updates: Partial<Authority>) => {
    const { error } = await supabase.from('authorities').update(updates).eq('id', id);
    if (error) throw error;
};

export const deleteAuthority = async (id: string) => {
    const { error } = await supabase.from('authorities').delete().eq('id', id);
    if (error) throw error;
};